import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { newSecureToken } from '../lib/secureToken';
import { adminAuth } from '../lib/adminApi';
import { ALLOW_DUPLICATE_USER_EMAILS } from '../lib/testFlags';
// computeDocumentState is no longer called here directly — every state on this
// page now goes through documentStateFor, which reconciles the onboarding row
// with any acknowledgement first. That's the whole point of the consolidation.
import { docLabelFor, DOCUMENT_STATES } from '../lib/compliance';
import { applyLiveDocumentFilter, liveRequiredKeys } from '../lib/liveDocuments';
import { documentStateFor, hasSupplierActioned } from '../lib/complianceRecord';
import { categoryOnly, subCategoryOnly, paymentTermsLabel, NOT_SET } from '../lib/supplierDisplay';
import InfoIcon from '../components/InfoIcon';
import { ONBOARDING_STEPS, completionByStep, checklistProgress } from '../lib/onboardingChecklist';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useCurrentInternalUser, isFinance, isAdmin } from '../lib/currentUser';
import Sidebar from '../components/Sidebar';
import MoneyInput from '../components/MoneyInput';
import AutoSizeTextarea from '../components/AutoSizeTextarea';
import DatePicker from 'react-datepicker';
import { fmtDate, fmtDateTime } from '../lib/dates';
import { ecoStageLabel } from '../lib/ecoAward';
import 'react-datepicker/dist/react-datepicker.css';
import '../lib/datepicker-theme.css';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import ComplianceBadge, { badgeLabelFor } from '../components/compliance/ComplianceBadge';
import { deriveSupplierPerformance, performanceBasis, rawAnswerSummary, PERFORMANCE_EXPLAINERS } from '../lib/supplierPerformance';
import { feedbackState, feedbackOutcomeLabel } from '../lib/briefFeedback';
import RecordTags from '../components/RecordTags';
import { useActionToast } from '../components/ActionToast';
import BackIconButton from '../components/BackIconButton';
import EntityHeaderActions, { headerButtonClass, HEADER_TITLE_CLASS_SENTENCE, HEADER_EYEBROW_CLASS, statusPillClass, ICON_NOD_PENCIL } from '../components/EntityHeaderActions';
import { firePinBurst, PIN_PULSE_MS } from '../components/pinBurst';
import { OttoLoading } from '../components/otto';
import { sendEmail, supplierSetupFormEmail, welcomeEmail, passwordEmail, complianceReminderEmail, supplierActivationEmail } from '../lib/email';
// Compliance doc list is sourced dynamically from fusion_documents (Admin >
// Fusion Documents tab) so adding a new compliance doc + ticking it for a
// risk level in Risk Flag Configuration flows through to supplier pages
// without a code change.

// Date helpers — round-trip stored "YYYY-MM-DD" string to a Date object for
// the picker and back, matching the AddBrief / AmendBriefPanel convention so
// every date input across the app uses the same react-datepicker UX.
const toDateObj = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};
const fromDateOnly = (d) => {
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function SupplierDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user: currentUser } = useCurrentInternalUser();
  // Action-confirmation toast — fires for the silent admin-side successes
  // on this page (tag add/remove for now; compliance approve/reject,
  // setup-form send, password reset land in the next wave).
  const { notify: notifyToast, Toast: ActionToast } = useActionToast();
  // Per-user pin state — pinning is scoped to the current internal
  // user via user_pinned_suppliers. Fetched separately from the
  // supplier record so multiple users can pin the same supplier
  // without stomping on each other.
  const [userPinned, setUserPinned] = useState(false);
  // Pin star burst — refs the inline-flex span wrapping the star SVG so
  // particles anchor to the icon, not the centre of the pill. Pulsing
  // drives the 400ms scale pulse. Burst fires on pin only (spec: undo
  // doesn't celebrate).
  const pinStarRef = useRef(null);
  const [pinPulsing, setPinPulsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    const allowed = ['overview', 'management', 'finance', 'compliance', 'briefs', 'activity'];
    return allowed.includes(t) ? t : 'overview';
  });
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState(null);
  const [supplierUsers, setSupplierUsers] = useState([]);
  // Supplier's earned badges (current + expired). Surfaces in the
  // achievements strip beneath the page header. Loaded once per
  // supplier; cheap query (one or two rows in practice).
  const [supplierBadges, setSupplierBadges] = useState([]);
  // Post-event feedback + incidents for this supplier (performance surfaces).
  const [feedbackRows, setFeedbackRows] = useState([]);
  const [incidents, setIncidents] = useState([]);
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    (async () => {
      const [fb, inc] = await Promise.all([
        supabase.from('brief_feedback')
          .select('*, briefs:brief_id(id, brief_name, fusion_project_number, closed_at, event_live_date, event_live_end_date, status, awarded_supplier_id)')
          .eq('supplier_id', id),
        supabase.from('supplier_incidents').select('*').eq('supplier_id', id).order('raised_at', { ascending: false }),
      ]);
      if (cancelled) return;
      setFeedbackRows(fb.data || []);
      setIncidents(inc.data || []);
    })();
    return () => { cancelled = true; };
  }, [id]);

  const closeIncident = async (inc) => {
    const closureNote = window.prompt('Closure note (optional) — what was resolved?', '');
    if (closureNote === null) return; // cancelled
    const nowIso = new Date().toISOString();
    const { error: err } = await supabase.from('supplier_incidents')
      .update({ closed_at: nowIso, closed_by_user_id: currentUser?.id || null, closure_note: closureNote.trim() || null })
      .eq('id', inc.id);
    if (!err) setIncidents(prev => prev.map(i => i.id === inc.id ? { ...i, closed_at: nowIso, closure_note: closureNote.trim() || null } : i));
  };
  // Supplier deactivation (admin lifecycle). status='inactive' + audit;
  // revokes portal access; suppresses emails. Reason required; reversible.
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivating, setDeactivating] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [confirmingReactivate, setConfirmingReactivate] = useState(false);
  const [badgeCategoriesMap, setBadgeCategoriesMap] = useState(new Map());
  const [showAddUser, setShowAddUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // notify: whether to email the welcome + password on create. Default on
  // (the common case); unticking creates the account silently so the admin
  // can send the details later from the user's row actions.
  const [newUser, setNewUser] = useState({ full_name: '', email: '', password: '', notify: true });
  const [success, setSuccess] = useState(null);
  const [sendingForm, setSendingForm] = useState(false);
  const [savingFinance, setSavingFinance] = useState(false);
  const [savingManagement, setSavingManagement] = useState(false);
  const [editingPassword, setEditingPassword] = useState('');
  const [editingPasswordRevealed, setEditingPasswordRevealed] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [briefsLoading, setBriefsLoading] = useState(false);
  const [briefsLoaded, setBriefsLoaded] = useState(false);
  const [activityNotes, setActivityNotes] = useState([]);
  const [activityResponses, setActivityResponses] = useState([]);
  const [activityComplianceHistory, setActivityComplianceHistory] = useState([]);
  const [activityLoginEvents, setActivityLoginEvents] = useState([]);
  const [activityEmailEvents, setActivityEmailEvents] = useState([]);
  // Acknowledge-related compliance_audit_log rows (Phase 5). Filtered
  // to the three ack actions so the Activity Log surfaces "X
  // acknowledged Y" / "Z approved/rejected the ack" without pulling
  // the entire audit log (most rows are doc-upload approvals which
  // are already surfaced via supplier_compliance_status_history).
  const [activityAckEvents, setActivityAckEvents] = useState([]);
  // Brief-interaction history (feedback 2026-08-05): questions the supplier
  // asked (+ when Fusion answered), post-deadline update requests, and pending
  // deadline-extension requests.
  const [activityQuestions, setActivityQuestions] = useState([]);
  const [activityUpdateRequests, setActivityUpdateRequests] = useState([]);
  const [activityExtensions, setActivityExtensions] = useState([]);
  const [activityLoaded, setActivityLoaded] = useState(false);
  // Activity Log search + filter chips. activityFilters is a Set of group
  // keys (see ACTIVITY_FILTER_GROUPS); empty = show everything. Search runs
  // case-insensitively across each event's label + detail.
  const [activitySearch, setActivitySearch] = useState('');
  const [activityFilters, setActivityFilters] = useState(new Set());
  const [activityLoading, setActivityLoading] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [onboardingDocs, setOnboardingDocs] = useState([]);
  // document_acknowledgements for this supplier, newest first.
  const [acknowledgements, setAcknowledgements] = useState([]);
  // Debrief notes tagged to this supplier (2026-08-13). A debrief belongs to the
  // PROJECT, but the parts of it that were about this supplier belong on their
  // record too — otherwise a lesson learned about them is filed under a project
  // number nobody thinks to open. Read-only here: debriefs are edited in the
  // debrief, and are NEVER visible to the supplier themselves.
  const [debriefNotes, setDebriefNotes] = useState([]);
  // Onboarding process checklist — internal steps, one row per completed step.
  const [checklistRows, setChecklistRows] = useState([]);
  const [checklistBusy, setChecklistBusy] = useState(null); // step_key mid-write
  // Briefs table sort (§7). Newest-sent first is the useful default: the
  // question people bring to this tab is "what have we asked them lately".
  const [briefSortKey, setBriefSortKey] = useState('sentAt');
  const [briefSortDir, setBriefSortDir] = useState('desc');
  const [savingDocKey, setSavingDocKey] = useState(null);
  // "Add additional compliance document" picker — opens beneath the
  // Documents table so staff can pull in a doc that isn't standard for
  // this supplier's risk flag without the previously-rendered greyed-out
  // rows cluttering the list.
  const [showAddOptionalDoc, setShowAddOptionalDoc] = useState(false);
  const [complianceNotes, setComplianceNotes] = useState([]);
  const [newComplianceNote, setNewComplianceNote] = useState('');
  const [savingComplianceStatus, setSavingComplianceStatus] = useState(false);
  const [savingComplianceNote, setSavingComplianceNote] = useState(false);
  const [sendingComplianceReminder, setSendingComplianceReminder] = useState(false);
  const [savingRiskFlag, setSavingRiskFlag] = useState(false);
  // Per-risk-level required document keys for THIS supplier's risk_flag.
  // Set of document_key strings. Loaded once on mount; risk_flag changes
  // re-fetch via the dependency on form.risk_flag.
  // Raw rows from risk_document_requirements. NOT the requirement set used by
  // the page — requirement rows outlive the documents they name, so this is
  // intersected with the live document list below (requiredDocKeys).
  const [requiredDocKeysRaw, setRequiredDocKeysRaw] = useState(new Set());
  // Whether the live document list has come back yet, so the tier card can
  // stay quiet instead of flashing "0 documents required" on the way in.
  const [complianceDocsLoaded, setComplianceDocsLoaded] = useState(false);
  // Master list of compliance docs from fusion_documents — same source the
  // Admin > Risk Flag Configuration tab reads. Each item is normalised to
  // { key, label } so the rendering loop below stays tidy. Loaded once on
  // mount; the Admin tab is the place to add/remove entries.
  const [complianceDocs, setComplianceDocs] = useState([]);
  // Activate flow — modal for inactive suppliers asks staff to pick a risk
  // flag at the same time as flipping status to active.
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [activateRiskFlag, setActivateRiskFlag] = useState(null);
  const [activating, setActivating] = useState(false);
  // Optional auto-publish of every compliance doc required for the chosen
  // risk flag — saves staff from clicking Publish on each row right after
  // activation. Default on because the most common reason to activate is
  // to start the supplier's onboarding lifecycle, and the docs need to be
  // visible on the portal for that to begin. Already-published docs are
  // left alone, so a re-activation doesn't re-stamp sent_at on rows the
  // supplier has already signed.
  const [activatePublishDocs, setActivatePublishDocs] = useState(true);
  // Optional launch / welcome email sent at activation time. Default off
  // so a staff member activating in bulk doesn't accidentally email every
  // supplier.
  const [activateSendEmail, setActivateSendEmail] = useState(false);
  // Compliance status override flow — staff can manually pin a status
  // (Green/Amber/Red) but only via this modal which forces a reason. The
  // reason is appended to the compliance notes thread so the audit trail
  // captures *why* the override was applied.
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [pendingOverrideStatus, setPendingOverrideStatus] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);
  const [financeFeedback, setFinanceFeedback] = useState(null);

  const EyeIcon = ({ show }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      {show
        ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
        : <><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>
      }
    </svg>
  );

  const EnvelopeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
  const KeyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a4 4 0 110 8 4 4 0 010-8zm-4 4l-7 7v3h3v-2h2v-2h2l1-1" />
    </svg>
  );
  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-1 12a2 2 0 01-2 2H8a2 2 0 01-2-2L5 7m5 4v6m4-6v6M4 7h16M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
    </svg>
  );
  const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h5M20 20v-5h-5M4 9a8 8 0 0114-4M20 15a8 8 0 01-14 4" />
    </svg>
  );
  const CopyIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 4h8a2 2 0 012 2v12a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2zm-2 14H4a2 2 0 01-2-2V4a2 2 0 012-2h8a2 2 0 012 2v2" />
    </svg>
  );

  // Filled flag icon used for the risk indicator. Pole on the left, banner
  // billowing right — currentColor so it inherits the risk tone wherever
  // it's rendered (header pill, compliance-tab card, button).
  const FlagIcon = ({ className = 'h-3.5 w-3.5' }) => (
    <svg xmlns="http://www.w3.org/2000/svg" className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5 3a1 1 0 011 1v17a1 1 0 11-2 0V4a1 1 0 011-1z" />
      <path d="M7 4h11.382a1 1 0 01.894 1.447L17.118 9.5l2.158 4.053A1 1 0 0118.382 15H7V4z" />
    </svg>
  );

  // Risk-flag config used in three places: the header pill, the Compliance
  // tab section, and the three pickable buttons. Centralised so colours
  // stay in sync.
  // Shared red / amber / green tone tokens — a single source for the dot
  // colour, text, card bg/border and active-button fill used by BOTH the Risk
  // Flag and Compliance Status cards, so the two can't drift apart with
  // duplicated hex values. riskCfg and csCfg just map their own keys + labels
  // onto these.
  const TONE = {
    red:   { dot: '#E0290A', text: 'text-[#E0290A]',     bg: 'bg-[#E0290A]/15', border: 'border-[#E0290A]/40', activeBtn: 'bg-[#E0290A] text-ink' },
    amber: { dot: '#E89A2E', text: 'text-[#7A4E10]',     bg: 'bg-[#E89A2E]/15', border: 'border-[#E89A2E]/40', activeBtn: 'bg-[#E89A2E] text-[#131313]' },
    green: { dot: '#92D400', text: 'text-fusion-forest', bg: 'bg-[#92D400]/15', border: 'border-[#92D400]/40', activeBtn: 'bg-[#92D400] text-[#131313]' },
  };
  const riskCfg = {
    high:   { label: 'High',   ...TONE.red },
    medium: { label: 'Medium', ...TONE.amber },
    low:    { label: 'Low',    ...TONE.green },
  };
  // Compliance tones live at component scope too: the override modal renders
  // outside the compliance tab's block, and both need the same vocabulary.
  const csCfg = {
    green: { label: 'Green', ...TONE.green },
    amber: { label: 'Amber', ...TONE.amber },
    red:   { label: 'Red',   ...TONE.red },
  };

  // Reset the inline-password field whenever the edit panel closes / opens for a different user.
  useEffect(() => {
    setEditingPassword('');
    setEditingPasswordRevealed(false);
  }, [editingUser?.id]);

  // sendEmail throws on failure; these helpers swallow + log so
  // callers (often fire-and-forget post-create) don't unhandled-
  // reject. Real user-facing send paths surface their own failures
  // via per-row try/catch further down.
  // Record a portal-user email send against the supplier so it shows in the
  // activity log — previously welcome/password sends were the only emails that
  // left no trail. Best-effort: a logging failure must never fail the email
  // that already went out, so it's caught and swallowed (with a console warn).
  const logUserEmail = async (kind, email) => {
    try {
      await supabase.from('supplier_email_log').insert([{
        supplier_id: id,
        email_kind: kind,
        recipients: [email],
        sent_by: currentUser?.id || null,
      }]);
      // Let the activity tab pick up the new entry next time it's opened.
      setActivityLoaded(false);
    } catch (e) {
      console.warn('supplier_email_log insert failed', { kind, error: e?.message });
    }
  };

  // Return true on success / false on failure so callers can CONFIRM delivery
  // rather than assume it. Previously these swallowed the error and returned
  // undefined, so every caller reported "email sent" even when it hadn't. On
  // success they now also log to the activity feed (welcome + password sends,
  // whether from create or a later resend).
  const sendWelcomeUserEmail = async (name, email) => {
    const { subject, html, text } = welcomeEmail({ fullName: name, email, appUrl: window.location.origin });
    try {
      await sendEmail({ to: email, subject, html, text });
      await logUserEmail('portal_user_welcome', email);
      return true;
    } catch (err) {
      console.warn('welcomeEmail failed', { to: email, error: err?.message });
      return false;
    }
  };

  const sendPasswordUserEmail = async (name, email, password) => {
    const { subject, html, text } = passwordEmail({ fullName: name, email, password, appUrl: window.location.origin });
    try {
      await sendEmail({ to: email, subject, html, text });
      await logUserEmail('portal_user_password', email);
      return true;
    } catch (err) {
      console.warn('passwordEmail failed', { to: email, error: err?.message });
      return false;
    }
  };

  const findAuthUserByEmail = async (email) => {
    const { ok, user } = await adminAuth.findByEmail(email);
    return ok ? (user || null) : null;
  };

  const generateRandomPassword = (length = 16) => {
    const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%^&*';
    const bytes = new Uint32Array(length);
    window.crypto.getRandomValues(bytes);
    let out = '';
    for (let i = 0; i < length; i++) out += charset[bytes[i] % charset.length];
    return out;
  };

  const generatePasswordForEdit = async (user) => {
    if (!user) return;
    if (!window.confirm(`Generate a new password for ${user.full_name}? It will replace their current password immediately. Use the Password Email button to send it to them, or Copy Password to share it manually.`)) return;
    setSaving(true);
    setError(null);
    const newPassword = generateRandomPassword(16);
    const pwRes = await adminAuth.updateUser(user.id, { password: newPassword });
    if (!pwRes.ok) { setError(pwRes.error); setSaving(false); return; }
    setEditingPassword(newPassword);
    setEditingPasswordRevealed(true);
    setSuccess('New password generated.');
    setTimeout(() => setSuccess(null), 2500);
    setSaving(false);
  };

  const sendPasswordEmailFromEdit = async (user) => {
    if (!user) return;
    if (!editingPassword) {
      setError('Generate a password first, then send the email.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (!window.confirm(`Email this password to ${user.email}?`)) return;
    setSaving(true);
    setError(null);
    const sent = await sendPasswordUserEmail(user.full_name, user.email, editingPassword);
    if (sent) {
      setSuccess(`Password emailed to ${user.email}.`);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(`Couldn't send the password email to ${user.email}. Share it manually or try again.`);
    }
    setSaving(false);
  };

  const resendSupplierUserWelcome = async (user) => {
    if (!window.confirm(`Send a welcome email to ${user.full_name} (${user.email})?`)) return;
    setSaving(true);
    setError(null);
    const sent = await sendWelcomeUserEmail(user.full_name, user.email);
    if (sent) {
      setSuccess(`Welcome email sent to ${user.email}.`);
      setTimeout(() => setSuccess(null), 3000);
    } else {
      setError(`Couldn't send the welcome email to ${user.email}. Try again in a moment.`);
    }
    setSaving(false);
  };

  const copyEditingPassword = async () => {
    if (!editingPassword) return;
    try {
      await navigator.clipboard.writeText(editingPassword);
      setSuccess('Password copied to clipboard');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Could not copy automatically — please reveal and copy manually');
      setTimeout(() => setError(null), 3000);
    }
  };

  const generatePasswordForNewUser = () => {
    setNewUser(prev => ({ ...prev, password: generateRandomPassword(16) }));
  };

  const refreshSupplierUsers = useCallback(async () => {
    const { data } = await supabase
      .from('supplier_users')
      .select('*')
      .eq('supplier_id', id)
      .order('full_name');
    const users = data || [];
    const enriched = await Promise.all(users.map(async (u) => {
      const r = await adminAuth.getUser(u.id);
      return { ...u, last_sign_in_at: r.user?.last_sign_in_at || null };
    }));
    setSupplierUsers(enriched);
  }, [id]);

  const refreshSupplier = useCallback(async () => {
    const { data: supplier } = await supabase.from('suppliers').select('*').eq('id', id).single();
    setForm(supplier);
    // Badge state — load alongside the supplier so the achievements
    // strip can render on first paint without a second loading pass.
    // Current + expired are both fetched; the strip distinguishes
    // them visually via the Badge component's `expired` prop.
    const [badgesRes, catsRes] = await Promise.all([
      supabase.from('supplier_badges').select('id, category_id, tier, awarded_at, expires_at, awarded_by_document_id, action_needed').eq('supplier_id', id),
      supabase.from('badge_categories').select('id, slug, name, bronze_name, silver_name, gold_name'),
    ]);
    setSupplierBadges(badgesRes.data || []);
    setBadgeCategoriesMap(new Map((catsRes.data || []).map(c => [c.id, c])));
  }, [id]);

  const refreshComplianceNotes = useCallback(async () => {
    const { data } = await supabase
      .from('supplier_compliance_notes')
      .select('id, body, created_at, created_by, author:internal_users!created_by(full_name)')
      .eq('supplier_id', id)
      .order('created_at', { ascending: true });
    setComplianceNotes(data || []);
  }, [id]);

  // Compliance status is normally derived from doc expiry by a Postgres
  // trigger (Red if any approved doc is expired, Amber if any expires in
  // 7 days, Green otherwise). Staff can override that derivation here, but
  // only via this handler which requires a reason — the reason is appended
  // to the compliance notes thread so the audit trail captures *why*.
  //
  // Setting an override pins compliance_status to the chosen value AND
  // populates compliance_status_override (the override gate). The trigger
  // and the daily cron will leave compliance_status alone while override
  // is non-null.
  const applyComplianceOverride = async () => {
    if (!form || !pendingOverrideStatus || !overrideReason.trim() || !currentUser) return;
    setSavingOverride(true);
    setError(null);
    const previousStatus = form.compliance_status || null;
    const reason = overrideReason.trim();

    const { error: updateError } = await supabase.from('suppliers').update({
      compliance_status: pendingOverrideStatus,
      compliance_status_override: pendingOverrideStatus,
      compliance_status_set_at: new Date().toISOString(),
      compliance_status_set_by: currentUser.id,
    }).eq('id', id);
    if (updateError) {
      setError(`Could not update compliance status: ${updateError.message}`);
      setTimeout(() => setError(null), 6000);
      setSavingOverride(false);
      return;
    }

    // Notes thread + history are best-effort. The override itself has
    // already landed; if these fail the audit trail just misses an entry.
    await supabase.from('supplier_compliance_notes').insert([{
      supplier_id: id,
      body: `Manually set status to ${pendingOverrideStatus.charAt(0).toUpperCase() + pendingOverrideStatus.slice(1)}. Reason: ${reason}`,
      created_by: currentUser.id,
    }]);
    if (previousStatus !== pendingOverrideStatus) {
      await supabase.from('supplier_compliance_status_history').insert([{
        supplier_id: id,
        old_status: previousStatus,
        new_status: pendingOverrideStatus,
        changed_by: currentUser.id,
        reason: reason,
      }]);
    }
    // Mirror the most recent body onto suppliers.compliance_status_notes so
    // the header tooltip / list-view tooltip preview stays in sync.
    await supabase.from('suppliers').update({ compliance_status_notes: reason }).eq('id', id);

    await Promise.all([refreshSupplier(), refreshComplianceNotes()]);
    setActivityLoaded(false);
    setShowOverrideModal(false);
    setPendingOverrideStatus(null);
    setOverrideReason('');
    setSavingOverride(false);
    setSuccess('Compliance status overridden.');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Clear the override and let the trigger derive compliance_status from
  // the doc state again. No reason required — reverting to auto is the
  // safe / default behaviour.
  const clearComplianceOverride = async () => {
    if (!form?.compliance_status_override) return;
    if (!window.confirm('Revert compliance status to auto-derived from document expiry?')) return;
    setSavingComplianceStatus(true);
    setError(null);
    // Clearing the override leaves compliance_status at its current value
    // momentarily; we then call the recompute RPC so it snaps to the
    // derived state. Cleaner than picking a value client-side that might
    // drift from the trigger's logic.
    const { error: updateError } = await supabase.from('suppliers').update({
      compliance_status_override: null,
    }).eq('id', id);
    if (updateError) {
      setError(`Could not clear override: ${updateError.message}`);
      setTimeout(() => setError(null), 6000);
      setSavingComplianceStatus(false);
      return;
    }
    await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: id });
    await refreshSupplier();
    setActivityLoaded(false);
    setSavingComplianceStatus(false);
    setSuccess('Reverted to auto-derived compliance status.');
    setTimeout(() => setSuccess(null), 3000);
  };

  // Manually-set risk classification — orthogonal to compliance status.
  // Saves immediately on click. Toggling the active button clears the flag
  // (back to "Not Set"). Doesn't write to a history table — risk changes
  // less frequently than compliance and the column-level set_at/set_by is
  // enough for now.
  //
  // AUTO-PUBLISH ON ANY TIER (Rob, 2026-08-14: "if a document has been flagged
  // as necessary for medium and high risk it should auto publish to those
  // suppliers"). Setting an active supplier's risk flag publishes whatever that
  // tier requires and isn't already on their portal.
  //
  // It used to fire only on a transition TO 'low', on the reasoning that the
  // higher tiers wanted a deliberate push per document. In practice that made
  // medium and high suppliers invisible to themselves: nothing reached their
  // portal, so they submitted nothing, so they appeared in the queue's
  // "Never sent" list for documents they had never been shown. The tier decides
  // WHAT is required, not WHETHER the supplier gets told.
  //
  // Publishing writes sent_at and nothing else — no email is sent from here, so
  // this cannot spam anyone. Already-published docs are never re-stamped.
  const setRiskFlag = async (newFlag) => {
    if (!form) return;
    const previousFlag = form.risk_flag || null;
    const next = previousFlag === newFlag ? null : newFlag;
    setSavingRiskFlag(true);
    setError(null);
    const { error: updateError } = await supabase.from('suppliers').update({
      risk_flag: next,
      risk_flag_set_at: new Date().toISOString(),
      risk_flag_set_by: currentUser?.id || null,
    }).eq('id', id);
    if (updateError) {
      setError(`Could not update risk flag: ${updateError.message}`);
      setTimeout(() => setError(null), 6000);
      setSavingRiskFlag(false);
      return;
    }
    // Any tier, not just low. `next` is null when the flag is being CLEARED —
    // that requires nothing of anybody, so there is nothing to publish.
    if (next && next !== previousFlag && form.status === 'active') {
      const { data: requirements } = await supabase
        .from('risk_document_requirements')
        .select('document_key')
        .eq('risk_level', next);
      const { data: existing } = await supabase
        .from('supplier_onboarding_documents')
        .select('document_key, sent_at, not_required, risk_override_applicable')
        .eq('supplier_id', id);
      const requiredKeys = new Set((requirements || []).map(r => r.document_key));
      for (const r of existing || []) {
        if (r.risk_override_applicable) requiredKeys.add(r.document_key);
      }
      const notRequiredKeys = new Set((existing || []).filter(r => r.not_required).map(r => r.document_key));
      const alreadyPublished = new Set((existing || []).filter(r => r.sent_at).map(r => r.document_key));
      const toPublish = Array.from(requiredKeys).filter(k => !notRequiredKeys.has(k) && !alreadyPublished.has(k));
      if (toPublish.length > 0) {
        const nowIso = new Date().toISOString();
        const rows = toPublish.map(k => ({
          supplier_id: id,
          document_key: k,
          sent_at: nowIso,
          sent_by: currentUser?.id || null,
          updated_at: nowIso,
        }));
        const { error: publishError } = await supabase
          .from('supplier_onboarding_documents')
          .upsert(rows, { onConflict: 'supplier_id,document_key' });
        if (!publishError) {
          await refreshOnboardingDocs();
          const tierName = next.charAt(0).toUpperCase() + next.slice(1);
          setSuccess(`Risk flag set to ${tierName} — published ${toPublish.length} compliance document${toPublish.length === 1 ? '' : 's'} to the supplier portal.`);
          setTimeout(() => setSuccess(null), 5000);
        }
      }
    }
    await refreshSupplier();
    setSavingRiskFlag(false);
  };

  // Onboarding tour controls (admin). Instant-persist, independent of the
  // main form Save — staff expect "Replay on next login" to take effect
  // immediately for support. Mirrors the supplier-portal state machine:
  // tips off → nothing onboarding-related shows; replay re-arms the
  // first-login auto-run by clearing the seen flag (and turning tips back
  // on so it can actually fire).
  const handleToggleOnboardingTips = async () => {
    if (!form?.id) return;
    const next = !(form.onboarding_tips_enabled ?? true);
    setForm(prev => ({ ...prev, onboarding_tips_enabled: next }));
    const { error: tErr } = await supabase.from('suppliers').update({ onboarding_tips_enabled: next }).eq('id', id);
    if (tErr) { setError(`Could not update onboarding tips: ${tErr.message}`); setTimeout(() => setError(null), 6000); }
  };
  const handleReplayOnNextLogin = async () => {
    if (!form?.id) return;
    setForm(prev => ({ ...prev, onboarding_tour_seen: false, onboarding_tips_enabled: true }));
    const { error: rErr } = await supabase.from('suppliers').update({ onboarding_tour_seen: false, onboarding_tips_enabled: true }).eq('id', id);
    if (rErr) { setError(`Could not reset the tour: ${rErr.message}`); setTimeout(() => setError(null), 6000); }
    else { setSuccess('The full tour will run next time this supplier signs in.'); setTimeout(() => setSuccess(null), 6000); }
  };

  // Deactivate: status='inactive' + audit trail, then revoke portal access
  // by disabling every portal user. New briefs already exclude inactive
  // suppliers; emails are suppressed by the status checks in the send paths.
  const handleDeactivate = async () => {
    const reason = deactivateReason.trim();
    if (!form?.id || reason.length < 5) return;
    setDeactivating(true);
    const nowIso = new Date().toISOString();
    const { error: e1 } = await supabase.from('suppliers').update({
      status: 'inactive',
      deactivated_at: nowIso,
      deactivated_by: currentUser?.id || null,
      deactivation_reason: reason,
    }).eq('id', id);
    if (e1) { setError(`Could not deactivate: ${e1.message}`); setTimeout(() => setError(null), 6000); setDeactivating(false); return; }
    // THIS is the step that actually revokes access — the suppliers row only
    // marks the company inactive. Unchecked, a failure here left every portal
    // user able to log in while the page reported "portal access revoked" and
    // greyed them all out. The most consequential silent write on this page.
    const { error: e2 } = await supabase.from('supplier_users').update({ is_active: false }).eq('supplier_id', id);
    setForm(prev => ({ ...prev, status: 'inactive', deactivated_at: nowIso, deactivated_by: currentUser?.id || null, deactivation_reason: reason }));
    setDeactivating(false);
    setShowDeactivateModal(false);
    setDeactivateReason('');
    if (e2) {
      // Don't fake the revoked state in the UI — reload so the rows show what
      // is actually true, and say plainly that access is still live.
      await refreshSupplierUsers();
      setError(`Supplier marked inactive, but their portal logins are STILL ACTIVE — disable them individually. (${e2.message})`);
      return;
    }
    setSupplierUsers(prev => prev.map(u => ({ ...u, is_active: false })));
    setSuccess('Supplier deactivated — portal access revoked and emails suppressed.');
    setTimeout(() => setSuccess(null), 6000);
  };

  // Reactivate: clear the audit + status, and re-enable portal users. (All
  // portal users are restored — including any that were disabled before
  // deactivation; re-disable individually if needed.)
  const handleReactivate = async () => {
    if (!form?.id) return;
    setReactivating(true);
    const { error: e1 } = await supabase.from('suppliers').update({
      status: 'active',
      deactivated_at: null,
      deactivated_by: null,
      deactivation_reason: null,
    }).eq('id', id);
    if (e1) { setError(`Could not reactivate: ${e1.message}`); setTimeout(() => setError(null), 6000); setReactivating(false); return; }
    // The mirror of deactivation: if this fails the supplier is active again
    // but nobody can log in, and the page claims access was restored. Less
    // dangerous than the reverse, equally confusing.
    const { error: e2 } = await supabase.from('supplier_users').update({ is_active: true }).eq('supplier_id', id);
    setForm(prev => ({ ...prev, status: 'active', deactivated_at: null, deactivated_by: null, deactivation_reason: null }));
    setReactivating(false);
    setConfirmingReactivate(false);
    if (e2) {
      await refreshSupplierUsers();
      setError(`Supplier reactivated, but their portal logins are still disabled — re-enable them individually. (${e2.message})`);
      return;
    }
    setSupplierUsers(prev => prev.map(u => ({ ...u, is_active: true })));
    setSuccess('Supplier reactivated — portal access restored.');
    setTimeout(() => setSuccess(null), 6000);
  };

  // Append a new note to the thread. Also stamps the latest body onto
  // suppliers.compliance_status_notes so existing previews (header tooltip,
  // Overall Compliance drill-down) keep working without a JOIN.
  const addComplianceNote = async () => {
    const body = newComplianceNote.trim();
    if (!body || !currentUser) return;
    setSavingComplianceNote(true);
    setError(null);
    const { error: insertError } = await supabase.from('supplier_compliance_notes').insert([{
      supplier_id: id,
      body,
      created_by: currentUser.id,
    }]);
    if (insertError) {
      setError(`Could not save note: ${insertError.message}`);
      setTimeout(() => setError(null), 6000);
      setSavingComplianceNote(false);
      return;
    }
    await supabase.from('suppliers').update({ compliance_status_notes: body }).eq('id', id);
    setNewComplianceNote('');
    await Promise.all([refreshComplianceNotes(), refreshSupplier()]);
    setSavingComplianceNote(false);
  };

  const refreshOnboardingDocs = useCallback(async () => {
    const { data } = await supabase
      .from('supplier_onboarding_documents')
      .select('*, sent_by_user:internal_users!sent_by(full_name), signed_marked_by_user:internal_users!signed_marked_by(full_name), expiry_approved_by_user:internal_users!expiry_approved_by(full_name)')
      .eq('supplier_id', id)
      // Archived documents leave the working record (detail-page §5); the file
      // and history remain, reachable from the document's own page.
      .is('archived_at', null);
    setOnboardingDocs(data || []);
  }, [id]);

  // Acknowledge-type documents don't touch supplier_onboarding_documents when
  // they're signed — the signature lands in document_acknowledgements. Without
  // these rows this page had no way to know a document had been actioned, so a
  // signed Code of Conduct still read "Not Published". The admin queue and the
  // supplier portal both already join them in; this page was the odd one out.
  const refreshAcknowledgements = useCallback(async () => {
    const { data } = await supabase
      .from('document_acknowledgements')
      .select('*')
      .eq('supplier_id', id)
      .order('acknowledged_at', { ascending: false });
    setAcknowledgements(data || []);
  }, [id]);

  const refreshDebriefNotes = useCallback(async () => {
    const { data: noteRows } = await supabase
      .from('debrief_notes')
      .select('id, body, created_at, topic_id, supplier_id')
      .eq('supplier_id', id)
      .order('created_at', { ascending: false });
    if (!noteRows?.length) { setDebriefNotes([]); return; }
    // Topic → debrief, for the heading and the project it came from.
    const topicIds = [...new Set(noteRows.map(n => n.topic_id).filter(Boolean))];
    const { data: topicRows } = await supabase
      .from('debrief_topics').select('id, title, debrief_id').in('id', topicIds);
    const debriefIds = [...new Set((topicRows || []).map(t => t.debrief_id).filter(Boolean))];
    const [{ data: debriefRows }, { data: actionRows }] = await Promise.all([
      debriefIds.length
        ? supabase.from('project_debriefs').select('id, project_ref, project_name, debrief_date, status').in('id', debriefIds)
        : Promise.resolve({ data: [] }),
      supabase.from('debrief_actions').select('id, note_id, owner, timeframe, status').eq('supplier_id', id),
    ]);
    const topicById = new Map((topicRows || []).map(t => [t.id, t]));
    const debriefById = new Map((debriefRows || []).map(d => [d.id, d]));
    const actionByNote = new Map((actionRows || []).map(a => [a.note_id, a]));
    setDebriefNotes(noteRows.map(n => {
      const topic = topicById.get(n.topic_id) || null;
      const debrief = topic ? debriefById.get(topic.debrief_id) : null;
      return { ...n, topic_title: topic?.title || '', debrief, action: actionByNote.get(n.id) || null };
    // A note whose debrief is a DRAFT isn't a finding yet — someone is still
    // writing it up. Only published debriefs surface on a supplier's record.
    }).filter(n => n.debrief?.status === 'published'));
  }, [id]);

  const refreshChecklist = useCallback(async () => {
    const { data } = await supabase
      .from('supplier_onboarding_checklist')
      .select('step_key, completed_at, completed_by, completed_by_user:internal_users!completed_by(full_name)')
      .eq('supplier_id', id);
    setChecklistRows(data || []);
  }, [id]);

  useEffect(() => { refreshChecklist(); }, [refreshChecklist]);

  // Tick = insert a row; untick = delete it. A row EXISTS only when the step is
  // done, so there's no "false" state to disagree with the absence of a row.
  // Both write instantly rather than waiting for a Save — a checklist someone
  // ticks mid-conversation shouldn't need a second action to stick.
  const toggleChecklistStep = async (stepKey, done) => {
    if (checklistBusy) return;
    setChecklistBusy(stepKey);
    const query = done
      ? supabase.from('supplier_onboarding_checklist')
          .insert([{ supplier_id: id, step_key: stepKey, completed_by: currentUser?.id || null }])
      : supabase.from('supplier_onboarding_checklist')
          .delete().eq('supplier_id', id).eq('step_key', stepKey);
    const { error: stepError } = await query;
    if (stepError) {
      setError(`Could not update the onboarding step: ${stepError.message}`);
      setTimeout(() => setError(null), 6000);
    } else {
      await refreshChecklist();
    }
    setChecklistBusy(null);
  };

  // Per-document state changes upsert into supplier_onboarding_documents.
  // Each handler scopes its loading state to the doc key being acted on so
  // multiple rows can be operated independently without disabling the whole
  // section.
  const upsertOnboardingDoc = async (docKey, patch) => {
    setSavingDocKey(docKey);
    setError(null);
    // Snapshot whether an override is in force *before* the upsert — the
    // sod trigger skips recompute when override is set, so we need to
    // know after the write whether to clear the override ourselves and
    // re-run the recompute.
    const hadOverride = Boolean(form?.compliance_status_override);
    const { error: upsertError } = await supabase
      .from('supplier_onboarding_documents')
      .upsert({
        supplier_id: id,
        document_key: docKey,
        updated_at: new Date().toISOString(),
        ...patch,
      }, { onConflict: 'supplier_id,document_key' });
    if (upsertError) {
      setError(`Could not update document: ${upsertError.message}`);
      setTimeout(() => setError(null), 6000);
    } else {
      // If staff had pinned the compliance status manually, a doc change
      // is a signal that the document state is the new source of truth.
      // Clear the override and re-run the recompute RPC so the supplier's
      // status flips to whatever the (possibly-changed) docs now imply.
      // Staff can manually override again afterwards if needed.
      if (hadOverride) {
        // Checked before claiming it. Unchecked, a failure left the supplier
        // pinned to a manual status while the message said it was back on
        // auto — and a pinned status is exactly the thing someone stops
        // watching once they've been told it's cleared.
        const { error: clearErr } = await supabase.from('suppliers')
          .update({ compliance_status_override: null })
          .eq('id', id);
        if (clearErr) {
          setError(`The document saved, but the manual compliance override couldn't be cleared: ${clearErr.message} — this supplier is still pinned to it.`);
          setTimeout(() => setError(null), 8000);
        } else {
          await supabase.rpc('recompute_supplier_compliance_status', { p_supplier_id: id });
          setSuccess('Document updated — manual compliance override cleared, status back on auto.');
          setTimeout(() => setSuccess(null), 4500);
        }
        setActivityLoaded(false);
      }
      // The sod_recompute_compliance trigger fires on this upsert and
      // updates suppliers.compliance_status in the DB (when no override
      // was set, or after we just cleared it above). Refresh the supplier
      // alongside the onboarding rows so the header pill / compliance
      // card pick up the new value without a page reload.
      await Promise.all([refreshOnboardingDocs(), refreshSupplier()]);
    }
    setSavingDocKey(null);
  };

  // Activate an inactive supplier — must pick a risk flag at the same time.
  // Sets status='active' + risk_flag in one round-trip so the supplier is
  // immediately ready to be invited to briefs. Optionally also fires a
  // launch/welcome email to the supplier's main + secondary contacts (one
  // per recipient, privacy rule). The send is logged to supplier_email_log
  // so it surfaces in the Activity Log.
  const handleActivateSupplier = async () => {
    if (!activateRiskFlag) return;
    setActivating(true);
    setError(null);
    const { error: updateError } = await supabase.from('suppliers').update({
      status: 'active',
      risk_flag: activateRiskFlag,
      risk_flag_set_at: new Date().toISOString(),
      risk_flag_set_by: currentUser?.id || null,
    }).eq('id', id);
    if (updateError) {
      setError(`Could not activate supplier: ${updateError.message}`);
      setTimeout(() => setError(null), 6000);
      setActivating(false);
      return;
    }

    // Fire-and-forget launch email if staff opted in. Sent to main +
    // secondary contact, one email per recipient. Failure here doesn't
    // roll back the activation — surface a softer success message instead.
    let emailReport = '';
    // Record-only suppliers never receive emails — gate the launch send
    // even if staff ticked the option, so a misconfig can't leak a mail.
    if (activateSendEmail && form?.main_contact_email && !form?.is_record_only && form?.status !== 'inactive') {
      const recipients = [
        { email: form.main_contact_email, name: form.main_contact_name },
        ...(form.secondary_contact_email && form.secondary_contact_email !== form.main_contact_email
          ? [{ email: form.secondary_contact_email, name: form.secondary_contact_name }]
          : []),
      ];
      const portalUrl = `${window.location.origin}/portal`;
      for (const r of recipients) {
        const { subject, html } = supplierActivationEmail({
          contactName: r.name,
          supplierName: form.supplier_name,
          portalUrl,
        });
        try { await sendEmail({ to: r.email, subject, html }); } catch (err) {
          console.warn('supplierActivationEmail failed', { to: r.email, error: err?.message });
        }
      }
      await supabase.from('supplier_email_log').insert([{
        supplier_id: id,
        email_kind: 'supplier_activation',
        recipients: recipients.map(r => r.email),
        sent_by: currentUser?.id || null,
      }]);
      emailReport = ` Launch email sent to ${recipients.map(r => r.email).join(', ')}.`;
      setActivityLoaded(false);
    }

    // Auto-publish every required compliance doc to the supplier portal
    // if staff opted in. Skips docs that already have sent_at so a re-
    // activation doesn't re-stamp signed rows. Required = level
    // requirements + this supplier's per-row overrides, minus anything
    // they've marked Not Required.
    let publishReport = '';
    if (activatePublishDocs) {
      const { data: requirements } = await supabase
        .from('risk_document_requirements')
        .select('document_key')
        .eq('risk_level', activateRiskFlag);
      const { data: overrides } = await supabase
        .from('supplier_onboarding_documents')
        .select('document_key, sent_at, not_required, risk_override_applicable')
        .eq('supplier_id', id);
      const requiredKeys = new Set((requirements || []).map(r => r.document_key));
      // Per-supplier overrides also count as "required" for publishing.
      for (const r of overrides || []) {
        if (r.risk_override_applicable) requiredKeys.add(r.document_key);
      }
      const notRequiredKeys = new Set((overrides || []).filter(r => r.not_required).map(r => r.document_key));
      const alreadyPublished = new Set((overrides || []).filter(r => r.sent_at).map(r => r.document_key));
      const toPublish = Array.from(requiredKeys).filter(k => !notRequiredKeys.has(k) && !alreadyPublished.has(k));
      if (toPublish.length > 0) {
        const nowIso = new Date().toISOString();
        const rows = toPublish.map(k => ({
          supplier_id: id,
          document_key: k,
          sent_at: nowIso,
          sent_by: currentUser?.id || null,
          updated_at: nowIso,
        }));
        const { error: publishError } = await supabase
          .from('supplier_onboarding_documents')
          .upsert(rows, { onConflict: 'supplier_id,document_key' });
        if (publishError) {
          publishReport = ` Could not publish compliance documents: ${publishError.message}.`;
        } else {
          publishReport = ` Published ${toPublish.length} compliance document${toPublish.length === 1 ? '' : 's'} to the supplier portal.`;
          await refreshOnboardingDocs();
        }
      } else {
        publishReport = ' No new compliance documents to publish — supplier already had every required doc on their portal.';
      }
    }

    await refreshSupplier();
    setShowActivateModal(false);
    setActivateRiskFlag(null);
    setActivateSendEmail(false);
    setActivatePublishDocs(true);
    setSuccess(`Supplier activated.${publishReport}${emailReport}`);
    setTimeout(() => setSuccess(null), 6000);
    setActivating(false);
  };

  // Per-supplier override of the risk-based applicability filter. Forces a
  // document that wouldn't normally apply to this supplier (because their
  // risk_flag's required-doc set doesn't include it) into the applicable
  // list. The reverse (clearing the override) puts the row back under the
  // global risk config.
  const handleSetDocRiskOverride = (docKey, applicable) => upsertOnboardingDoc(docKey, {
    risk_override_applicable: applicable,
  });

  // Generic compliance reminder — section-level. Doesn't list specific
  // documents (the supplier sees the canonical list in their portal); it's
  // just a nudge to log in and complete what's outstanding. Sent to the
  // supplier's PORTAL USERS (supplier_users) — the people who can actually log
  // in and act — not the finance/main contact, who may not have a login. One
  // email per recipient, matching the privacy rule used elsewhere.
  const handleSendComplianceReminder = async () => {
    if (form?.is_record_only) {
      setError('This is a record-only supplier — no emails are sent to them.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    if (form?.status === 'inactive') {
      setError('This supplier is deactivated — emails are suppressed. Reactivate them first.');
      setTimeout(() => setError(null), 5000);
      return;
    }
    const recipients = supplierUsers
      .filter(u => u.is_active && u.email)
      .map(u => ({ email: u.email, name: u.full_name }));
    if (recipients.length === 0) {
      setError('No active portal users on this supplier — add a portal user before sending a compliance reminder (only they can log in to complete it).');
      setTimeout(() => setError(null), 6000);
      return;
    }
    const recipientLabel = recipients.map(r => r.email).join(' and ');
    if (!window.confirm(`Send a generic compliance reminder email to ${recipientLabel}?`)) return;
    setSendingComplianceReminder(true);
    setError(null);
    const portalUrl = `${window.location.origin}/portal`;
    for (const r of recipients) {
      const { subject, html } = complianceReminderEmail({
        contactName: r.name,
        supplierName: form.supplier_name,
        portalUrl,
      });
      try { await sendEmail({ to: r.email, subject, html }); } catch (err) {
        console.warn('complianceReminderEmail failed', { to: r.email, error: err?.message });
      }
    }
    // Audit: one row per send-event (not per recipient) so the Activity Log
    // reads "sent to X and Y" as a single action by one user. Best-effort —
    // if the insert fails, the email itself still went out so we don't
    // surface the failure to the user.
    await supabase.from('supplier_email_log').insert([{
      supplier_id: id,
      email_kind: 'compliance_reminder',
      recipients: recipients.map(r => r.email),
      sent_by: currentUser?.id || null,
    }]);
    setActivityLoaded(false); // force the Activity Log to refetch on next view
    setSuccess(`Compliance reminder sent to ${recipientLabel}.`);
    setTimeout(() => setSuccess(null), 4000);
    setSendingComplianceReminder(false);
  };

  useEffect(() => {
    const getData = async () => {
      const { data: cats } = await supabase.from('list_values').select('value').eq('list_name', 'supplier_category').order('display_order');
      const { data: regs } = await supabase.from('list_values').select('value').eq('list_name', 'region').order('display_order');
      const { data: supplier } = await supabase.from('suppliers').select('*').eq('id', id).single();
      setCategories(cats || []);
      setRegions(regs || []);
      setForm(supplier);
      setLoading(false);
    };
    getData();
    refreshSupplierUsers();
    refreshOnboardingDocs();
    refreshAcknowledgements();
    refreshComplianceNotes();
    refreshDebriefNotes();
  }, [id, refreshSupplierUsers, refreshOnboardingDocs, refreshAcknowledgements, refreshComplianceNotes, refreshDebriefNotes]);

  // When a Finance user opens the page and the finance user name is empty, pre-populate it.
  useEffect(() => {
    if (form && currentUser && isFinance(currentUser) && !form.finance_user_name) {
      setForm(prev => ({ ...prev, finance_user_name: currentUser.full_name }));
    }
  }, [form, currentUser]);

  // Load the required-document set for this supplier's risk_flag from
  // risk_document_requirements. Refetches when the risk flag changes (e.g.
  // staff activates the supplier or overrides the flag). When risk_flag is
  // null the set is empty and the Documents section uses a fallback ("set a
  // risk flag to see required documents").
  useEffect(() => {
    if (!form?.risk_flag) {
      setRequiredDocKeysRaw(new Set());
      return;
    }
    const loadRequirements = async () => {
      const { data } = await supabase
        .from('risk_document_requirements')
        .select('document_key')
        .eq('risk_level', form.risk_flag);
      setRequiredDocKeysRaw(new Set((data || []).map(r => r.document_key)));
    };
    loadRequirements();
  }, [form?.risk_flag]);

  // The requirement set the page actually uses: required keys that still
  // point at a LIVE document. risk_document_requirements keeps a row after
  // its document is archived, deleted or (the common case) never published,
  // so the raw set overstated what this supplier owes — the tier card read
  // "12 documents required" for keys that had no live document behind them.
  //
  // Empty until the document list has loaded, so nothing downstream acts on a
  // half-loaded picture — in particular the auto-publish effect below, which
  // was stamping sent_at on unpublished drafts and making them outstanding.
  const liveDocKeys = useMemo(
    () => new Set(complianceDocs.map(d => d.key)),
    [complianceDocs],
  );
  // Latest acknowledgement per document. The loader orders newest-first, so
  // the first row seen for a key wins.
  const ackByKey = useMemo(() => {
    const m = new Map();
    for (const a of acknowledgements) if (!m.has(a.document_key)) m.set(a.document_key, a);
    return m;
  }, [acknowledgements]);
  const requiredDocKeys = useMemo(
    () => (complianceDocsLoaded ? liveRequiredKeys(requiredDocKeysRaw, liveDocKeys) : new Set()),
    [complianceDocsLoaded, requiredDocKeysRaw, liveDocKeys],
  );
  // Requirements pointing at a document that no longer exists or was never
  // published. Surfaced on the tier card rather than silently dropped — a
  // requirement configured against a missing document is a configuration
  // problem someone should fix, not a number to quietly shrink.
  const staleRequirementCount = complianceDocsLoaded
    ? [...requiredDocKeysRaw].filter(k => !liveDocKeys.has(k)).length
    : 0;

  // Auto-publish reconciliation, ALL TIERS (Rob, 2026-08-14).
  //
  // setRiskFlag publishes when the flag transitions, but the required set drifts
  // afterwards: a document added to a tier later, or a supplier activated
  // through a code path that didn't publish, leaves documents required of
  // somebody and sitting unpublished. Symptom: rows read "Not Published" on a
  // supplier the policy says should have them.
  //
  // This used to be low-risk only, which is how medium and high suppliers ended
  // up with nothing on their portal at all — and then appeared in the queue's
  // "Never sent" list for documents they had never been shown. The tier decides
  // WHAT is required, not WHETHER the supplier is told about it.
  //
  // It repairs a supplier as their record is opened, so it rolls out gradually
  // rather than as one estate-wide write. Publishing stamps sent_at and sends
  // no email, so nobody is spammed by a page visit. Already-published docs are
  // never re-stamped — sent_at is only inserted, never overwritten.
  useEffect(() => {
    if (!form || form.status !== 'active' || !form.risk_flag) return;
    if (!id || !currentUser) return;
    if (requiredDocKeys.size === 0) return;
    const existingByKey = new Map((onboardingDocs || []).map(r => [r.document_key, r]));
    const toPublish = [];
    for (const key of requiredDocKeys) {
      const existing = existingByKey.get(key);
      if (existing && (existing.sent_at || existing.not_required)) continue;
      toPublish.push(key);
    }
    if (toPublish.length === 0) return;
    let cancelled = false;
    (async () => {
      const nowIso = new Date().toISOString();
      const rows = toPublish.map(k => ({
        supplier_id: id,
        document_key: k,
        sent_at: nowIso,
        sent_by: currentUser.id || null,
        updated_at: nowIso,
      }));
      const { error } = await supabase
        .from('supplier_onboarding_documents')
        .upsert(rows, { onConflict: 'supplier_id,document_key' });
      if (!error && !cancelled) {
        refreshOnboardingDocs();
      }
    })();
    return () => { cancelled = true; };
  }, [form, id, currentUser, requiredDocKeys, onboardingDocs, refreshOnboardingDocs]);

  // Load the master compliance-doc list from fusion_documents. Active rows
  // with a compliance_doc_key set are exactly what the Admin > Risk Flag
  // Configuration grid lists, so this keeps the supplier page in lockstep
  // with whatever staff have configured.
  useEffect(() => {
    const loadComplianceDocs = async () => {
      // LIVE documents only. is_active defaults to true, so filtering on it
      // alone returned unpublished drafts — which then counted as required
      // documents, were auto-published to the supplier by the effect above,
      // and showed as outstanding against them (see lib/liveDocuments).
      const { data } = await applyLiveDocumentFilter(supabase
        .from('fusion_documents')
        .select('compliance_doc_key, document_name, satisfied_by')
        .not('compliance_doc_key', 'is', null))
        .order('document_name');
      // satisfied_by is carried through: it decides whether a document's
      // evidence lives on the onboarding row or in document_acknowledgements
      // (lib/complianceRecord). Without it every document is treated as an
      // upload, which is what hid signed acknowledgements from this page.
      setComplianceDocs((data || []).map(d => ({
        key: d.compliance_doc_key,
        label: d.document_name,
        satisfiedBy: d.satisfied_by || 'upload',
      })));
      setComplianceDocsLoaded(true);
    };
    loadComplianceDocs();
  }, []);

  // Lazy-load briefs the first time the user opens the Brief Activity OR
  // Activity tab. Recipient rows exist for drafts too, so we filter those out —
  // only briefs that were actually sent (or have moved past sending) count
  // as "invited". notified_at lets the Activity tab show when this supplier
  // was emailed about each brief.
  useEffect(() => {
    if (briefsLoaded) return;
    if (activeTab !== 'briefs' && activeTab !== 'activity') return;
    const loadBriefs = async () => {
      setBriefsLoading(true);
      const { data } = await supabase
        .from('brief_recipients')
        .select('id, status, decline_reason, response_deadline, notified_at, viewed_at, declined_at, briefs(id, brief_name, fusion_project_name, fusion_project_number, brief_category, status, created_at, archived_at)')
        .eq('supplier_id', id);
      const rows = (data || [])
        .filter(r => r.briefs && r.briefs.status !== 'draft')
        .sort((a, b) => new Date(b.briefs?.created_at || 0) - new Date(a.briefs?.created_at || 0));
      setBriefs(rows);
      setBriefsLoaded(true);
      setBriefsLoading(false);
    };
    loadBriefs();
  }, [activeTab, briefsLoaded, id]);

  // Lazy-load Activity tab specifics: manual notes + this supplier's brief
  // responses. Notes are joined to the author so we can show who wrote them.
  useEffect(() => {
    if (activeTab !== 'activity' || activityLoaded) return;
    const loadActivity = async () => {
      setActivityLoading(true);
      const [{ data: notes }, { data: resps }, { data: history }, { data: logins }, { data: emails }, { data: ackAudit }, { data: questions }, { data: updateReqs }, { data: extensions }] = await Promise.all([
        supabase
          .from('supplier_activity_notes')
          .select('id, body, created_at, created_by, author:internal_users!created_by(full_name)')
          .eq('supplier_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('brief_responses')
          .select('id, brief_id, total_price, submitted_at, briefs(brief_name)')
          .eq('supplier_id', id)
          .eq('is_submitted', true),
        supabase
          .from('supplier_compliance_status_history')
          .select('id, old_status, new_status, changed_at, changed_by, reason, author:internal_users!changed_by(full_name)')
          .eq('supplier_id', id)
          .order('changed_at', { ascending: false }),
        supabase
          .from('supplier_login_events')
          .select('id, signed_in_at, user_email')
          .eq('supplier_id', id)
          .order('signed_in_at', { ascending: false })
          .limit(100),
        supabase
          .from('supplier_email_log')
          .select('id, email_kind, recipients, sent_at, sent_by, sender:internal_users!sent_by(full_name)')
          .eq('supplier_id', id)
          .order('sent_at', { ascending: false })
          .limit(100),
        supabase
          .from('compliance_audit_log')
          .select('id, action, document_key, created_at, actor_id, meta, actor:internal_users!actor_id(full_name)')
          .eq('supplier_id', id)
          .in('action', ['acknowledged', 'acknowledge_approved', 'acknowledge_rejected'])
          .order('created_at', { ascending: false })
          .limit(200),
        // Questions this supplier asked on any brief (+ whether/when answered).
        supabase
          .from('brief_questions')
          .select('id, body, created_at, brief_id, briefs(brief_name), brief_question_answers(id, created_at, published)')
          .eq('supplier_id', id)
          .order('created_at', { ascending: false }),
        // Post-deadline update requests they've sent.
        supabase
          .from('brief_update_requests')
          .select('id, message, created_at, brief_id, briefs(brief_name)')
          .eq('supplier_id', id)
          .order('created_at', { ascending: false }),
        // Pending deadline-extension requests (cleared on grant — the grant
        // itself surfaces as a deadline_extended email above).
        supabase
          .from('brief_recipients')
          .select('id, extension_requested_at, extension_request_note, brief_id, briefs(brief_name)')
          .eq('supplier_id', id)
          .not('extension_requested_at', 'is', null),
      ]);
      setActivityNotes(notes || []);
      setActivityResponses(resps || []);
      setActivityComplianceHistory(history || []);
      setActivityLoginEvents(logins || []);
      setActivityEmailEvents(emails || []);
      setActivityAckEvents(ackAudit || []);
      setActivityQuestions(questions || []);
      setActivityUpdateRequests(updateReqs || []);
      setActivityExtensions(extensions || []);
      setActivityLoaded(true);
      setActivityLoading(false);
    };
    loadActivity();
  }, [activeTab, activityLoaded, id]);

  const handleSaveNote = async () => {
    const body = newNote.trim();
    if (!body || !currentUser) return;
    setSavingNote(true);
    const { error: insertError } = await supabase.from('supplier_activity_notes').insert([{
      supplier_id: id,
      body,
      created_by: currentUser.id,
    }]);
    if (insertError) {
      setError(`Could not save note: ${insertError.message}`);
    } else {
      setNewNote('');
      // Refresh just the notes
      const { data: notes } = await supabase
        .from('supplier_activity_notes')
        .select('id, body, created_at, created_by, author:internal_users!created_by(full_name)')
        .eq('supplier_id', id)
        .order('created_at', { ascending: false });
      setActivityNotes(notes || []);
    }
    setSavingNote(false);
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    const { error: deleteError } = await supabase.from('supplier_activity_notes').delete().eq('id', noteId);
    if (deleteError) {
      setError(`Could not delete note: ${deleteError.message}`);
    } else {
      setActivityNotes(prev => prev.filter(n => n.id !== noteId));
    }
  };

  // Compliance notes have no delete (§6). They record WHY a compliance
  // decision was made — an override, an investigation, a judgement call — and
  // deleting one rewrites that record. A correction is another note.

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  // Toggle the per-user pin for this supplier. Optimistic — the UI
  // flips immediately and rolls back if the DB write fails. Pinning
  // is a fast, low-stakes signal staff use to surface frequent
  // collaborators on the dashboard and Pinned page; scoped per user
  // via the user_pinned_suppliers join table.
  const togglePinned = async () => {
    if (!form?.id || !currentUser?.id) return;
    const newValue = !userPinned;
    setUserPinned(newValue);
    const { error: dbError } = newValue
      ? await supabase.from('user_pinned_suppliers').upsert(
          { user_id: currentUser.id, supplier_id: form.id },
          { onConflict: 'user_id,supplier_id' }
        )
      : await supabase.from('user_pinned_suppliers')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('supplier_id', form.id);
    if (dbError) {
      setUserPinned(!newValue);
      setError(`Could not ${newValue ? 'pin' : 'unpin'} supplier: ${dbError.message}`);
      setTimeout(() => setError(null), 6000);
    }
  };

  // Wrap togglePinned with the burst animation. Pin only — unpin is
  // a quiet colour-only transition per spec (undoing an action
  // doesn't warrant the celebration).
  const handlePinClick = () => {
    if (!userPinned) {
      setPinPulsing(true);
      firePinBurst(pinStarRef.current);
      setTimeout(() => setPinPulsing(false), PIN_PULSE_MS);
    }
    togglePinned();
  };

  // Hard-delete the supplier. Admin / super_user only — the button
  // is gated server-side by the same isAdmin() helper used everywhere
  // else for elevated actions.
  //
  // Cascade strategy:
  // - brief_responses + brief_recipients are pre-deleted because the
  //   initial-schema FKs to suppliers don't carry ON DELETE CASCADE
  //   (verified by searching the migrations folder; only later tables
  //   like brief_send_log, compliance_audit_log, supplier_badges,
  //   etc. cascade automatically).
  // - supplier_users + their auth.users entries go via the service
  //   role, mirroring the internal-user delete flow in Admin.js.
  // - briefs.awarded_supplier_id is ON DELETE SET NULL so awarded
  //   briefs survive; the brief_award_history rows preserved the
  //   awarded_supplier_name at write time so the audit reads stay
  //   intact even after the supplier row is gone.
  const [deleting, setDeleting] = useState(false);
  const handleDeleteSupplier = async () => {
    if (!form?.id) return;
    if (!isAdmin(currentUser)) {
      setError('Only admins and super users can delete suppliers.');
      setTimeout(() => setError(null), 4000);
      return;
    }
    const message =
      `Permanently delete "${form.supplier_name}"?\n\n` +
      `This removes the supplier and every related record — brief recipient rows, supplier responses, compliance documents, status history, audit log, linked supplier-portal logins.\n\n` +
      `Briefs they've been awarded keep existing but lose their link to this supplier (the award amount + history are preserved with the supplier name snapshotted at award time).\n\n` +
      `This cannot be undone. If you might need this record back later, deactivate the supplier instead.`;
    if (!window.confirm(message)) return;
    setDeleting(true);
    setError(null);

    // 1) Pre-delete brief_responses for this supplier. Their child
    //    rows (line items, attachments) cascade from brief_responses.
    const { error: respError } = await supabase.from('brief_responses').delete().eq('supplier_id', form.id);
    if (respError) { setError(`Could not delete responses: ${respError.message}`); setDeleting(false); return; }

    // 2) Pre-delete brief_recipients for this supplier.
    const { error: recError } = await supabase.from('brief_recipients').delete().eq('supplier_id', form.id);
    if (recError) { setError(`Could not delete brief recipients: ${recError.message}`); setDeleting(false); return; }

    // 3) Linked supplier-portal users — fetch ids first so we can
    //    delete their auth.users entries after the row drop.
    const { data: linkedUsers, error: lookupError } = await supabase
      .from('supplier_users')
      .select('id')
      .eq('supplier_id', form.id);
    if (lookupError) { setError(`Could not look up supplier users: ${lookupError.message}`); setDeleting(false); return; }
    if (linkedUsers && linkedUsers.length > 0) {
      const { error: usersError } = await supabase.from('supplier_users').delete().eq('supplier_id', form.id);
      if (usersError) { setError(`Could not delete linked supplier users: ${usersError.message}`); setDeleting(false); return; }
      // Best-effort auth cleanup via the admin endpoint. If one fails, the row
      // delete still succeeded so the supplier delete shouldn't be blocked.
      for (const u of linkedUsers) {
        await adminAuth.deleteUser(u.id);
      }
    }

    // 4) The supplier itself. Cascades cover compliance docs, status
    //    history, notes, badges, send/email log, audit log, pins.
    const { error: supError } = await supabase.from('suppliers').delete().eq('id', form.id);
    if (supError) { setError(`Could not delete supplier: ${supError.message}`); setDeleting(false); return; }

    navigate('/suppliers');
  };

  // Fetch the current user's pin status on this supplier. Runs whenever
  // the supplier or user changes — keeps the header Pin button accurate
  // without baking the join into the main supplier fetch.
  useEffect(() => {
    if (!form?.id || !currentUser?.id) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_pinned_suppliers')
        .select('supplier_id')
        .eq('user_id', currentUser.id)
        .eq('supplier_id', form.id)
        .maybeSingle();
      if (!cancelled) setUserPinned(!!data);
    })();
    return () => { cancelled = true; };
  }, [form?.id, currentUser?.id]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      estimated_first_order: form.estimated_first_order === '' || form.estimated_first_order === null ? null : Number(form.estimated_first_order),
      estimated_annual_spend: form.estimated_annual_spend === '' || form.estimated_annual_spend === null ? null : Number(form.estimated_annual_spend),
    };
    const { error: updateError } = await supabase.from('suppliers').update(payload).eq('id', id);
    if (updateError) { setError(updateError.message); setSaving(false); return; }
    setEditing(false);
    setSaving(false);
    setSuccess('Supplier updated.');
    setTimeout(() => setSuccess(null), 2500);
  };

  // Finance users can edit and save only their section without entering the
  // global Edit Supplier mode. Persists only the finance_* columns so other
  // unsaved edits in the form state can't be accidentally written.
  const saveFinanceSection = async () => {
    setSavingFinance(true);
    setError(null);
    setFinanceFeedback(null);
    const financePayload = {
      finance_credit_check_completed: form.finance_credit_check_completed,
      finance_risk_flags: form.finance_risk_flags,
      finance_risk_flag_details: form.finance_risk_flag_details,
      finance_approved: form.finance_approved,
      finance_approver_name: form.finance_approver_name,
      finance_account_code_created: form.finance_account_code_created,
      finance_account_created_date: form.finance_account_created_date,
      finance_user_name: form.finance_user_name,
      finance_notes: form.finance_notes,
    };
    const { error: updateError } = await supabase.from('suppliers').update(financePayload).eq('id', id);
    if (updateError) {
      setError(updateError.message);
      setFinanceFeedback({ type: 'error', message: updateError.message });
      setSavingFinance(false);
      return;
    }
    await refreshSupplier();
    setSavingFinance(false);
    setSuccess('Finance section saved.');
    setFinanceFeedback({ type: 'success', message: 'Finance section saved.' });
    setTimeout(() => setSuccess(null), 2500);
    setTimeout(() => setFinanceFeedback(null), 4000);
  };

  // Save the Supplier Management tab inline without entering global edit
  // mode. Persists only the status / ratings / notes columns so other
  // unsaved edits elsewhere on the form state can't be accidentally
  // written. Set-up form sending and Portal Users have their own actions.
  const saveManagementSection = async () => {
    setSavingManagement(true);
    setError(null);
    const payload = {
      do_not_use_status: form.do_not_use_status,
      preferred_supplier: form.preferred_supplier,
      new_supplier_form_received: form.new_supplier_form_received,
      nda_signed: form.nda_signed,
      hs_questionnaire_returned: form.hs_questionnaire_returned,
      code_of_conduct_returned: form.code_of_conduct_returned,
      rating_general: form.rating_general || null,
      rating_price: form.rating_price || null,
      rating_quality: form.rating_quality || null,
      notes_general: form.notes_general,
      notes_previous_work: form.notes_previous_work,
      notes_relationship: form.notes_relationship,
    };
    const { error: updateError } = await supabase.from('suppliers').update(payload).eq('id', id);
    if (updateError) { setError(updateError.message); setSavingManagement(false); return; }
    setSavingManagement(false);
    setSuccess('Supplier management saved.');
    setTimeout(() => setSuccess(null), 2500);
  };

  const sendSetupForm = async () => {
    if (!form.main_contact_email) { setError('Main contact email is required to send the setup form.'); setTimeout(() => setError(null), 4000); return; }
    if (!window.confirm(`Send the supplier set-up form to ${form.main_contact_email}${form.secondary_contact_email ? ' and ' + form.secondary_contact_email : ''}? A unique link will be emailed to the supplier.`)) return;
    setSendingForm(true);
    setError(null);

    // This token is the ONLY thing protecting a form that collects bank
    // details, so it must be cryptographically random. The old fallback was
    // `${Date.now()}-${Math.random()}` — Math.random is not a CSPRNG and
    // Date.now is guessable, so on any browser without randomUUID (or any
    // non-secure context, where it is undefined) the product minted a
    // predictable link to a supplier's account number.
    //
    // getRandomValues has been available far longer than randomUUID and works
    // wherever crypto does, so there is no case left that needs a weak path.
    // If crypto is genuinely absent we refuse rather than issue a guessable
    // link: not sending the form is recoverable, leaking bank details is not.
    const token = newSecureToken(32);
    if (!token) {
      setError('This browser cannot generate a secure link. Please use an up-to-date browser over HTTPS.');
      setSendingForm(false);
      return;
    }
    const formUrl = `${window.location.origin}/supplier-form/${token}`;

    // Save token + sent metadata first so the link is valid the moment the email lands.
    const { error: updateError } = await supabase.from('suppliers').update({
      supplier_form_token: token,
      supplier_form_submitted: false,
      setup_form_sent_at: new Date().toISOString(),
      setup_form_sent_by: currentUser?.id || null,
    }).eq('id', id);
    if (updateError) { setError(updateError.message); setSendingForm(false); return; }

    // Email the supplier contacts only (one email per recipient — privacy rule).
    // Internal users are NOT notified on send — they get notified when the
    // supplier completes the form (handled in api/supplier-form-submit.js +
    // SupplierSetupForm.js). Two notifications was noisy; the completion event
    // is the one finance/requestor actually need to act on.
    const supplierRecipients = [
      { email: form.main_contact_email, name: form.main_contact_name },
      ...(form.secondary_contact_email ? [{ email: form.secondary_contact_email, name: form.secondary_contact_name }] : []),
    ];
    for (const r of supplierRecipients) {
      const { subject, html } = supplierSetupFormEmail({ contactName: r.name, supplierName: form.supplier_name, formUrl });
      try { await sendEmail({ to: r.email, subject, html }); } catch (err) {
        console.warn('supplierSetupFormEmail failed', { to: r.email, error: err?.message });
      }
    }

    await refreshSupplier();
    setSendingForm(false);
    setSuccess(`Setup form sent to ${form.main_contact_email}.`);
    setTimeout(() => setSuccess(null), 4000);
  };

  const createSupplierUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.password) {
      setError('Please fill in all fields'); return;
    }
    setSaving(true);
    setError(null);
    // TESTING: ALLOW_DUPLICATE_USER_EMAILS bypasses this pre-check. See
    // src/lib/testFlags.js — roll back before production. Auth still enforces
    // unique emails; a reused address falls into the re-link path below.
    if (!ALLOW_DUPLICATE_USER_EMAILS) {
      const { data: existingInternal } = await supabase.from('internal_users').select('id').eq('email', newUser.email).single();
      const { data: existingSupplier } = await supabase.from('supplier_users').select('id').eq('email', newUser.email).single();
      if (existingInternal || existingSupplier) {
        setError('A user with this email address already exists'); setSaving(false); return;
      }
    }
    let authUserId;
    let relinked = false;
    const created = await adminAuth.createUser({ email: newUser.email, password: newUser.password });
    if (!created.ok) {
      if (created.code !== 'email_exists') { setError(created.error || 'Could not create the account'); setSaving(false); return; }
      const existing = await findAuthUserByEmail(newUser.email);
      if (!existing) {
        setError("This email exists in Supabase Auth but I couldn't find the orphaned account to re-link. Clean it up manually in Supabase → Authentication → Users, then try again.");
        setSaving(false); return;
      }
      if (!window.confirm(`An auth account with email ${newUser.email} already exists but isn't linked to any Supply Pilot user. Re-link this account to ${newUser.full_name}? Their password will be reset to the temporary password you've entered.`)) {
        setSaving(false); return;
      }
      const updRes = await adminAuth.updateUser(existing.id, { password: newUser.password });
      if (!updRes.ok) { setError(`Found existing auth account but couldn't reset its password: ${updRes.error}`); setSaving(false); return; }
      authUserId = existing.id;
      relinked = true;
    } else {
      authUserId = created.id;
    }
    const { error: dbError } = await supabase.from('supplier_users').insert([{
      // Active on creation so the new portal user is immediately routable for
      // brief/award comms — don't depend on the DB default or a later step.
      id: authUserId, full_name: newUser.full_name, email: newUser.email, supplier_id: id, is_active: true,
    }]);
    if (dbError) { setError(dbError.message); setSaving(false); return; }

    // Send the welcome + password emails and CONFIRM the outcome — setup
    // shouldn't read as "done" while an email quietly failed. Both go out
    // together; we report per-email so a single failure is actionable (the
    // admin can pass the credentials on by hand). Capture the values first:
    // newUser is cleared below and the sends are awaited before that.
    const who = newUser.full_name;
    const toWhere = newUser.email;
    const outcomeMsg = relinked ? `Re-linked existing account for ${who}` : `User ${who} created`;
    if (!newUser.notify) {
      // "Send later" — account created without emailing. The admin sends the
      // welcome + password from the user's row actions when ready.
      setSuccess(`${outcomeMsg} — no emails sent yet. Send their welcome and password from the user's row when you're ready.`);
      setTimeout(() => setSuccess(null), 6000);
    } else {
      const [welcomeSent, passwordSent] = await Promise.all([
        sendWelcomeUserEmail(who, toWhere),
        sendPasswordUserEmail(who, toWhere, newUser.password),
      ]);
      if (welcomeSent && passwordSent) {
        setSuccess(`${outcomeMsg} — welcome and password emails sent to ${toWhere}.`);
        setTimeout(() => setSuccess(null), 6000);
      } else {
        // Not a hard error — the user WAS created — but it needs attention, so
        // it stays on screen (no auto-dismiss) rather than flashing green.
        const failed = [!welcomeSent && 'welcome', !passwordSent && 'password'].filter(Boolean);
        const list = failed.join(' and ');
        setError(`${outcomeMsg}, but the ${list} email${failed.length > 1 ? 's' : ''} could not be sent to ${toWhere}. Share their login details manually, or use the user's row actions to resend.`);
      }
    }
    setNewUser({ full_name: '', email: '', password: '', notify: true });
    setShowAddUser(false);
    await refreshSupplierUsers();
    setSaving(false);
  };

  const updateSupplierUser = async () => {
    if (!editingUser.full_name || !editingUser.email) {
      setError('Full name and email are required'); return;
    }
    setSaving(true);
    setError(null);
    const { error: dbError } = await supabase.from('supplier_users').update({
      full_name: editingUser.full_name,
      email: editingUser.email,
      is_active: editingUser.is_active,
    }).eq('id', editingUser.id);
    if (dbError) { setError(dbError.message); setSaving(false); return; }
    setSuccess('User updated successfully!');
    setTimeout(() => setSuccess(null), 3000);
    setEditingUser(null);
    await refreshSupplierUsers();
    setSaving(false);
  };

  const deleteSupplierUser = async (user) => {
    if (!window.confirm(`Delete ${user.full_name}? This permanently removes their portal account and cannot be undone. Consider disabling them instead if you might need to restore access later.`)) return;
    setSaving(true);
    setError(null);
    const { error: dbError } = await supabase.from('supplier_users').delete().eq('id', user.id);
    if (dbError) {
      const fkLike = dbError.code === '23503' || (dbError.message || '').toLowerCase().includes('foreign key');
      setError(fkLike
        ? `${user.full_name} can't be deleted because they're still referenced by other records. Disable them instead.`
        : dbError.message);
      setTimeout(() => setError(null), 6000);
      setSaving(false);
      return;
    }
    const authDel = await adminAuth.deleteUser(user.id);
    if (!authDel.ok) {
      setError(`User row removed, but couldn't delete the Supabase Auth account: ${authDel.error}. Clean it up manually in Supabase → Authentication → Users to avoid an orphaned login.`);
      setTimeout(() => setError(null), 8000);
    } else {
      setSuccess(`${user.full_name} deleted.`);
      setTimeout(() => setSuccess(null), 3000);
    }
    setEditingUser(null);
    await refreshSupplierUsers();
    setSaving(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <OttoLoading size="large" message="Loading supplier" />
    </div>
  );

  const userIsFinance = isFinance(currentUser);
  // The Financial Management tab carries supplier finance data and the
  // legally sensitive set-up form payload. Admins/super_users see it for
  // oversight but standard internal users do not.
  const canSeeFinanceTab = userIsFinance || isAdmin(currentUser);
  // Finance users can edit their section directly — they don't need to flip
  // the global "Edit Supplier" toggle (which is for the rest of the form).
  const financeEditable = userIsFinance;
  const isFabricator = form.category === 'Fabrication / Materials' || form.category === 'Fabricator';
  const isVenue = form.category === 'Venue';
  const inputClass = `w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors ${!editing ? 'opacity-60 cursor-not-allowed' : ''}`;
  const financeInputClass = `w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors ${!financeEditable ? 'opacity-60 cursor-not-allowed' : ''}`;
  const readOnlyClass = "w-full bg-white border border-line rounded-xl text-fusion-mid-grey px-4 py-2.5 font-['Metropolis'] text-sm";
  // Field styling aligned to the BriefDetail canonical pattern (2026-06
  // page-consistency pass). Brief's muted grey-green normal-case label
  // reads as quiet metadata; the value below carries the visual weight.
  // The previous Supplier-only uppercase-green-bold treatment competed
  // with section headers and overloaded the page with green type.
  //
  // labelClass / valueClass / sectionClass / subSectionClass now match
  // BriefDetail line-for-line so a future tweak in one place applies
  // to both pages via grep.
  //
  // groupCardClass flattened in 2026-06 (consistency follow-up): the
  // previous p-5 + border + dark bg + rounded chrome wrapped every
  // subsection on the Overview tab in a card, which read as much more
  // boxed than Brief's flat two-column flow under each section header.
  // The 10 callsites in this file still wrap their field groups in a
  // <div className={groupCardClass}> so the markup is stable, but the
  // class now carries only the inter-section bottom margin — no
  // visual chrome. If a future requirement reintroduces card chrome
  // for a specific layout (e.g. a true contact card with distinct
  // visual containment), define a new contactCardClass alongside.
  const labelClass = "font-['Metropolis'] text-xs font-semibold text-[#51534A] block mb-1";
  const sectionClass = "font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest mb-4 mt-8 pb-2 border-b border-line";
  const subSectionClass = "font-['Metropolis'] text-base font-semibold text-ink mb-3 mt-6";
  // §4 — ONE heading system. Section eyebrows (sectionClass) and card titles
  // were both bold dark text at nearly the same size, so nothing said which
  // contained which. Card titles are now a quieter, smaller caps label: the
  // eyebrow names the section, this names the card inside it.
  const cardHeadingClass = "font-['Metropolis'] text-[10px] font-bold tracking-[0.16em] uppercase text-[#51534A] mb-2.5";

  // §4a — sparse is the NORMAL case on this page, not the exception. Reading a
  // card, an empty field tells you nothing except that it's empty; ten of them
  // bury the two that are filled in. Read-only, this shows what's there and
  // counts the rest in one line. Editing shows every field, because that's the
  // moment you want the blanks.
  const GRID_COLS = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' };
  const FieldGrid = ({ fields, cols = 3, emptyText = 'Nothing recorded here yet.' }) => {
    const gridClass = `grid ${GRID_COLS[cols] || GRID_COLS[3]} gap-6`;
    if (editing) {
      return (
        <div className={gridClass}>
          {fields.map(([field, label]) => (
            <div key={field}>
              <label className={labelClass}>{label}</label>
              <input className={inputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
            </div>
          ))}
        </div>
      );
    }
    const filled = fields.filter(([field]) => String(form[field] ?? '').trim());
    const emptyCount = fields.length - filled.length;
    const addDetails = (
      <button type="button" onClick={() => setEditing(true)}
        className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.14em] text-[#2E8FA1] hover:text-active-green bg-transparent border-0 p-0 cursor-pointer">
        Add details
      </button>
    );
    if (filled.length === 0) {
      return (
        <p className="font-['Metropolis'] text-[13px] text-[#9BA08C] flex items-center gap-3 flex-wrap">
          {emptyText} {addDetails}
        </p>
      );
    }
    return (
      <>
        <div className={gridClass}>
          {filled.map(([field, label]) => (
            <div key={field}>
              <label className={labelClass}>{label}</label>
              <p className={valueClass}>{form[field]}</p>
            </div>
          ))}
        </div>
        {emptyCount > 0 && (
          <p className="font-['Metropolis'] text-[12px] text-[#9BA08C] mt-2.5 flex items-center gap-3 flex-wrap">
            {emptyCount} more {emptyCount === 1 ? 'field' : 'fields'} not set {addDetails}
          </p>
        )}
      </>
    );
  };
  const groupCardClass = "mb-4";
  const valueClass = "font-['Metropolis'] text-sm text-ink";
  const tabClass = (tab) => `font-['Metropolis'] text-sm font-semibold px-6 py-3 transition-colors ${activeTab === tab ? 'text-fusion-forest border-b-2 border-[#92D400]' : 'text-[#51534A] hover:text-fusion-mid-grey'}`;

  // Activity Log filter chips. Groups roll up the underlying event kinds
  // into themes that read better in the UI ("Briefs" covers invites, opens,
  // responses, declines). Multi-select — empty Set = show everything.
  const ACTIVITY_FILTER_GROUPS = [
    { key: 'note',       label: 'Notes',      kinds: ['note'] },
    { key: 'briefs',     label: 'Briefs',     kinds: ['brief', 'view', 'response', 'declined'] },
    { key: 'qa',         label: 'Q&A · requests', kinds: ['question', 'question_answered', 'update_request', 'extension_request'] },
    { key: 'email',      label: 'Emails',     kinds: ['email'] },
    { key: 'login',      label: 'Logins',     kinds: ['login'] },
    { key: 'compliance', label: 'Compliance', kinds: ['compliance', 'portal_upload'] },
    { key: 'system',     label: 'Setup',      kinds: ['system', 'portal_form_complete'] },
  ];
  const toggleActivityFilter = (groupKey) => {
    setActivityFilters(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) next.delete(groupKey); else next.add(groupKey);
      return next;
    });
  };

  // Overflow ⋮ items for the EntityHeaderActions cluster. Delete moved
  // out of the primary row in 2026-06 per the entity-header action-
  // cluster spec — destructive verbs always live in the overflow,
  // never one mis-click away in the main cluster. Admin-only.
  const supplierOverflowItems = isAdmin(currentUser) ? [
    {
      label: deleting ? 'Deleting…' : 'Delete supplier',
      danger: true,
      disabled: deleting,
      onClick: handleDeleteSupplier,
      icon: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
      ),
    },
  ] : [];

  return (
    <div className="h-screen bg-canvas flex overflow-hidden">
      {/* Action-confirmation toast — bound to useActionToast above. */}
      <ActionToast />

      {/* Sidebar — sticky to viewport so it stays visible when content
          scrolls. The outer is locked to h-screen and the main content
          column owns the scroll, not the page body. */}
      <Sidebar currentInternalUser={currentUser} />

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {/* Page header — breadcrumb-style back link, inline H1, status
            badges beneath, action buttons on the right, divider beneath.
            Matches the Dashboard pattern; the divider is inset (mx-8)
            so it doesn't run edge-to-edge across the page. */}
        <div className="pt-6 flex-shrink-0">
          {/* Band 1 — back arrow + breadcrumb subtitle (Category ·
              Region · Status) + action cluster. The action cluster
              anchors to the breadcrumb row on BOTH entity pages per
              the entity-header action-cluster spec — keeps the title
              row clean and gives suppliers/briefs/etc. one shared
              anchor row going forward. */}
          <div className="px-8 flex items-center gap-4 mb-3 min-w-0">
            <BackIconButton to="/suppliers" label="Back to suppliers" />
            {(() => {
              const bits = [form.category, form.region, form.status === 'active' ? 'Active' : form.status === 'inactive' ? 'Inactive' : form.status === 'onboarding' ? 'Onboarding' : null]
                .filter(b => b && String(b).trim());
              if (bits.length === 0) return null;
              return (
                <p
                  className={`${HEADER_EYEBROW_CLASS} flex-1 min-w-0 truncate`}
                  title={bits.join(' · ')}>
                  {bits.join(' · ')}
                </p>
              );
            })()}
            {/* Action cluster anchored to this row. Editing mode swaps
                the cluster for inline Cancel + Save Changes; otherwise
                the shared EntityHeaderActions renders Pin → Activate
                (when inactive) → Edit Supplier → overflow ⋮ (Delete). */}
            <div className="ml-auto flex items-center gap-2 flex-shrink-0">
              {editing ? (
                <>
                  {error && <p className="font-['Metropolis'] text-sm text-[#E0290A] mr-2">{error}</p>}
                  <button onClick={() => { setEditing(false); refreshSupplier(); }} className="bg-white rounded-r-btn border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors">Cancel</button>
                  {/* Names its scope (IA §5): this saves the supplier RECORD, and sat
                            near notes and portal users where "Save Changes" didn't say
                            which of them it meant. */}
                        <button onClick={handleSave} disabled={saving} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save supplier'}
                  </button>
                </>
              ) : (
                <EntityHeaderActions
                  pin={{ isPinned: userPinned, onToggle: handlePinClick, label: 'supplier', starRef: pinStarRef, pulsing: pinPulsing }}
                  error={error}
                  overflowItems={supplierOverflowItems}
                  overflowDisabled={deleting}
                >
                  {form.status === 'inactive' && (
                    <button onClick={() => { const initialRisk = form.risk_flag || null; setActivateRiskFlag(initialRisk); setActivatePublishDocs(initialRisk === 'low'); setShowActivateModal(true); }} className="bg-[#2E8FA1] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-[#246d7d] transition-colors">
                      Activate Supplier
                    </button>
                  )}
                  {/* Amend Supplier → STANDARD tier (teal outline) +
                      pencil icon (same SVG as Amend Brief). Renamed
                      from "Edit Supplier" in the 2026-06 amend-rename
                      pass so the two "amend this record" actions are
                      verbally + visually identical across entities.
                      Green-filled (PRIMARY tier) stays reserved for
                      outcome-completing actions — the supplier page
                      has no Complete-equivalent, so it carries no
                      green-filled button. */}
                  <button onClick={() => setEditing(true)} className={headerButtonClass('standard')}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={ICON_NOD_PENCIL}><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                    Amend Supplier
                  </button>
                </EntityHeaderActions>
              )}
            </div>
          </div>
          <div className="px-8 flex items-start justify-between gap-6 mb-3">
          {/* H1 + status/identity pills now sit on one inline row
              per the entity-header alignment brief §2 — matches the
              BriefDetail Band 2 pattern where state pills (Awarded /
              Amended / Type) sit inline-right of the brief title.
              Reads as "this thing, and its state" across both pages. */}
          <div className="min-w-0 flex items-center gap-3 flex-wrap">
            <h1 className={HEADER_TITLE_CLASS_SENTENCE}>{form.supplier_name}</h1>
            {/* Identity / status pills — all routed through the shared
                statusPillClass() helper so chrome (size, padding,
                rounding, type scale) is identical to the Brief header.
                Tone choices map the supplier's semantic state to the
                pill colour vocabulary defined alongside the helper. */}
            {/* ONE fixed order (§2), so the same supplier reads the same way
                every time and the eye learns where to look:
                  1. STOP pills   — the things that change whether you may use
                                    this supplier at all. First because they
                                    outrank everything else on the page.
                  2. Risk         — the routine status the brief asks to lead.
                  3. Compliance   — labelled, right after risk, because those
                                    two were the pair that read as a
                                    contradiction when one was a bare hexagon.
                  4. Lifecycle    — what stage this record is at.
                Previously these appeared in whatever order the JSX happened to
                be written in, which put "Supplier Record Only" ahead of "Do Not
                Use". */}

            {/* 1. Stop pills */}
            {form.do_not_use_status === 'Do Not Use' && (
              <span className={statusPillClass('danger')}>Do Not Use</span>
            )}
            {incidents.some(i => !i.closed_at) && (
              <span title="A safety incident is open — flagged for review" className={statusPillClass('danger')}>Open safety incident</span>
            )}
            {form.do_not_use_status === 'Use With Caution' && (
              <span title="Supplier flagged for use with caution" className={statusPillClass('warning')}>Use With Caution</span>
            )}
            {form.on_hold && (
              <span title={form.on_hold_reason ? `Reason: ${form.on_hold_reason}` : 'On hold'} className={statusPillClass('warning')}>On Hold</span>
            )}

            {/* 2. Risk — leads the routine status pair.
                Eco oak deliberately isn't here: the Sustainability Achievements
                banner directly below shows it in full, so a pill would only
                repeat it. Risk and compliance earn their place because their
                editable form lives in a distant tab. */}
            {form.risk_flag && riskCfg[form.risk_flag] && (() => {
              // Map risk levels to pill tones so the visual register
              // stays consistent with other header pills.
              const riskTone = form.risk_flag === 'high' ? 'danger' : form.risk_flag === 'medium' ? 'warning' : 'positive';
              return (
                <span
                  title={`Risk: ${riskCfg[form.risk_flag].label}`}
                  className={statusPillClass(riskTone)}>
                  <FlagIcon />
                  Risk: {riskCfg[form.risk_flag].label}
                </span>
              );
            })()}

            {/* 3. Compliance is LABELLED, not a bare hexagon — an unlabelled red
                stamp sitting next to "Risk: Low" read as a contradiction. The
                word comes from the same badge vocabulary (badgeLabelFor). */}
            {form.compliance_status && (() => {
              const cLabel = badgeLabelFor(form.compliance_status);
              const cTone = form.compliance_status === 'green' ? 'positive'
                : form.compliance_status === 'red' ? 'danger'
                : form.compliance_status === 'amber' ? 'warning' : 'neutral';
              return (
                <span className="inline-flex items-center gap-1.5">
                  <ComplianceBadge state={form.compliance_status} size="sm" ariaLabel={form.compliance_status_notes || undefined} />
                  <span title={form.compliance_status_notes || `Compliance: ${cLabel}`} className={statusPillClass(cTone)}>
                    Compliance: {cLabel}
                  </span>
                </span>
              );
            })()}

            {/* 4. Lifecycle — what stage this record is at. Last, because it's
                the least urgent of the four groups, and §4c: an active supplier
                whose accounts set-up is still pending should say so plainly
                here rather than leaving "Active" to imply it's all done. */}
            {form.is_record_only && (
              <span title="Reference record — no emails, no portal access" className={statusPillClass('info')}>Record only</span>
            )}
            {form.status === 'inactive' && (
              form.deactivated_at
                ? <span title={`Deactivated${form.deactivation_reason ? ` — ${form.deactivation_reason}` : ''} · ${fmtDate(form.deactivated_at)}`} className={statusPillClass('danger')}>Deactivated</span>
                : <span className={statusPillClass('neutral')}>Inactive</span>
            )}
            {/* Only while a decision is outstanding AND the supplier is active:
                the pending-finance state is irrelevant until someone activates
                them, and once finance_approved is set either way the decision
                lives in the Financial tab. */}
            {form.status === 'active' && form.finance_approved !== true && form.finance_approved !== false && (
              <span title="Fusion Accounts haven't finished setting this supplier up" className={statusPillClass('info')}>Pending Fusion Accounts set-up</span>
            )}
          </div>
          {/* Action cluster moved to Band 1 (breadcrumb row) per the
              entity-header action-cluster spec. Title row stays clean
              with just H1 + identity pills. */}
        </div>
        {/* Tags strip — sits between the title+pills row and the
            achievements strip. Tagging lives in the entity HEADER
            (not the Overview tab body) so it stays reachable from
            every supplier tab — Documents, Activity, Financial, etc.
            Mirrors the BriefDetail tags placement: chips lead with
            no section label (the "TAGS" eyebrow was dropped per the
            entity-header action-cluster brief §6 — chips lead on
            both pages, no eyebrow prefix). */}
        <div className="mx-8 mb-1 flex items-center gap-2 flex-wrap">
          <RecordTags
            vocabulary="supplier"
            entityId={id}
            actorId={currentUser?.id}
            label=""
            onApplied={(tag) => notifyToast({ eyebrow: 'Tag added', title: `#${tag.name} applied to this supplier` })}
            onRemoved={(tag) => notifyToast({ eyebrow: 'Tag removed', title: `#${tag.name} removed from this supplier` })}
          />
        </div>
        {/* Achievements strip — beneath the page header, above the divider.
            Renders only when the supplier has at least one badge (current
            OR expired). Mirrors the WinnerBanner brand pattern (deep-green
            gradient + 3px left stripe). Each badge in the row shows its
            tier + category label; expired badges render greyscale via the
            Badge component's expired prop. */}
        {supplierBadges.length > 0 && (() => {
          const now = Date.now();
          const sortedBadges = [...supplierBadges].sort((a, b) => {
            const aExp = a.expires_at && new Date(a.expires_at).getTime() < now;
            const bExp = b.expires_at && new Date(b.expires_at).getTime() < now;
            if (aExp !== bExp) return aExp ? 1 : -1;
            return new Date(b.awarded_at).getTime() - new Date(a.awarded_at).getTime();
          });
          const currentBadges = sortedBadges.filter(b => !b.expires_at || new Date(b.expires_at).getTime() >= now);
          const expiredBadges = sortedBadges.filter(b => b.expires_at && new Date(b.expires_at).getTime() < now);
          const highestCurrent = currentBadges[0];
          const highestCat = highestCurrent ? badgeCategoriesMap.get(highestCurrent.category_id) : null;
          const tierName = (cat, tier) => {
            if (!cat) return null;
            if (cat.slug === 'eco') return ecoStageLabel(tier); // oak stage, not a metal tier
            if (tier === 'bronze') return cat.bronze_name;
            if (tier === 'silver') return cat.silver_name;
            if (tier === 'gold')   return cat.gold_name;
            return null;
          };
          const nextRenewal = currentBadges
            .map(b => b.expires_at ? new Date(b.expires_at).getTime() : Infinity)
            .filter(t => Number.isFinite(t))
            .sort((a, b) => a - b)[0];
          return (
            <div className="mx-8 mt-6 mb-4 relative overflow-hidden rounded-xl bg-gradient-to-br from-[#009159]/[0.10] to-[#92D400]/[0.04] border border-[#009159]/30 pl-6 pr-5 py-4">
              <span aria-hidden="true" className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#009159]" />
              <div className="flex items-center gap-6 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.25em] uppercase text-[#009159]">Sustainability Achievements</div>
                  <div className="font-['Metropolis'] text-base font-bold text-ink mt-0.5">
                    {highestCurrent && highestCat
                      ? (highestCat.slug === 'eco'
                          ? `Eco — ${tierName(highestCat, highestCurrent.tier)}`
                          : `${tierName(highestCat, highestCurrent.tier)} supplier`)
                      : 'No active achievements'}
                  </div>
                  <div className="font-['Metropolis'] text-xs text-fusion-mid-grey mt-0.5">
                    {currentBadges.length} badge{currentBadges.length === 1 ? '' : 's'} current
                    {expiredBadges.length > 0 && <> · {expiredBadges.length} expired</>}
                    {nextRenewal && <> · next renewal {fmtDate(nextRenewal)}</>}
                  </div>
                </div>
                <div className="flex items-center gap-5 flex-shrink-0">
                  {sortedBadges.map(b => {
                    const cat = badgeCategoriesMap.get(b.category_id);
                    const expired = b.expires_at && new Date(b.expires_at).getTime() < now;
                    return (
                      <div key={b.id} className="flex flex-col items-center" title={cat ? `${cat.name} — awarded ${fmtDate(b.awarded_at)}${b.expires_at ? ` · expires ${fmtDate(b.expires_at)}` : ''}` : undefined}>
                        <Badge tier={b.tier} size="md" expired={expired} categorySlug={cat?.slug} />
                        <div className={`font-['Metropolis'] text-[9px] font-bold tracking-[0.15em] uppercase mt-1 ${expired ? 'text-[#7A4E10]' : 'text-[#51534A]'}`}>
                          {cat ? cat.name : ''} · {expired ? 'Expired' : (cat?.slug === 'eco' ? ecoStageLabel(b.tier) : b.tier.charAt(0).toUpperCase() + b.tier.slice(1))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}
        <div className="mx-8 h-px bg-[#E6E9DA]" aria-hidden="true" />
        </div>

        {/* Tab bar — inset to mx-8 so the active underline lines up
            with the page divider above.
            -----------------------------------------------------------
            Navigation pattern note (per entity-header alignment brief):
            Supplier detail uses TABS; Brief detail uses single-scroll.
            This split is DELIBERATE: a supplier is a multi-facet
            long-lived entity (Overview / Management / Financial /
            Documents / Brief Activity / Activity Log) and benefits
            from tabbed navigation to find a specific facet without
            scrolling past unrelated content. A brief is more linear
            (facts → preview → recipients → activity) so single-scroll
            reads as the natural flow. Treat the divergence as
            intentional; don't "align" the brief to tabs or the
            supplier to single-scroll without a deliberate decision.
            -----------------------------------------------------------
            Spacing rhythm (per header sizing/spacing brief §4): mt-8
            here gives a 32px gap between the header block (divider)
            and the tab bar — matches the implicit 32px gap Brief gets
            from its content wrapper's p-8 top padding, so both pages
            share the same "header → next element" rhythm. Tab bar →
            content stays at 32px via the existing p-8 on the form
            wrapper below. */}
        {/* §1: every label used to start with "Supplier" — on a supplier's own
            page that word carries no information and pushed the useful part of
            each label rightwards. "Brief Activity" also read as a sibling of
            "Activity Log"; it's the briefs they've been sent, so it's "Briefs".
            §7a: scroll-padding on the container stops the sticky bar covering
            whatever an in-page link jumps to. */}
        <div className="mx-8 mt-8 border-b border-line flex flex-shrink-0">
            <button className={tabClass('overview')} onClick={() => setActiveTab('overview')}>Overview</button>
            <button className={tabClass('management')} onClick={() => setActiveTab('management')}>Management</button>
            {canSeeFinanceTab && (
              <button className={tabClass('finance')} onClick={() => setActiveTab('finance')}
                title="Financial details — visible to Finance, Admin and Super User accounts only">
                <span className="inline-flex items-center gap-2">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="4" y="11" width="16" height="10" rx="1.5"/>
                    <path d="M8 11V7a4 4 0 0 1 8 0v4"/>
                  </svg>
                  Financial
                </span>
              </button>
            )}
            <button className={tabClass('compliance')} onClick={() => setActiveTab('compliance')}>Documents &amp; compliance</button>
            <button className={tabClass('briefs')} onClick={() => setActiveTab('briefs')}>Briefs</button>
            <button className={tabClass('activity')} onClick={() => setActiveTab('activity')}>Activity log</button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="max-w-6xl">

            {success && (
              <div className="mb-4 px-4 py-3 border border-[#92D400]/30 bg-[#92D400]/10">
                <p className="font-['Metropolis'] text-sm text-fusion-forest">{success}</p>
              </div>
            )}

            {/* §3: Overview gets a right rail — the at-a-glance facts that were
                scattered across tabs (how they're rated, what they've earned,
                who can log in, how much work we've sent). The rail SUMMARISES;
                editing stays where it lives, because moving the editable
                machinery out of Management would mean two places to change the
                same field. It stacks under the content below 1100px.
                §4b: on a sparse record the rail leads with what's blocking use,
                not with empty stars. */}
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_300px] gap-8 items-start">
              <div className="min-w-0">
                {/* Performance (post-event feedback) + any open safety incident.
                    Aggregates live on Overview (brief §11); an open incident is
                    prominent, not buried. All derived from one helper. */}
                {(() => {
                  const perf = deriveSupplierPerformance(feedbackRows, incidents);
                  const openInc = incidents.filter(i => !i.closed_at);
                  const OUTCOME = { well: 'went well', mostly: 'mostly went well', issues: 'issues raised', reportable: 'reportable incident' };
                  return (
                    <>
                      {openInc.length > 0 && (
                        <>
                          <p className={sectionClass}>Safety incidents</p>
                          {openInc.map(inc => (
                            <div key={inc.id} className="mb-3 rounded-xl border border-[#E0290A]/30 bg-[#FFFBFA] border-l-4 border-l-[#E0290A] px-4 py-3">
                              <div className="flex items-center gap-2.5 flex-wrap mb-1">
                                <span className="font-['Metropolis'] text-[14px] font-bold text-ink">{inc.severity === 'reportable' ? 'Reportable' : 'Minor'}</span>
                                <span className="text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-0.5 bg-[#E0290A]/10 text-[#E0290A]">Open</span>
                                <span className="ml-auto font-['Metropolis'] text-[11px] text-[#51534A]">{fmtDate(inc.raised_at)}</span>
                              </div>
                              {inc.note && <p className="font-['Metropolis'] text-[13px] text-ink-secondary leading-snug whitespace-pre-wrap">{inc.note}</p>}
                              <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                                <span className="font-['Metropolis'] text-[11px] text-[#51534A] mr-auto">Flagged for review while this is open.</span>
                                <button type="button" onClick={() => closeIncident(inc)} className="font-['Metropolis'] text-[11px] font-semibold rounded-full px-3 py-1.5 border border-line text-fusion-mid-grey hover:border-[#C3C9AF] bg-white">Close incident</button>
                              </div>
                            </div>
                          ))}
                        </>
                      )}

                      <p className={sectionClass}>Performance</p>
                      <div className={groupCardClass}>
                        {perf.totalCompleted === 0 ? (
                          <p className="font-['Metropolis'] text-sm text-[#51534A] italic">Not yet rated — no post-event feedback recorded.</p>
                        ) : (
                          <>
                            {/* Below 3 jobs these are the ANSWERS, not averages
                                — the caption said so all along while the
                                columns printed em-dashes and threw the answers
                                away. Each figure carries an info icon saying
                                how it's worked out, so a number can be
                                questioned instead of just believed. */}
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
                              <div>
                                <label className={labelClass}>
                                  Quality
                                  <InfoIcon heading="How quality is worked out" ariaLabel="How is quality worked out?">{PERFORMANCE_EXPLAINERS.quality}</InfoIcon>
                                </label>
                                <p className={valueClass}>
                                  {perf.rated && perf.quality ? `${perf.quality.stars}/5` : (rawAnswerSummary(perf, 'delivered') || NOT_SET)}
                                </p>
                              </div>
                              <div>
                                <label className={labelClass}>
                                  Would use again
                                  <InfoIcon heading="How this is worked out" ariaLabel="How is would-use-again worked out?">{PERFORMANCE_EXPLAINERS.useAgain}</InfoIcon>
                                </label>
                                <p className={valueClass}>
                                  {perf.rated && perf.useAgain ? `${perf.useAgain.pct}% · ${perf.useAgain.yes} of ${perf.useAgain.of}` : (rawAnswerSummary(perf, 'use_again') || NOT_SET)}
                                </p>
                              </div>
                              <div>
                                <label className={labelClass}>
                                  Delivered on time
                                  <InfoIcon heading="How this is worked out" ariaLabel="How is delivered-on-time worked out?">{PERFORMANCE_EXPLAINERS.onTime}</InfoIcon>
                                </label>
                                <p className={valueClass}>
                                  {perf.rated && perf.onTime ? `${perf.onTime.yes} of ${perf.onTime.of}` : (rawAnswerSummary(perf, 'on_time') || NOT_SET)}
                                </p>
                              </div>
                              <div>
                                <label className={labelClass}>
                                  Last job
                                  <InfoIcon heading="How this is worked out" ariaLabel="How is the last job outcome worked out?">{PERFORMANCE_EXPLAINERS.lastJob}</InfoIcon>
                                </label>
                                <p className={valueClass}>{perf.lastJob ? `${fmtDate(perf.lastJob.at)} · ${OUTCOME[perf.lastJob.outcome] || ''}` : NOT_SET}</p>
                              </div>
                            </div>
                            <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-3 inline-flex items-center">
                              {performanceBasis(perf)}{!perf.rated ? ' — individual answers shown until 3 jobs' : ''}.
                              <InfoIcon heading="Which jobs count" ariaLabel="Which jobs are counted?">{PERFORMANCE_EXPLAINERS.window}</InfoIcon>
                            </p>
                          </>
                        )}
                      </div>
                    </>
                  );
                })()}


                <p className={sectionClass}>Supplier Identity & Contact Details</p>
                <div className={groupCardClass}>
                  <div>
                    <label className={labelClass}>Supplier Name</label>
                    {editing
                      ? <input className={inputClass} value={form.supplier_name || ''} onChange={e => set('supplier_name', e.target.value)} />
                      : <p className={valueClass}>{form.supplier_name || NOT_SET}</p>}
                  </div>
                </div>
                {/* §4a: an address is ONE thing, not five. Read-only it prints
                    as an address; only the edit form needs the separate lines.
                    Five rows each saying "Not set" was the single loudest
                    source of emptiness on a sparse record — 319 suppliers are
                    still pending, so sparse is the normal case, not the
                    exception. */}
                <div className={groupCardClass}>
                  <p className={cardHeadingClass}>Supplier Address</p>
                  {editing ? (
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        ['address_line_1', 'Address Line 1'],
                        ['address_line_2', 'Address Line 2'],
                        ['address_line_3', 'Address Line 3'],
                        ['town', 'Town'],
                        ['postcode', 'Postcode'],
                      ].map(([field, label]) => (
                        <div key={field}>
                          <label className={labelClass}>{label}</label>
                          <input className={inputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                        </div>
                      ))}
                    </div>
                  ) : (() => {
                    const lines = ['address_line_1', 'address_line_2', 'address_line_3', 'town', 'postcode']
                      .map(f => (form[f] || '').trim())
                      .filter(Boolean);
                    if (lines.length === 0) {
                      return <p className="font-['Metropolis'] text-sm text-[#9BA08C]">No address on file.</p>;
                    }
                    return (
                      <p className={`${valueClass} whitespace-pre-line`}>{lines.join('\n')}</p>
                    );
                  })()}
                </div>
                {/* Payment Terms mirrored from the Financial Management tab
                    so staff can see it at a glance on the Overview without
                    switching tabs. Same `payment_terms` field — edits in
                    either place write to the same column. */}
                <div className={groupCardClass}>
                  <p className={cardHeadingClass}>Supplier Payment Terms</p>
                  <div>
                    <label className={labelClass}>Payment Terms</label>
                    {editing
                      ? <input className={inputClass} value={form.payment_terms || ''} onChange={e => set('payment_terms', e.target.value)}
                          placeholder="e.g. 30 — or write it out, like 30 days from invoice" />
                      : <p className={valueClass}>{paymentTermsLabel(form.payment_terms)}</p>}
                  </div>
                </div>
                <div className={groupCardClass}>
                  <p className={cardHeadingClass}>Main Contact</p>
                  <FieldGrid
                    fields={[['main_contact_name', 'Name'], ['main_contact_email', 'Email'], ['main_contact_phone', 'Phone']]}
                    emptyText="No main contact on file." />
                </div>
                <div className={groupCardClass}>
                  <p className={cardHeadingClass}>Secondary Contact</p>
                  <FieldGrid
                    fields={[['secondary_contact_name', 'Name'], ['secondary_contact_email', 'Email'], ['secondary_contact_phone', 'Phone']]}
                    emptyText="No secondary contact on file." />
                </div>
                <div className={groupCardClass}>
                  <p className={cardHeadingClass}>Other Details</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <label className={labelClass}>Phone</label>
                      {editing
                        ? <input className={inputClass} value={form.phone || ''} onChange={e => set('phone', e.target.value)} />
                        : <p className={valueClass}>{form.phone || NOT_SET}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Website</label>
                      {editing
                        ? <input className={inputClass} value={form.website || ''} onChange={e => set('website', e.target.value)} />
                        : <p className={valueClass}>{form.website || NOT_SET}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Region</label>
                      {editing
                        ? <select className={inputClass} value={form.region || ''} onChange={e => set('region', e.target.value)}>
                            <option value="">Select region...</option>
                            {regions.map(r => <option key={r.value} value={r.value}>{r.value}</option>)}
                          </select>
                        : <p className={valueClass}>{form.region || NOT_SET}</p>}
                    </div>
                  </div>
                </div>

                {/* Category-specific classification — only shown when the
                    supplier's category warrants it. Sits at the bottom of the
                    Overview so the contact details aren't pushed off-screen. */}
                {isFabricator && (
                  <>
                    <p className={sectionClass}>Fabricator Capabilities</p>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        ['fab_carpentry', 'Carpentry'], ['fab_metalwork', 'Metal Work'],
                        ['fab_solid_surface', 'Solid Surface'], ['fab_print_graphics', 'Print / Graphics'],
                        ['fab_cnc_cutting', 'CNC Cutting'], ['fab_wet_spraying', 'Wet Spraying'],
                        ['fab_plastics', 'Plastics'], ['fab_3d_printing', '3D Printing'],
                        ['fab_development_capability', 'Development Capability'], ['fab_dmx', 'DMX'],
                        ['fab_electrical_engineering', 'Electrical Engineering'], ['fab_meu_coachbuilding', 'MEU / Coachbuilding'],
                      ].map(([field, label]) => (
                        <label key={field} className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={form[field] || false}
                            onChange={e => editing && set(field, e.target.checked)}
                            className="w-4 h-4 accent-active-green" />
                          <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">{label}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}

                {isVenue && (
                  <>
                    <p className={sectionClass}>Venue Information</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {[['venue_high_footfall', 'High Footfall'], ['venue_test_drive_events', 'Test Drive Events'], ['venue_press_launch', 'Press Launch']].map(([field, label]) => (
                        <label key={field} className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={form[field] || false}
                            onChange={e => editing && set(field, e.target.checked)}
                            className="w-4 h-4 accent-active-green" />
                          <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">{label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      {[
                        ['venue_type', 'Venue Type'], ['venue_number_of_bedrooms', 'Number of Bedrooms'],
                        ['venue_inc_ex_vat', 'Inc or Ex VAT'], ['venue_commission_percentage', 'Commission Percentage'],
                        ['venue_ddr_rate', 'DDR Rate'], ['venue_24hr_rate', '24 Hour Rate'],
                        ['venue_car_parking', 'Onsite Car Parking'], ['venue_car_parking_cost', 'Car Parking Cost'],
                        ['venue_space_hire_rate', 'Space Hire Rate'], ['venue_name_of_space', 'Name of Space'],
                      ].map(([field, label]) => (
                        <div key={field}>
                          <label className={labelClass}>{label}</label>
                          {editing
                            ? <input className={inputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                            : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                        </div>
                      ))}
                      {[
                        ['venue_meeting_room_sizes', 'Meeting Room Sizes'],
                        ['venue_test_drive_environment', 'Test Drive Environment'],
                        ['venue_preferred_suppliers', 'Venue Preferred Suppliers'],
                        ['venue_access_information', 'Access Information'],
                        ['venue_onsite_rules', 'Onsite Rules and Regs'],
                        ['venue_automotive_permitted', 'Automotive Events Permitted'],
                        ['venue_power', 'Power'], ['venue_external_water_power', 'External Water and Power'],
                        ['venue_ev_chargers', 'EV Car Chargers'],
                        ['venue_vehicles_displayed_inside', 'Vehicles Displayed Inside'],
                        ['venue_commissionable_items', 'Commissionable Items'],
                        ['venue_ddr_inclusions', 'DDR Inclusions'],
                        ['venue_24hr_inclusions', '24 Hour Rate Inclusions'],
                        ['venue_additional_catering', 'Additional Catering'],
                        ['venue_av_inclusions', 'AV Inclusions'],
                        ['venue_cost_additional_av', 'Cost of Additional AV'],
                        ['venue_valeting_area', 'Valeting Area'],
                        ['venue_search_criteria', 'Search Criteria'],
                      ].map(([field, label]) => (
                        <div key={field} className="col-span-2">
                          <label className={labelClass}>{label}</label>
                          {editing
                            ? <AutoSizeTextarea minRows={3} className={inputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                            : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Initial Requestor Details sits LAST (IA §7). It is archival
                    — who asked for this supplier, once, at the beginning — and
                    it was rendering above the supplier's own name and contact
                    details. Order is now identity/contact → commercial →
                    performance → how-they-joined.

                    Tags moved to the header chip strip above (2026-06 tagging
                    -consistency brief) so tagging is reachable from every tab. */}
                <div className={groupCardClass}>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Name</label>
                      {editing
                        ? <input className={inputClass} value={form.requestor_name || ''} onChange={e => set('requestor_name', e.target.value)} />
                        : <p className={valueClass}>{form.requestor_name || NOT_SET}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Job Title</label>
                      {editing
                        ? <input className={inputClass} value={form.requestor_job_title || ''} onChange={e => set('requestor_job_title', e.target.value)} />
                        : <p className={valueClass}>{form.requestor_job_title || NOT_SET}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Department / Project Team</label>
                      {editing
                        ? <input className={inputClass} value={form.requestor_department || ''} onChange={e => set('requestor_department', e.target.value)} />
                        : <p className={valueClass}>{form.requestor_department || NOT_SET}</p>}
                    </div>
                    <div>
                      <label className={labelClass}>Date of Request</label>
                      {editing
                        ? <DatePicker
                            selected={toDateObj(form.date_of_request)}
                            onChange={(d) => set('date_of_request', fromDateOnly(d))}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="dd/mm/yyyy"
                            isClearable
                            className={inputClass}
                            wrapperClassName="block w-full" />
                        : <p className={valueClass}>{fmtDate(form.date_of_request)}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* ---- Right rail (§3) ---- */}
              <aside className="min-w-0 flex flex-col gap-4 min-[1100px]:sticky min-[1100px]:top-2">
                {(() => {
                  const activeUsers = supplierUsers.filter(u => u.is_active !== false).length;
                  const ratings = [
                    ['rating_quality', 'Quality'],
                    ['rating_general', 'General'],
                    ['rating_price', 'Price'],
                  ];
                  const anyRating = ratings.some(([f]) => form[f]);
                  // §4b — what's stopping this supplier being used comes FIRST,
                  // above ratings and badges nobody has earned yet. On a record
                  // that's still being set up, that's the only thing worth
                  // saying.
                  const blockers = [];
                  if (form.status === 'inactive') blockers.push('This supplier is inactive — activate them before sending briefs.');
                  else if (form.is_record_only) blockers.push('Reference record only — no portal access and no emails.');
                  else if (activeUsers === 0) blockers.push('No portal account yet, so they can\'t receive briefs or upload documents.');
                  if (form.compliance_status === 'red') blockers.push('Compliance is red — new work can\'t be awarded until it\'s resolved.');

                  const railCard = 'bg-white border border-[#E6EADB] rounded-xl p-4';
                  const railLabel = "font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.16em] text-[#51534A] mb-2";

                  return (
                    <>
                      {blockers.length > 0 && (
                        <div className="rounded-xl border-[1.5px] border-[#E89A2E]/45 bg-[#E89A2E]/[0.08] p-4">
                          <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.16em] text-[#7A4E10] mb-1.5">Needs setting up</div>
                          <ul className="flex flex-col gap-1.5">
                            {blockers.map((b, i) => (
                              <li key={i} className="font-['Metropolis'] text-[12px] text-ink leading-snug">{b}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className={railCard}>
                        <div className={railLabel}>Ratings</div>
                        {anyRating ? (
                          <div className="flex flex-col gap-1.5">
                            {ratings.map(([field, label]) => {
                              const v = form[field] || 0;
                              return (
                                <div key={field} className="flex items-center justify-between gap-2">
                                  <span className="font-['Metropolis'] text-[12px] text-[#51534A]">{label}</span>
                                  {v ? (
                                    <span aria-label={`${v} out of 5`} className="text-[13px] tracking-[1px]">
                                      <span style={{ color: '#92D400' }}>{'★'.repeat(v)}</span>
                                      <span style={{ color: '#DADCCD' }}>{'★'.repeat(5 - v)}</span>
                                    </span>
                                  ) : (
                                    <span className="font-['Metropolis'] text-[11px] italic text-[#9BA08C]">Not rated</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          // Never render empty stars as a score — an unrated
                          // supplier must not read as a bad one.
                          <p className="font-['Metropolis'] text-[12px] text-[#9BA08C] italic">Not yet rated. Ratings are set on Management.</p>
                        )}
                      </div>

                      <div className={railCard}>
                        <div className={railLabel}>Recognition</div>
                        {supplierBadges.length === 0 ? (
                          <p className="font-['Metropolis'] text-[12px] text-[#9BA08C] italic">None earned yet.</p>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            {supplierBadges.map(bdg => {
                              const cat = badgeCategoriesMap.get(bdg.category_id);
                              const expired = bdg.expires_at && new Date(bdg.expires_at) < new Date();
                              return (
                                <div key={bdg.id} className="font-['Metropolis'] text-[12px] text-ink flex items-center justify-between gap-2">
                                  <span>{cat?.name || 'Recognition'}</span>
                                  <span className={expired ? 'text-[#9BA08C]' : 'text-fusion-forest font-semibold'}>
                                    {expired ? 'Expired' : (bdg.tier || '').replace(/^./, c => c.toUpperCase())}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      <div className={railCard}>
                        <div className={railLabel}>Portal access</div>
                        <p className="font-['Metropolis'] text-[12px] text-ink">
                          {supplierUsers.length === 0
                            ? 'No portal users'
                            : `${activeUsers} active of ${supplierUsers.length} ${supplierUsers.length === 1 ? 'user' : 'users'}`}
                        </p>
                        <button type="button" onClick={() => setActiveTab('management')}
                          className="mt-2 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.14em] text-[#2E8FA1] hover:text-active-green bg-transparent border-0 p-0 cursor-pointer">
                          Manage users →
                        </button>
                      </div>

                      <div className={railCard}>
                        <div className={railLabel}>Record</div>
                        <div className="flex flex-col gap-1">
                          <div className="font-['Metropolis'] text-[12px] text-[#51534A] flex items-center justify-between gap-2">
                            <span>Added</span><span className="text-ink">{form.created_at ? fmtDate(form.created_at) : NOT_SET}</span>
                          </div>
                          <div className="font-['Metropolis'] text-[12px] text-[#51534A] flex items-center justify-between gap-2">
                            <span>Documents on file</span><span className="text-ink">{onboardingDocs.length}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </aside>
              </div>
            )}

            {activeTab === 'management' && (
              <>
                {/* Status / Ratings / Notes are always-editable on this tab —
                    the user works directly in the controls and clicks Save
                    Changes at the bottom. The global Edit Supplier mode is
                    not required, similar to the Finance Use Only block. */}
                <p className={sectionClass}>Status</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-4">
                  {[
                    ['do_not_use_status', 'Do Not Use Status', ['Do Not Use', 'Use With Caution', 'Cleared for Use']],
                    ['preferred_supplier', 'Preferred Supplier Status', ['Preferred Supplier', 'Approved Supplier', 'Under Review', 'Not Preferred']],
                    ['new_supplier_form_received', 'New Supplier Form Received', ['Yes', 'No', 'Sent to Venue/Supplier to complete']],
                    // NDA Signed / H&S Questionnaire / Code of Conduct used to
                    // be typed in here too (§5). Compliance tracks those
                    // documents now, and two places to record the same fact is
                    // how they end up disagreeing — the panel below derives
                    // them instead. The columns are kept; they're just not
                    // edited here any more.
                  ].map(([field, label, options]) => (
                    <div key={field}>
                      <label className={labelClass}>{label}</label>
                      <select className={inputClass.replace('opacity-60 cursor-not-allowed', '')} value={form[field] || ''} onChange={e => set(field, e.target.value)}>
                        <option value="">Select...</option>
                        {options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                {/* The three retired dropdowns (§5). Compliance owns these
                    documents now, so the page READS from it instead of asking
                    someone to keep a second copy in step. Anything previously
                    typed in is still shown, marked as the old record — a
                    "Not Required" here was somebody's decision about this
                    supplier, and compliance has no way to say that yet. */}
                <div className="mt-6 rounded-xl border border-line bg-[#F7F9F0] p-4">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.16em] text-[#51534A] mb-1">
                        Tracked in compliance
                      </div>
                      <p className="font-['Metropolis'] text-[12px] text-[#51534A] leading-snug max-w-xl">
                        NDA, H&amp;S questionnaire and code of conduct are tracked as compliance documents —
                        their real status lives on Documents &amp; compliance, so they aren't set by hand here any more.
                      </p>
                    </div>
                    <button type="button" onClick={() => setActiveTab('compliance')}
                      className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.14em] text-[#2E8FA1] hover:text-active-green bg-transparent border-0 cursor-pointer">
                      Documents &amp; compliance →
                    </button>
                  </div>
                  {[
                    ['nda_signed', 'NDA signed and returned'],
                    ['hs_questionnaire_returned', 'H&S questionnaire returned'],
                    ['code_of_conduct_returned', 'Code of conduct returned'],
                  ].some(([field]) => form[field]) && (
                    <div className="mt-3 pt-3 border-t border-[#E6EADB]">
                      <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] text-[#9BA08C] mb-1.5">
                        Previously recorded by hand
                      </div>
                      <div className="flex flex-col gap-1">
                        {[
                          ['nda_signed', 'NDA signed and returned'],
                          ['hs_questionnaire_returned', 'H&S questionnaire returned'],
                          ['code_of_conduct_returned', 'Code of conduct returned'],
                        ].filter(([field]) => form[field]).map(([field, label]) => (
                          <div key={field} className="font-['Metropolis'] text-[12px] text-[#51534A]">
                            {label}: <span className="text-ink font-semibold">{form[field]}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Record-only toggle — flips the supplier into / out of
                    reference-only mode. When on, every supplier-bound
                    email gates off and the Supplier Users / brief send
                    paths skip them. Sits in the Management tab Status
                    section so staff land on it alongside the other
                    lifecycle flags. */}
                <label className="flex items-start gap-3 mt-4 p-4 bg-white border border-[#DADCCD] rounded-xl cursor-pointer">
                  <input
                    type="checkbox"
                    checked={Boolean(form.is_record_only)}
                    onChange={e => set('is_record_only', e.target.checked)}
                    className="mt-0.5 accent-active-green" />
                  <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                    Supplier Record Only
                    <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                      When on, this supplier is a reference record only. No emails fire to them (brief invites, reminders, compliance notifications all skip), no portal access is granted, and they're excluded from compliance alert recipient pools.
                    </span>
                  </span>
                </label>

                {/* Deactivation — admin lifecycle control. Reuses
                    status='inactive' (already blocks new briefs) and also
                    revokes portal access + suppresses emails. Reason
                    required; reversible. */}
                {isAdmin(currentUser) && (
                  <div className="mt-4 p-4 bg-white border border-[#DADCCD] rounded-xl">
                    {form.status === 'inactive' ? (
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-['Metropolis'] text-sm font-semibold text-[#7A4E10]">Supplier deactivated</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 leading-relaxed">
                            No new briefs, portal access revoked, emails suppressed.
                            {form.deactivation_reason && <> · Reason: <span className="text-fusion-mid-grey">{form.deactivation_reason}</span></>}
                            {form.deactivated_at && <> · {fmtDate(form.deactivated_at)}</>}
                          </div>
                        </div>
                        {confirmingReactivate ? (
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button type="button" onClick={handleReactivate} disabled={reactivating}
                              className="font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase text-[#131313] bg-[#92D400] hover:bg-[#a4e810] rounded-r-btn px-3 py-1.5 transition-colors disabled:opacity-50">
                              {reactivating ? 'Reactivating…' : 'Confirm reactivate'}
                            </button>
                            <button type="button" onClick={() => setConfirmingReactivate(false)} disabled={reactivating}
                              className="font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase text-[#51534A] hover:text-fusion-mid-grey bg-transparent border border-line rounded-r-btn px-3 py-1.5 transition-colors">
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button type="button" onClick={() => setConfirmingReactivate(true)}
                            className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase text-fusion-forest border border-[#92D400]/40 hover:bg-[#92D400]/10 rounded-r-btn px-3 py-1.5 transition-colors">
                            Reactivate supplier
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <div className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">Deactivate supplier</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5 leading-relaxed">Takes them out of play — no new briefs, portal logins disabled, all emails suppressed. Reversible.</div>
                        </div>
                        <button type="button" onClick={() => { setDeactivateReason(''); setShowDeactivateModal(true); }}
                          className="flex-shrink-0 font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase text-[#E0290A] border border-[#E0290A]/40 hover:bg-[#E0290A]/10 rounded-r-btn px-3 py-1.5 transition-colors">
                          Deactivate
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Onboarding tour control (admin). Instant-persist toggle +
                    replay reset + status line; drives the supplier-portal
                    first-login tour / quiet launcher. Headed as its own zone so
                    it stops floating unlabelled between Status and Ratings. */}
                <p className={`${sectionClass} mt-8`}>Portal onboarding</p>
                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4 -mt-2">Controls the guided tour a supplier sees on their first portal login.</p>
                {(() => {
                  const tipsOn = form.onboarding_tips_enabled ?? true;
                  const seen = form.onboarding_tour_seen ?? false;
                  const completedAt = form.onboarding_tour_completed_at;
                  const stateWord = tipsOn ? (seen ? 'tips on' : 'will run on next login') : 'tips off';
                  const statusLine = completedAt
                    ? `Last completed ${fmtDate(completedAt)} · ${stateWord}`
                    : (tipsOn
                        ? (seen ? 'Tour seen · tips on' : 'Not started — runs on first login')
                        : 'Tips off — nothing shown');
                  return (
                    <div className="mt-4 p-4 bg-white border border-[#DADCCD] rounded-xl">
                      <div className="font-['Metropolis'] text-[10px] font-bold tracking-[0.18em] uppercase text-[#51534A] mb-1">Onboarding</div>
                      <div className="flex items-center justify-between gap-4 py-3 border-t border-line mt-2">
                        <div className="min-w-0">
                          <div className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">Onboarding tips</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Auto-runs the welcome tour on their first login. They can replay it any time from their account menu.</div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={tipsOn}
                          aria-label="Onboarding tips"
                          onClick={handleToggleOnboardingTips}
                          className="relative w-[38px] h-[22px] rounded-full flex-shrink-0 transition-colors"
                          style={{ background: tipsOn ? '#92D400' : '#3a3d35' }}>
                          <span
                            aria-hidden="true"
                            className="absolute top-[2px] left-[2px] w-[18px] h-[18px] rounded-full bg-white transition-transform"
                            style={{ transform: tipsOn ? 'translateX(16px)' : 'translateX(0)' }} />
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3 border-t border-line">
                        <div className="min-w-0">
                          <div className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">Replay on next login</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">Re-runs the full tour the next time they sign in.</div>
                        </div>
                        <button
                          type="button"
                          onClick={handleReplayOnNextLogin}
                          className="font-['Metropolis'] text-xs font-bold text-[#2E8FA1] hover:brightness-110 bg-transparent border-0 cursor-pointer whitespace-nowrap transition-[filter]">
                          Reset →
                        </button>
                      </div>
                      <div className="flex items-center justify-between gap-4 py-3 border-t border-line">
                        <div className="min-w-0">
                          <div className="font-['Metropolis'] text-sm font-semibold text-fusion-mid-grey">Last completed</div>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">{statusLine}</div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                <p className={sectionClass}>Ratings</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {[['rating_general', 'General'], ['rating_price', 'Price'], ['rating_quality', 'Quality']].map(([field, label]) => {
                    const val = form[field] || 0;
                    return (
                    <div key={field}>
                      <div className="flex items-baseline justify-between mb-1">
                        <label className={labelClass}>{label}</label>
                        {/* Five stars, matching the Performance section — the
                            page used to rate out of TEN here and show quality
                            out of FIVE above it (§5). Unrated reads as unrated:
                            pale empty stars and "Not yet rated", never a row of
                            dark stars that looks like a top score. */}
                        {val ? <span className="font-['Metropolis'] text-xs text-[#51534A]">{val} / 5</span>
                             : <span className="font-['Metropolis'] text-xs italic text-[#9BA08C]">Not yet rated</span>}
                      </div>
                      <div className="flex gap-0.5">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} type="button"
                            onClick={() => set(field, val === n ? '' : n)}
                            aria-label={`Set ${label} to ${n} out of 5`}
                            className="text-2xl leading-none transition-colors hover:scale-110"
                            style={{color: n <= val ? '#92D400' : '#DADCCD'}}>★</button>
                        ))}
                      </div>
                    </div>
                    );
                  })}
                </div>

                <p className={sectionClass}>Notes</p>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {[['notes_general', 'General Notes'], ['notes_previous_work', 'Previous Work'], ['notes_relationship', 'Relationship Notes']].map(([field, label]) => (
                    <div key={field}>
                      <label className={labelClass}>{label}</label>
                      <AutoSizeTextarea minRows={5} className={inputClass.replace('opacity-60 cursor-not-allowed', '')} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                    </div>
                  ))}
                </div>

                {/* Save bar — sticky at the bottom of the always-editable
                    sections. Mirrors the Finance Use Only save pattern. */}
                <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-line">
                  <button onClick={saveManagementSection} disabled={savingManagement} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                    {/* Names its scope — this bar saves the status flags,
                        ratings and notes on this tab, not the whole record. */}
                    {savingManagement ? 'Saving...' : 'Save management details'}
                  </button>
                </div>

                {/* Supplier Portal Users */}
                <div className="flex items-center justify-between mt-8 mb-4 pb-2 border-b border-line">
                  <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest">Supplier Portal Users</p>
                  <button onClick={() => { setShowAddUser(true); setEditingUser(null); }}
                    className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-medium px-4 py-1.5 hover:bg-[#6a9a00] transition-colors">
                    + Add User
                  </button>
                </div>

                {showAddUser && (
                  <div className="bg-white border border-line rounded-xl p-6 mb-6">
                    <p className="font-['Metropolis'] text-xs font-bold tracking-[3px] uppercase text-ink mb-4">New Supplier User</p>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className={labelClass}>Full Name</label>
                        <input className={inputClass.replace('opacity-60 cursor-not-allowed', '')} value={newUser.full_name} onChange={e => setNewUser(prev => ({...prev, full_name: e.target.value}))} />
                      </div>
                      <div>
                        <label className={labelClass}>Email Address</label>
                        <input type="email" className={inputClass.replace('opacity-60 cursor-not-allowed', '')} value={newUser.email} onChange={e => setNewUser(prev => ({...prev, email: e.target.value}))} />
                      </div>
                      <div className="col-span-2">
                        <label className={labelClass}>Temporary Password</label>
                        <div className="flex gap-2">
                          <input type="text" className={inputClass.replace('opacity-60 cursor-not-allowed', '') + ' flex-1'} value={newUser.password} onChange={e => setNewUser(prev => ({...prev, password: e.target.value}))} />
                          <button type="button" onClick={generatePasswordForNewUser}
                            className="flex items-center gap-2 bg-white border border-[#92D400]/40 text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-active-green/10 transition-colors whitespace-nowrap"
                            title="Generate a secure random password">
                            <RefreshIcon /> Generate
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Send-now vs send-later. Default on (the common case).
                        Unticking creates the account silently — the welcome +
                        password can be sent afterwards from the user's row. */}
                    <label className="flex items-start gap-2.5 mb-4 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newUser.notify}
                        onChange={e => setNewUser(prev => ({ ...prev, notify: e.target.checked }))}
                        className="mt-0.5 w-4 h-4 accent-active-green cursor-pointer" />
                      <span className="font-['Metropolis'] text-xs text-fusion-mid-grey leading-snug">
                        <span className="text-ink font-semibold">Email the welcome &amp; password now</span><br />
                        Leave unticked to set the account up without emailing — you can send their login details later from the user&rsquo;s row.
                      </span>
                    </label>
                    {error && (
                      <p className="font-['Metropolis'] text-sm text-[#E0290A] mb-3">{error}</p>
                    )}
                    <div className="flex gap-3">
                      <button onClick={createSupplierUser} disabled={saving}
                        className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-medium px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                        {saving ? 'Creating...' : (newUser.notify ? 'Create & send' : 'Create user')}
                      </button>
                      <button onClick={() => { setShowAddUser(false); setNewUser({ full_name: '', email: '', password: '', notify: true }); setError(null); }}
                        className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-xs font-medium px-5 py-2 hover:border-[#DADCCD] transition-colors">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {editingUser && (
                  <div className="bg-white border border-[#92D400]/30 rounded-xl p-6 mb-6">
                    <p className="font-['Metropolis'] text-xs font-bold tracking-[3px] uppercase text-ink mb-4">Edit Portal User</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Full Name *</label>
                        <input className={inputClass.replace('opacity-60 cursor-not-allowed', '')} value={editingUser.full_name || ''} onChange={e => setEditingUser(prev => ({...prev, full_name: e.target.value}))} />
                      </div>
                      <div>
                        <label className={labelClass}>Email *</label>
                        <input type="email" className={inputClass.replace('opacity-60 cursor-not-allowed', '')} value={editingUser.email || ''} onChange={e => setEditingUser(prev => ({...prev, email: e.target.value}))} />
                      </div>
                      <div>
                        <label className={labelClass}>Status</label>
                        <select className={inputClass.replace('opacity-60 cursor-not-allowed', '')}
                          value={editingUser.is_active === false ? 'disabled' : 'enabled'}
                          onChange={e => setEditingUser(prev => ({...prev, is_active: e.target.value === 'enabled'}))}>
                          <option value="enabled">Enabled</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className={labelClass}>Password</label>
                      <div className="flex gap-2 items-stretch">
                        <div className="relative flex-1">
                          <input
                            type={editingPasswordRevealed ? 'text' : 'password'}
                            value={editingPassword}
                            readOnly
                            placeholder="Click Generate Password to set a new password"
                            className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 pr-10 font-['Metropolis'] text-sm focus:outline-none" />
                          <button type="button"
                            onClick={() => setEditingPasswordRevealed(prev => !prev)}
                            disabled={!editingPassword}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#51534A] hover:text-active-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                            title={editingPasswordRevealed ? 'Hide password' : 'Show password'}>
                            <EyeIcon show={editingPasswordRevealed} />
                          </button>
                        </div>
                        <button onClick={() => generatePasswordForEdit(editingUser)} disabled={saving}
                          className="flex items-center gap-2 bg-white border border-[#92D400]/40 text-fusion-forest font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-active-green/10 transition-colors disabled:opacity-50 whitespace-nowrap">
                          <RefreshIcon /> Generate Password
                        </button>
                        <button onClick={copyEditingPassword} disabled={!editingPassword}
                          className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
                          <CopyIcon /> Copy Password
                        </button>
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t border-line flex flex-wrap items-center gap-3">
                      <p className="font-['Metropolis'] text-xs font-semibold text-[#51534A] mr-2">Send Email:</p>
                      <button onClick={() => resendSupplierUserWelcome(editingUser)} disabled={saving}
                        className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-active-green hover:text-active-green transition-colors disabled:opacity-50">
                        <EnvelopeIcon /> Welcome Email
                      </button>
                      <button onClick={() => sendPasswordEmailFromEdit(editingUser)} disabled={saving || !editingPassword}
                        title={!editingPassword ? 'Generate a password first' : 'Email this password to the user'}
                        className="flex items-center gap-2 bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:border-[#2E8FA1] hover:text-[#2E8FA1] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        <KeyIcon /> Password Email
                      </button>
                    </div>

                    <div className="flex justify-between gap-3 mt-6 pt-4 border-t border-line">
                      <button onClick={() => deleteSupplierUser(editingUser)} disabled={saving}
                        className="flex items-center gap-2 bg-white border border-[#E0290A]/40 rounded-xl text-[#E0290A] font-['Metropolis'] text-sm font-semibold px-4 py-2 hover:bg-[#E0290A]/10 transition-colors disabled:opacity-50">
                        <TrashIcon /> Delete User
                      </button>
                      <div className="flex gap-3">
                        <button onClick={() => setEditingUser(null)}
                          className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-xs font-medium px-5 py-2 hover:border-[#DADCCD] transition-colors">
                          Cancel
                        </button>
                        {/* This one saves the PORTAL USER being edited, not the
                            supplier record — the two sat feet apart both
                            saying "Save Changes" (IA §5). */}
                        <button onClick={updateSupplierUser} disabled={saving}
                          className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-medium px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                          {saving ? 'Saving…' : 'Save user'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {supplierUsers.length === 0 ? (
                  <p className="font-['Metropolis'] text-sm text-[#51534A] mb-6">No portal users linked to this supplier.</p>
                ) : (
                  <div className="overflow-x-auto">
                    {/* Portal users: Name, Email, Status, Last Logged In + actions. */}
                    <table className="w-full mb-6 min-w-[720px]">
                      <thead>
                        <tr className="border-b border-line">
                          <th className="text-left py-2"><p className="font-['Metropolis'] text-xs font-semibold text-[#51534A]">Name</p></th>
                          <th className="text-left py-2"><p className="font-['Metropolis'] text-xs font-semibold text-[#51534A]">Email</p></th>
                          <th className="text-left py-2"><p className="font-['Metropolis'] text-xs font-semibold text-[#51534A]">Status</p></th>
                          <th className="text-left py-2"><p className="font-['Metropolis'] text-xs font-semibold text-[#51534A]">Last Logged In</p></th>
                        </tr>
                      </thead>
                      <tbody>
                        {supplierUsers.map(user => (
                          <tr key={user.id}
                            onClick={() => { setEditingUser(user); setShowAddUser(false); }}
                            className="border-b border-line hover:bg-white transition-colors cursor-pointer">
                            <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-ink">{user.full_name}</p></td>
                            <td className="py-3 pr-4"><p className="font-['Metropolis'] text-sm text-fusion-mid-grey">{user.email}</p></td>
                            <td className="py-3 pr-4">
                              <span className={`font-['Metropolis'] text-xs font-medium rounded-md px-2 py-1 ${user.is_active ? 'text-fusion-forest bg-[#92D400]/10' : 'text-[#E0290A] bg-[#E0290A]/10'}`}>
                                {user.is_active ? 'Enabled' : 'Disabled'}
                              </span>
                            </td>
                            <td className="py-3 pr-4">
                              <p className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                                {user.last_sign_in_at
                                  ? fmtDateTime(user.last_sign_in_at)
                                  : 'Never'}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {activeTab === 'finance' && canSeeFinanceTab && (
              <>
                {/* Supplier company identity & registration — sourced from
                    the supplier's own set-up form submission. Editable only
                    by Finance role users (financeEditable gate). */}
                <p className={sectionClass}>Supplier Company Identity & Registration</p>
                <div className={`p-5 border ${userIsFinance ? 'border-[#2E8FA1]/30' : 'border-line'} bg-white`}>
                  <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
                    {form.supplier_form_submitted
                      ? (userIsFinance
                          ? 'These details were provided by the supplier via their set-up form. You can edit them.'
                          : 'These details were provided by the supplier via their set-up form. Only Finance role users can edit them.')
                      : 'These fields will be populated automatically when the supplier completes their set-up form. They have not yet submitted.'}
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      ['supplier_company_legal_name', 'Company Legal Name'],
                      ['supplier_trading_name', 'Trading Name'],
                      ['supplier_reg_number', 'Registration Number'],
                      ['supplier_years_trading', 'Years Trading'],
                      ['supplier_vat_number', 'VAT Number'],
                      ['supplier_type', 'Supplier Type'],
                    ].map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        {financeEditable
                          ? <input className={financeInputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                          : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                <p className={sectionClass}>Business Context</p>
                <div className="space-y-6">
                  {[
                    ['goods_services_description', 'What goods/services will this supplier provide?'],
                    ['reason_for_new_supplier', 'Why is a new supplier required?'],
                    ['impact_if_not_setup', 'Impact if this supplier is not set up?'],
                    ['who_evaluated', 'Who evaluated supplier options?'],
                  ].map(([field, label]) => (
                    <div key={field}>
                      <label className={labelClass}>{label}</label>
                      {editing
                        ? <AutoSizeTextarea minRows={3} className={inputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                        : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                    </div>
                  ))}
                </div>

                <p className={sectionClass}>Project & Spend</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Initial Project / Cost Centre</label>
                    {editing
                      ? <input className={inputClass} value={form.initial_project_cost_centre || ''} onChange={e => set('initial_project_cost_centre', e.target.value)} />
                      : <p className={valueClass}>{form.initial_project_cost_centre || NOT_SET}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Expected Duration</label>
                    {editing
                      ? <select className={inputClass} value={form.expected_duration || ''} onChange={e => set('expected_duration', e.target.value)}>
                          <option value="">Select...</option>
                          <option value="One off">One off</option>
                          <option value="Short term">Short term</option>
                          <option value="Ongoing">Ongoing</option>
                        </select>
                      : <p className={valueClass}>{form.expected_duration || NOT_SET}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Estimated Value of First Order</label>
                    {editing
                      ? <MoneyInput value={form.estimated_first_order ?? ''} onChange={(v) => set('estimated_first_order', v)} min="0" />
                      : <p className={valueClass}>{form.estimated_first_order != null ? `£${Number(form.estimated_first_order).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Estimated Annual / Ongoing Spend</label>
                    {editing
                      ? <MoneyInput value={form.estimated_annual_spend ?? ''} onChange={(v) => set('estimated_annual_spend', v)} min="0" />
                      : <p className={valueClass}>{form.estimated_annual_spend != null ? `£${Number(form.estimated_annual_spend).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}</p>}
                  </div>
                </div>

                {/* Additional Supplier Information — Fusion-side
                    classification: category, sub-category, analysis codes,
                    source and the supplier's account code. Edit gating is
                    the same as other Fusion-managed fields (global edit
                    mode, not finance-only). */}
                <p className={sectionClass}>Additional Supplier Information</p>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Category</label>
                    {editing
                      ? <select className={inputClass} value={form.category || ''} onChange={e => set('category', e.target.value)}>
                          <option value="">Select category...</option>
                          {categories.map(c => <option key={c.value} value={c.value}>{c.value}</option>)}
                        </select>
                      : <p className={valueClass}>{categoryOnly(form)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Sub Category</label>
                    {editing
                      ? <input className={inputClass} value={form.sub_category || ''} onChange={e => set('sub_category', e.target.value)} />
                      : <p className={valueClass}>{subCategoryOnly(form)}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Analysis Code 1</label>
                    {editing
                      ? <input className={inputClass} value={form.analysis_code_1 || ''} onChange={e => set('analysis_code_1', e.target.value)} />
                      : <p className={valueClass}>{form.analysis_code_1 || NOT_SET}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Analysis Code 2</label>
                    {editing
                      ? <input className={inputClass} value={form.analysis_code_2 || ''} onChange={e => set('analysis_code_2', e.target.value)} />
                      : <p className={valueClass}>{form.analysis_code_2 || NOT_SET}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Account Code</label>
                    {editing
                      ? <input className={inputClass} value={form.account_code || ''} onChange={e => set('account_code', e.target.value)} />
                      : <p className={valueClass}>{form.account_code || NOT_SET}</p>}
                  </div>
                  <div className="col-span-2">
                    <label className={labelClass}>Source</label>
                    {editing
                      ? <input className={inputClass} value={form.source || ''} onChange={e => set('source', e.target.value)} />
                      : <p className={valueClass}>{form.source || NOT_SET}</p>}
                  </div>
                </div>

                {/* Supplier-Provided Details — registered address, accounts
                    contact, bank details, signature. Sourced from the
                    supplier's own set-up form submission; edit gated to
                    Finance role. */}
                <p className={sectionClass}>Supplier-Provided Details</p>
                <div className={`p-5 border ${userIsFinance ? 'border-[#2E8FA1]/30' : 'border-line'} bg-white`}>
                  <p className={subSectionClass}>Registered Address</p>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      ['supplier_address_line_1', 'Address Line 1'],
                      ['supplier_address_line_2', 'Address Line 2'],
                      ['supplier_address_line_3', 'Address Line 3'],
                      ['supplier_town', 'Town'],
                      ['supplier_postcode', 'Postcode'],
                    ].map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        {financeEditable
                          ? <input className={financeInputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                          : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                      </div>
                    ))}
                  </div>

                  <p className={subSectionClass}>Accounts Contact</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                      ['supplier_accounts_contact_name', 'Name'],
                      ['supplier_accounts_contact_email', 'Email'],
                      ['supplier_accounts_contact_phone', 'Phone'],
                    ].map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        {financeEditable
                          ? <input className={financeInputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                          : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                      </div>
                    ))}
                  </div>

                  <p className={subSectionClass}>Bank Details</p>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      ['supplier_bank_name', 'Bank Name'],
                      ['supplier_bank_account_name', 'Name on Account'],
                      ['supplier_bank_sort_code', 'Sort Code'],
                      ['supplier_bank_account_number', 'Account Number'],
                    ].map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        {financeEditable
                          ? <input className={financeInputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                          : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                      </div>
                    ))}
                    <div className="col-span-2">
                      <label className={labelClass}>Accepts Payment by Credit Card</label>
                      {financeEditable
                        ? <select className={financeInputClass}
                            value={form.supplier_accepts_credit_card === true ? 'yes' : form.supplier_accepts_credit_card === false ? 'no' : ''}
                            onChange={e => set('supplier_accepts_credit_card', e.target.value === '' ? null : e.target.value === 'yes')}>
                            <option value="">—</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        : <p className={valueClass}>{form.supplier_accepts_credit_card === true ? 'Yes' : form.supplier_accepts_credit_card === false ? 'No' : '—'}</p>}
                    </div>
                  </div>

                  <p className={subSectionClass}>Signature</p>
                  <div className="grid grid-cols-2 gap-6">
                    {[
                      ['supplier_form_completed_by_name', 'Completed By'],
                      ['supplier_form_completed_by_position', 'Position in Company'],
                      ['supplier_form_signed_date', 'Signed Date'],
                    ].map(([field, label]) => (
                      <div key={field}>
                        <label className={labelClass}>{label}</label>
                        {financeEditable
                          ? <input className={financeInputClass} value={form[field] || ''} onChange={e => set(field, e.target.value)} />
                          : <p className={valueClass}>{form[field] || NOT_SET}</p>}
                      </div>
                    ))}
                    <div className="col-span-2">
                      <label className={labelClass}>Digital Signature</label>
                      {/* Drawn signatures land as base64 data URLs — render as image.
                          Older typed signatures (pre-2026-04) are plain text — render as text. */}
                      {form.supplier_form_signature
                        ? (form.supplier_form_signature.startsWith('data:image/')
                            ? <img src={form.supplier_form_signature} alt="Supplier signature" className="bg-[#F9FCF1] border border-line max-w-md" style={{height: 120}} />
                            : <p className={valueClass}>{form.supplier_form_signature}</p>)
                        : <p className={valueClass}>—</p>}
                    </div>
                  </div>
                </div>

                <p className={sectionClass}>Commercial</p>
                <div>
                  <label className={labelClass}>Supplier Payment Terms</label>
                  {editing
                    ? <input className={inputClass} value={form.payment_terms || ''} onChange={e => set('payment_terms', e.target.value)} />
                    : <p className={valueClass}>{form.payment_terms || NOT_SET}</p>}
                </div>

                {/* Finance Use Only */}
                <p className={sectionClass}>Finance Use Only</p>
                <div className={`p-5 border ${userIsFinance ? 'border-[#2E8FA1]/30 bg-white' : 'border-line bg-white'}`}>
                  <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">
                    {userIsFinance
                      ? 'You can edit this section.'
                      : 'This section can only be edited by Finance role users. Other internal users can view it.'}
                  </p>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label className={labelClass}>Supplier Credit Check Completed</label>
                      <select className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_credit_check_completed === true ? 'yes' : form.finance_credit_check_completed === false ? 'no' : ''}
                        onChange={e => set('finance_credit_check_completed', e.target.value === '' ? null : e.target.value === 'yes')}>
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Risk Flags Found</label>
                      <select className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_risk_flags === true ? 'yes' : form.finance_risk_flags === false ? 'no' : ''}
                        onChange={e => set('finance_risk_flags', e.target.value === '' ? null : e.target.value === 'yes')}>
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className={labelClass}>Risk Flag Details</label>
                      <AutoSizeTextarea minRows={2} className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_risk_flag_details || ''} onChange={e => set('finance_risk_flag_details', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelClass}>Approved by Finance</label>
                      <select className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_approved === true ? 'yes' : form.finance_approved === false ? 'no' : ''}
                        onChange={e => set('finance_approved', e.target.value === '' ? null : e.target.value === 'yes')}>
                        <option value="">Select...</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Name of Approver</label>
                      <input className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_approver_name || ''} onChange={e => set('finance_approver_name', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelClass}>Supplier Account Code Created</label>
                      <input className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_account_code_created || ''} onChange={e => set('finance_account_code_created', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelClass}>Date Account Created</label>
                      <input className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_account_created_date || ''} onChange={e => set('finance_account_created_date', e.target.value)} />
                    </div>
                    <div>
                      <label className={labelClass}>Name of Finance User</label>
                      <input className={readOnlyClass}
                        value={form.finance_user_name || ''} readOnly />
                    </div>
                    <div className="col-span-2">
                      <label className={labelClass}>Notes</label>
                      <AutoSizeTextarea minRows={3} className={financeInputClass} disabled={!financeEditable}
                        value={form.finance_notes || ''} onChange={e => set('finance_notes', e.target.value)} />
                    </div>
                  </div>
                  {/* Dedicated save for finance users so they don't have to enter
                      global edit mode. Hidden when global edit is active —
                      otherwise the user sees two save buttons doing different things. */}
                  {userIsFinance && !editing && (
                    <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-line">
                      {financeFeedback && (
                        <p className={`font-['Metropolis'] text-sm ${financeFeedback.type === 'error' ? 'text-[#E0290A]' : 'text-fusion-forest'}`}>
                          {financeFeedback.message}
                        </p>
                      )}
                      <button onClick={saveFinanceSection} disabled={savingFinance} className="bg-[#92D400] text-[#131313] rounded-r-btn font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                        {savingFinance ? 'Saving...' : 'Save Finance Section'}
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'briefs' && (
              <>
                {briefsLoading ? (
                  <p className="font-['Metropolis'] text-sm text-fusion-mid-grey mt-2">Loading briefs...</p>
                ) : briefs.length === 0 ? (
                  <div className={groupCardClass + ' mt-2'}>
                    <p className="font-['Metropolis'] text-sm text-[#51534A]">This supplier hasn't been invited to any briefs yet.</p>
                  </div>
                ) : (
                  (() => {
                    // §7: a table, not 26 tall cards. Cards are for browsing a
                    // handful of things; this is a track record you scan and
                    // compare — "how often do we ask them, how often do they
                    // say yes" — which is a column job.
                    const OUTCOME_TONE = { good: 'text-[#3B6D11] bg-[#3B6D11]/10', warn: 'text-[#9A6208] bg-[#9A6208]/10', bad: 'text-[#E0290A] bg-[#E0290A]/10', muted: 'text-[#51534A] bg-[#51534A]/10' };
                    const rows = briefs.map(r => {
                      const b = r.briefs || {};
                      const fb = feedbackRows.find(f => f.brief_id === b.id) || null;
                      return {
                        r,
                        b,
                        outcome: fb ? feedbackOutcomeLabel(feedbackState(fb.briefs, fb), fb) : null,
                        sentAt: b.created_at || null,
                        dueAt: r.response_deadline || null,
                        name: b.brief_name || 'Untitled brief',
                        response: r.status || 'pending',
                        briefStatus: b.status || 'unknown',
                      };
                    });
                    const dir = briefSortDir === 'asc' ? 1 : -1;
                    const sorted = [...rows].sort((x, y) => {
                      const pick = (row) => ({
                        name: (row.name || '').toLowerCase(),
                        sentAt: row.sentAt || '',
                        dueAt: row.dueAt || '',
                        response: row.response,
                        briefStatus: row.briefStatus,
                      }[briefSortKey]);
                      const a = pick(x); const b2 = pick(y);
                      if (a === b2) return 0;
                      return a > b2 ? dir : -dir;
                    });

                    // The summary line the brief asks for — sent / accepted /
                    // response rate. Declines and non-responses are different
                    // things, so they're counted separately rather than folded
                    // into one "not won" number.
                    const accepted = rows.filter(x => x.response === 'accepted').length;
                    const declined = rows.filter(x => x.response === 'declined').length;
                    const answered = accepted + declined;
                    const rate = rows.length > 0 ? Math.round((answered / rows.length) * 100) : 0;

                    const sortBtn = (key, label, extra = '') => (
                      <th scope="col" className={`font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A] px-3 py-2.5 ${extra}`}
                        aria-sort={briefSortKey === key ? (briefSortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button type="button"
                          onClick={() => {
                            if (briefSortKey === key) setBriefSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
                            else { setBriefSortKey(key); setBriefSortDir(key === 'sentAt' || key === 'dueAt' ? 'desc' : 'asc'); }
                          }}
                          className="inline-flex items-center gap-1 bg-transparent border-0 p-0 cursor-pointer hover:text-ink">
                          {label}
                          {briefSortKey === key && <span aria-hidden="true">{briefSortDir === 'asc' ? '↑' : '↓'}</span>}
                        </button>
                      </th>
                    );

                    return (
                      <>
                        <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4 mt-2">
                          <strong className="text-ink">{rows.length}</strong> {rows.length === 1 ? 'brief' : 'briefs'} sent ·{' '}
                          <strong className="text-ink">{accepted}</strong> accepted ·{' '}
                          <strong className="text-ink">{declined}</strong> declined ·{' '}
                          <strong className="text-ink">{rate}%</strong> answered
                        </p>
                        <div className="bg-white border border-line rounded-xl overflow-x-auto">
                          <table className="w-full text-left min-w-[720px]">
                            <thead>
                              <tr className="bg-[#F7F9F0] border-b border-line">
                                {sortBtn('name', 'Brief')}
                                {sortBtn('sentAt', 'Sent')}
                                {sortBtn('dueAt', 'Response by')}
                                {sortBtn('response', 'Their response')}
                                {sortBtn('briefStatus', 'Brief status')}
                                <th scope="col" className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.14em] text-[#51534A] px-3 py-2.5">How it went</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sorted.map(({ r, b, outcome, name }) => {
                                const responseColour =
                                  r.status === 'accepted' ? 'text-fusion-forest bg-[#92D400]/10' :
                                  r.status === 'declined' ? 'text-[#E0290A] bg-[#E0290A]/10' :
                                  'text-fusion-mid-grey bg-[#DADCCD]/10';
                                const briefColour =
                                  b.status === 'sent' ? 'text-fusion-forest bg-[#92D400]/10' :
                                  b.status === 'closed' ? 'text-[#51534A] bg-[#51534A]/10' :
                                  b.status === 'cancelled' ? 'text-[#E0290A] bg-[#E0290A]/10' :
                                  'text-fusion-mid-grey bg-[#DADCCD]/10';
                                return (
                                  <tr key={r.id} className="border-b border-[#F0F2E8] last:border-0 hover:bg-[#F7F9F0]/60">
                                    <td className="px-3 py-2.5 align-top">
                                      <button type="button" onClick={() => navigate(`/briefs/${b.id}`)}
                                        className="font-['Metropolis'] text-[13px] font-semibold text-ink hover:text-active-green bg-transparent border-0 p-0 cursor-pointer text-left">
                                        {name}
                                      </button>
                                      {(b.fusion_project_number || b.fusion_project_name) && (
                                        <div className="font-['Metropolis'] text-[11px] text-[#51534A]">
                                          {[b.fusion_project_number, b.fusion_project_name].filter(Boolean).join(' · ')}
                                        </div>
                                      )}
                                      {/* §7: the decline reason is LABELLED. A
                                          bare quote under a brief name never
                                          said whose words they were. */}
                                      {r.status === 'declined' && r.decline_reason && (
                                        <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1">
                                          <span className="font-semibold">Reason given:</span> {r.decline_reason}
                                        </div>
                                      )}
                                      {b.archived_at && (
                                        <div className="font-['Metropolis'] text-[10px] text-[#9BA08C] uppercase tracking-[0.12em] mt-1">Archived</div>
                                      )}
                                    </td>
                                    <td className="font-['Metropolis'] text-[12px] text-[#51534A] px-3 py-2.5 align-top whitespace-nowrap">{b.created_at ? fmtDate(b.created_at) : NOT_SET}</td>
                                    <td className="font-['Metropolis'] text-[12px] text-[#51534A] px-3 py-2.5 align-top whitespace-nowrap">{r.response_deadline ? fmtDate(r.response_deadline) : NOT_SET}</td>
                                    <td className="px-3 py-2.5 align-top">
                                      <span className={`font-['Metropolis'] text-[11px] font-medium rounded-md px-2 py-1 whitespace-nowrap ${responseColour}`}>
                                        {(r.status || 'pending').replace(/^./, c => c.toUpperCase())}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                      <span className={`font-['Metropolis'] text-[11px] font-medium rounded-md px-2 py-1 whitespace-nowrap ${briefColour}`}>
                                        {(b.status || 'Unknown').replace(/^./, c => c.toUpperCase())}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2.5 align-top">
                                      {outcome
                                        ? <span className={`font-['Metropolis'] text-[11px] font-semibold rounded-md px-2 py-1 whitespace-nowrap ${OUTCOME_TONE[outcome.tone] || OUTCOME_TONE.muted}`}>{outcome.text}</span>
                                        : <span className="font-['Metropolis'] text-[11px] text-[#9BA08C]">—</span>}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    );
                  })()
                )}

                {/* What project debriefs said about THIS supplier. A debrief
                    belongs to the project, but the parts about one supplier
                    belong on their record too — otherwise a lesson learned
                    about them is filed under a project number nobody thinks to
                    open. Read-only: debriefs are edited in the debrief.
                    Internal — never visible to the supplier. */}
                {debriefNotes.length > 0 && (
                  <div className="mt-8">
                    <p className="font-['Metropolis'] text-[11px] font-bold tracking-[0.18em] uppercase text-fusion-forest mb-1">From project debriefs</p>
                    <p className="font-['Metropolis'] text-[12px] text-[#51534A] mb-3">
                      What was said about {form?.supplier_name || 'this supplier'} when the project was reviewed. Internal — never shown to the supplier.
                    </p>
                    <ul className="bg-white border border-[#E6EADB] rounded-xl overflow-hidden">
                      {debriefNotes.map(n => (
                        <li key={n.id} className="px-4 py-3 border-b border-[#F0F2E8] last:border-0">
                          <p className="font-['Metropolis'] text-[13px] text-ink leading-snug whitespace-pre-wrap">{n.body}</p>
                          <div className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1 flex items-center gap-x-2 gap-y-1 flex-wrap">
                            {n.topic_title && <span>{n.topic_title}</span>}
                            {n.debrief && (
                              <>
                                <span aria-hidden="true">·</span>
                                <button
                                  type="button"
                                  onClick={() => navigate(`/debriefs/${n.debrief.id}`)}
                                  className="underline decoration-[#51534A]/30 underline-offset-2 hover:text-fusion-forest hover:decoration-fusion-forest rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-fusion-forest">
                                  {n.debrief.project_ref}{n.debrief.debrief_date ? ` · ${fmtDate(n.debrief.debrief_date)}` : ''}
                                </button>
                              </>
                            )}
                          </div>
                          {n.action && (
                            <div className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#F7F9F0] border border-[#E6EADB] px-2.5 py-1.5">
                              <span className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.12em] text-[#7A4E10]">Action</span>
                              <span className="font-['Metropolis'] text-[11px] text-[#51534A]">
                                {[n.action.owner || 'No owner', n.action.timeframe].filter(Boolean).join(' · ')}
                              </span>
                              <span className={`font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-0.5 border ${
                                n.action.status === 'open' ? 'text-[#7A4E10] bg-[#E89A2E]/12 border-[#E89A2E]/40'
                                  : n.action.status === 'done' ? 'text-fusion-forest bg-[#92D400]/12 border-[#92D400]/40'
                                  : 'text-[#51534A] bg-[#51534A]/8 border-[#DADCCD]'
                              }`}>
                                {n.action.status}
                              </span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}

            {activeTab === 'activity' && (() => {
              // Build the unified timeline by combining auto-derived events
              // (from suppliers, brief_recipients, brief_responses) with the
              // manual notes table. Sorted newest first.
              const events = [];
              if (form.created_at) {
                events.push({ kind: 'system', at: form.created_at, label: 'Supplier created', detail: null });
              }
              if (form.setup_form_sent_at) {
                events.push({ kind: 'system', at: form.setup_form_sent_at, label: 'New Supplier Form sent', detail: null });
              }
              if (form.setup_form_completed_at) {
                events.push({ kind: 'response', at: form.setup_form_completed_at, label: 'New Supplier Form completed', detail: null });
              } else if (form.supplier_form_submitted) {
                events.push({ kind: 'response', at: null, label: 'New Supplier Form completed', detail: null });
              }
              for (const r of briefs) {
                if (r.notified_at) {
                  events.push({
                    kind: 'brief',
                    at: r.notified_at,
                    label: `Invited to brief: ${r.briefs?.brief_name || 'Untitled brief'}`,
                    detail: r.briefs?.fusion_project_number || r.briefs?.fusion_project_name
                      ? [r.briefs?.fusion_project_number, r.briefs?.fusion_project_name].filter(Boolean).join(' · ')
                      : null,
                    briefId: r.briefs?.id,
                  });
                }
                if (r.viewed_at) {
                  events.push({
                    kind: 'view',
                    at: r.viewed_at,
                    label: `Opened brief: ${r.briefs?.brief_name || 'Untitled brief'}`,
                    detail: null,
                    briefId: r.briefs?.id,
                  });
                }
                if (r.status === 'declined') {
                  events.push({
                    kind: 'declined',
                    at: r.declined_at || null,
                    label: `Declined: ${r.briefs?.brief_name || 'Untitled brief'}`,
                    detail: r.decline_reason ? `"${r.decline_reason}"` : null,
                    briefId: r.briefs?.id,
                  });
                }
              }
              for (const ev of activityLoginEvents) {
                events.push({
                  kind: 'login',
                  at: ev.signed_in_at,
                  label: 'Logged in to portal',
                  detail: ev.user_email || null,
                });
              }
              // Supplier portal doc actions — synthesised from
              // supplier_onboarding_documents rather than a separate event
              // table. Each row with supplier_uploaded_at set represents a
              // portal upload (renewal certificate or first-time submission);
              // the label uses the compliance doc's human name where
              // available, falling back to the raw key.
              for (const doc of onboardingDocs) {
                if (!doc.supplier_uploaded_at) continue;
                const docLabel = docLabelFor(complianceDocs.find(d => d.key === doc.document_key)?.label, doc.document_key);
                events.push({
                  kind: 'portal_upload',
                  at: doc.supplier_uploaded_at,
                  label: `Supplier uploaded ${docLabel}`,
                  detail: doc.supplier_uploaded_file_name
                    ? `File: ${doc.supplier_uploaded_file_name}${doc.expiry_date ? ` · Proposed expiry: ${fmtDate(doc.expiry_date)}` : ''}`
                    : (doc.expiry_date ? `Proposed expiry: ${fmtDate(doc.expiry_date)}` : null),
                });
              }
              // New Supplier Form completion — driven by the supplier
              // submitting the public setup form. setup_form_completed_at
              // is stamped at submission time; supplier_form_submitted is
              // the flag.
              if (form.supplier_form_submitted && form.setup_form_completed_at) {
                events.push({
                  kind: 'portal_form_complete',
                  at: form.setup_form_completed_at,
                  label: 'Supplier completed the New Supplier Form',
                  detail: null,
                });
              }
              // Email sends from the supplier card (compliance reminders
              // for now; brief invites etc. follow once they're moved into
              // this log). One row per send-event, not per recipient — the
              // detail line lists everyone who received it.
              const emailKindLabel = (k) => k === 'compliance_reminder'
                ? 'Compliance reminder email sent'
                : k === 'supplier_activation'
                ? 'Launch / welcome email sent'
                : k === 'portal_user_welcome'
                ? 'Portal user welcome email sent'
                : k === 'portal_user_password'
                ? 'Portal user password email sent'
                : k === 'compliance_doc_published'
                ? 'New compliance document notification sent'
                : k === 'document_published'
                ? 'New document notification sent'
                : k === 'brief_reminder'
                ? 'Brief reminder email sent'
                : k === 'deadline_extended'
                ? 'Deadline extension email sent'
                : k === 'compliance_alert_brief_add'
                ? 'Compliance alert sent to internal users (added to brief)'
                : `Email sent (${k})`;
              for (const ev of activityEmailEvents) {
                const recipientList = Array.isArray(ev.recipients) ? ev.recipients.join(', ') : '';
                const senderLine = ev.sender?.full_name ? ` · sent by ${ev.sender.full_name}` : '';
                events.push({
                  kind: 'email',
                  at: ev.sent_at,
                  label: emailKindLabel(ev.email_kind),
                  detail: recipientList ? `To ${recipientList}${senderLine}` : (senderLine.trim() || null),
                });
              }
              for (const resp of activityResponses) {
                events.push({
                  kind: 'response',
                  at: resp.submitted_at || null,
                  label: `Submitted response to brief: ${resp.briefs?.brief_name || 'Untitled brief'}`,
                  detail: resp.total_price != null ? `Quoted £${Number(resp.total_price).toLocaleString()}` : null,
                  briefId: resp.brief_id,
                });
              }
              for (const note of activityNotes) {
                events.push({
                  kind: 'note',
                  at: note.created_at,
                  label: note.body,
                  detail: note.author?.full_name ? `Note by ${note.author.full_name}` : 'Note',
                  noteId: note.id,
                });
              }
              // Questions the supplier asked (+ Fusion's answer as its own event).
              for (const q of activityQuestions) {
                const briefName = q.briefs?.brief_name || 'a brief';
                events.push({
                  kind: 'question',
                  at: q.created_at,
                  label: `Asked a question on brief: ${briefName}`,
                  detail: q.body ? `"${q.body}"` : null,
                  briefId: q.brief_id,
                });
                const ans = (q.brief_question_answers || []).slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0];
                if (ans) {
                  events.push({
                    kind: 'question_answered',
                    at: ans.created_at,
                    label: `Fusion 2K answered their question on brief: ${briefName}`,
                    detail: ans.published ? 'Shared with everyone quoting' : 'Sent to this supplier',
                    briefId: q.brief_id,
                  });
                }
              }
              // Post-deadline update requests.
              for (const ur of activityUpdateRequests) {
                events.push({
                  kind: 'update_request',
                  at: ur.created_at,
                  label: `Requested an update on brief: ${ur.briefs?.brief_name || 'a brief'}`,
                  detail: ur.message && ur.message.trim() ? `"${ur.message.trim()}"` : null,
                  briefId: ur.brief_id,
                });
              }
              // Pending deadline-extension requests.
              for (const ext of activityExtensions) {
                events.push({
                  kind: 'extension_request',
                  at: ext.extension_requested_at,
                  label: `Requested more time on brief: ${ext.briefs?.brief_name || 'a brief'}`,
                  detail: ext.extension_request_note && ext.extension_request_note.trim() ? `"${ext.extension_request_note.trim()}"` : null,
                  briefId: ext.brief_id,
                });
              }
              // Phase 5 — acknowledge audit events surface as
              // 'compliance' kind so the existing Compliance filter
              // chip covers them. Three variants, all with the doc
              // label in the primary line and the actor (admin for
              // approved/rejected; signer for the supplier-side
              // acknowledged event) in the detail line.
              for (const ev of activityAckEvents) {
                const m = ev.meta || {};
                const docLabel = m.document_label ? m.document_label : (ev.document_key ? docLabelFor(null, ev.document_key) : 'compliance document');
                let label, detail;
                if (ev.action === 'acknowledged') {
                  label = `Acknowledged ${docLabel}`;
                  detail = m.signer_name ? `Signed by ${m.signer_name}` : null;
                } else if (ev.action === 'acknowledge_approved') {
                  label = `Acknowledgement approved · ${docLabel}`;
                  const who = ev.actor?.full_name;
                  const signer = m.signer_name;
                  detail = who && signer
                    ? `${who} · was signed by ${signer}`
                    : who ? `By ${who}`
                    : signer ? `Signed by ${signer}`
                    : null;
                } else if (ev.action === 'acknowledge_rejected') {
                  label = `Acknowledgement rejected · ${docLabel}`;
                  const who = ev.actor?.full_name ? `By ${ev.actor.full_name}` : null;
                  const why = m.review_notes ? `"${m.review_notes}"` : null;
                  detail = who && why ? `${who} — ${why}` : who || why || null;
                } else {
                  continue;
                }
                events.push({
                  kind: 'compliance',
                  at: ev.created_at,
                  label,
                  detail,
                });
              }
              for (const h of activityComplianceHistory) {
                const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : 'Cleared';
                const label = h.old_status
                  ? `Compliance status changed from ${cap(h.old_status)} to ${cap(h.new_status)}`
                  : `Compliance status set to ${cap(h.new_status)}`;
                // Detail line combines who-did-it + why-it-changed. Auto
                // changes have no author but carry a system-written reason
                // (doc that expired etc.); manual overrides carry both an
                // author name and the staff-entered reason.
                const who = h.author?.full_name ? `By ${h.author.full_name}` : null;
                const why = h.reason || null;
                const detail = who && why
                  ? `${who} — ${why}`
                  : who || why || null;
                events.push({
                  kind: 'compliance',
                  complianceStatus: h.new_status,
                  at: h.changed_at,
                  label,
                  detail,
                });
              }
              const sorted = events.sort((a, b) => {
                const ta = a.at ? new Date(a.at).getTime() : 0;
                const tb = b.at ? new Date(b.at).getTime() : 0;
                return tb - ta;
              });
              // Filter pipeline — first the chip selection (empty Set =
              // everything), then the free-text search across label +
              // detail. Done after sort so the displayed list keeps its
              // newest-first order.
              const allowedKinds = activityFilters.size === 0
                ? null
                : new Set(ACTIVITY_FILTER_GROUPS.filter(g => activityFilters.has(g.key)).flatMap(g => g.kinds));
              const search = activitySearch.trim().toLowerCase();
              const visibleEvents = sorted.filter(ev => {
                if (allowedKinds && !allowedKinds.has(ev.kind)) return false;
                if (search) {
                  const inLabel = ev.label && String(ev.label).toLowerCase().includes(search);
                  const inDetail = ev.detail && String(ev.detail).toLowerCase().includes(search);
                  if (!inLabel && !inDetail) return false;
                }
                return true;
              });
              const filtersActive = activityFilters.size > 0 || Boolean(search);
              const dotFor = (ev) => {
                if (ev.kind === 'compliance') {
                  return ev.complianceStatus === 'green' ? 'bg-[#92D400]'
                    : ev.complianceStatus === 'amber' ? 'bg-[#E89A2E]'
                    : ev.complianceStatus === 'red' ? 'bg-[#E0290A]'
                    : 'bg-[#51534A]';
                }
                switch (ev.kind) {
                  case 'note': return 'bg-[#F9FCF1]';
                  case 'brief': return 'bg-[#2E8FA1]';
                  case 'view': return 'bg-[#2E8FA1]';
                  case 'login': return 'bg-[#DADCCD]';
                  case 'email': return 'bg-[#E89A2E]';
                  case 'response': return 'bg-[#92D400]';
                  case 'declined': return 'bg-[#E0290A]';
                  case 'portal_upload': return 'bg-[#2E8FA1]';
                  case 'portal_form_complete': return 'bg-[#92D400]';
                  case 'question': return 'bg-[#7C6FD6]';
                  case 'question_answered': return 'bg-[#92D400]';
                  case 'update_request': return 'bg-[#E89A2E]';
                  case 'extension_request': return 'bg-[#E89A2E]';
                  default: return 'bg-[#51534A]';
                }
              };
              // "Last login" summary — most recent across both the new
              // login-events table and supplier_users.last_sign_in_at (the
              // latter is populated even before this feature shipped, so old
              // suppliers still show a meaningful value). Each candidate
              // carries name + email so we can name the most recent user
              // on the card rather than just "X users on this supplier".
              const loginCandidates = [
                ...activityLoginEvents.map(e => ({
                  at: e.signed_in_at,
                  email: e.user_email || null,
                  name: supplierUsers.find(u => (u.email || '').toLowerCase() === (e.user_email || '').toLowerCase())?.full_name || null,
                })),
                ...supplierUsers.map(u => ({
                  at: u.last_sign_in_at,
                  email: u.email || null,
                  name: u.full_name || null,
                })),
              ].filter(c => c.at);
              const lastLogin = loginCandidates.length
                ? loginCandidates.reduce((a, b) => new Date(a.at) > new Date(b.at) ? a : b)
                : null;
              const lastLoginAt = lastLogin?.at || null;
              return (
                <>
                  {/* Last login summary — sits above the note composer per
                      Robynne's design ("somewhere near the top"). Hidden if
                      we don't yet have a value to show. */}
                  <div className={groupCardClass + ' mt-2'}>
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className={labelClass}>Last Portal Login</p>
                        <p className="font-['Metropolis'] text-lg text-ink">
                          {lastLoginAt
                            ? fmtDateTime(lastLoginAt)
                            : <span className="text-[#51534A]">Never signed in</span>}
                        </p>
                        {lastLoginAt && (lastLogin?.name || lastLogin?.email) && (
                          <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">
                            By {lastLogin.name || lastLogin.email}
                            {lastLogin.name && lastLogin.email ? ` (${lastLogin.email})` : ''}
                          </p>
                        )}
                        {lastLoginAt && !(lastLogin?.name || lastLogin?.email) && (
                          <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">
                            Across {supplierUsers.length} {supplierUsers.length === 1 ? 'user' : 'users'} on this supplier
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Note composer */}
                  <div className={groupCardClass + ' mt-4'}>
                    <p className={labelClass}>Add a Note</p>
                    <AutoSizeTextarea
                      minRows={3}
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      placeholder="Capture a call, meeting, issue or anything else worth recording against this supplier..."
                      className="w-full bg-white border border-line rounded-xl text-ink px-4 py-2.5 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors" />
                    <div className="flex justify-end mt-3">
                      <button
                        type="button"
                        onClick={handleSaveNote}
                        disabled={savingNote || !newNote.trim()}
                        className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                        {savingNote ? 'Saving...' : 'Save Note'}
                      </button>
                    </div>
                  </div>

                  {/* Search + filter chips. Search runs case-insensitively
                      across event label + detail; chips toggle
                      multi-select. Hidden while activity is loading and
                      when there's no activity at all. */}
                  {!activityLoading && sorted.length > 0 && (
                    <div className="mb-4">
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <input
                          type="text"
                          value={activitySearch}
                          onChange={e => setActivitySearch(e.target.value)}
                          placeholder="Search the activity log..."
                          className="flex-1 min-w-[260px] bg-white border border-line rounded-xl text-ink px-4 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] transition-colors" />
                        {filtersActive && (
                          <button
                            type="button"
                            onClick={() => { setActivitySearch(''); setActivityFilters(new Set()); }}
                            className="font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase text-[#51534A] hover:text-active-green transition-colors px-3 py-2">
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {ACTIVITY_FILTER_GROUPS.map(g => {
                          const isActive = activityFilters.has(g.key);
                          return (
                            <button
                              key={g.key}
                              type="button"
                              onClick={() => toggleActivityFilter(g.key)}
                              className={`font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-3 py-1.5 transition-colors ${isActive ? 'bg-[#92D400] text-[#131313]' : 'bg-white border border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}>
                              {g.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {activityLoading ? (
                    <p className="font-['Metropolis'] text-sm text-[#51534A]">Loading activity...</p>
                  ) : sorted.length === 0 ? (
                    <p className="font-['Metropolis'] text-sm text-[#51534A]">No activity yet for this supplier.</p>
                  ) : visibleEvents.length === 0 ? (
                    <p className="font-['Metropolis'] text-sm text-[#51534A]">No activity matches your search or filters.</p>
                  ) : (
                    <div className="border border-line bg-white rounded-xl overflow-hidden">
                      {visibleEvents.map((ev, idx) => (
                        <div key={idx} className="flex items-start gap-4 px-4 py-3 border-b border-line last:border-0">
                          <div className="flex-shrink-0 mt-1.5">
                            <span className={`inline-block w-2 h-2 rounded-xl ${dotFor(ev)}`}></span>
                          </div>
                          <div className="flex-1 min-w-0">
                            {ev.kind === 'note' ? (
                              <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{ev.label}</p>
                            ) : ev.briefId ? (
                              <button type="button" onClick={() => navigate(`/briefs/${ev.briefId}`)} className="font-['Metropolis'] text-sm text-ink text-left hover:text-active-green transition-colors">
                                {ev.label}
                              </button>
                            ) : (
                              <p className="font-['Metropolis'] text-sm text-ink">{ev.label}</p>
                            )}
                            {ev.detail && (
                              <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5">{ev.detail}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <p className="font-['Metropolis'] text-xs text-[#51534A] whitespace-nowrap">
                              {ev.at ? fmtDateTime(ev.at) : ''}
                            </p>
                            {ev.kind === 'note' && (
                              <button
                                type="button"
                                onClick={() => handleDeleteNote(ev.noteId)}
                                className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A] hover:text-[#E0290A] transition-colors">
                                Delete
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              );
            })()}

            {activeTab === 'compliance' && (() => {
              // New Supplier Form lives at the top of Onboarding rather than
              // alongside the dynamic compliance-doc list because its lifecycle
              // is different: completion is supplier-driven (auto-flagged when
              // the public form is submitted), not a manual "mark signed".
              // It uses the existing setup_form_sent_at /
              // supplier_form_submitted / setup_form_completed_at columns on
              // the suppliers row.
              const nsfSent = Boolean(form.setup_form_sent_at);
              const nsfCompleted = Boolean(form.supplier_form_submitted);
              const nsfStatus = nsfCompleted ? 'signed' : nsfSent ? 'sent' : 'not_sent';
              const nsfBadge = {
                not_sent: 'text-[#51534A] bg-[#51534A]/10',
                sent: 'text-[#2E8FA1] bg-[#2E8FA1]/10',
                signed: 'text-fusion-forest bg-[#92D400]/10',
              }[nsfStatus];
              const nsfLabel = { not_sent: 'Not Sent', sent: 'Sent', signed: 'Completed' }[nsfStatus];
              const fmtNsf = (ts) => fmtDate(ts, '');
              const cs = form.compliance_status;
              const csCurrent = cs && csCfg[cs];
              const rf = form.risk_flag;
              const rfCurrent = rf && riskCfg[rf];
              // §3: the Documents tab gets its own rail — the compliance
              // numbers and what's chasing the supplier, so you don't have to
              // read every row to know where this supplier stands.
              // ⚠️ WHAT COUNTS. A document only counts against a supplier once
              // we've actually ASKED them for it — i.e. it's been published to
              // their portal (sent_at). An unpublished row is Fusion's
              // preparation, not the supplier's omission, and counting it as
              // Outstanding blames them for something they've never been shown.
              // Not-required rows are excluded for the same reason.
              //
              // This is why the rail's numbers can be kinder than the RED
              // status above it: the stored compliance_status is computed in
              // the database against the full required set for the tier,
              // including documents nobody has sent yet. See the note under
              // the counts.
              // A row also stops counting if the document behind it is no
              // longer live. Unpublished drafts were being treated as live
              // (is_active defaults to true) and auto-published to suppliers,
              // so rows exist with sent_at set against documents that were
              // never really published. They aren't the supplier's problem.
              const onLiveDoc = (d) => !complianceDocsLoaded || liveDocKeys.has(d.document_key);
              // A document the supplier has actually actioned counts, whether or
              // not anyone pressed Publish on it. Acknowledge-type documents are
              // signed in document_acknowledgements and never set sent_at at
              // all, so gating purely on sent_at both dropped them from the
              // totals and left them showing as unpublished — the counts then
              // disagreed with the review queue about the same document.
              const actioned = (d) => Boolean(d.sent_at)
                || hasSupplierActioned({ record: d, acknowledgement: ackByKey.get(d.document_key) });
              const countableDocs = onboardingDocs.filter(d => !d.not_required && actioned(d) && onLiveDoc(d));
              const unpublishedCount = onboardingDocs.filter(d => !d.not_required && !actioned(d) && onLiveDoc(d)).length;
              // Rows against a document that isn't live any more — reported
              // separately so the totals reconcile with the rows on screen.
              const notLiveCount = complianceDocsLoaded
                ? onboardingDocs.filter(d => !d.not_required && !liveDocKeys.has(d.document_key)).length
                : 0;
              // Same derivation as the rows above — the counts and the pills
              // must not be able to disagree about one document.
              const satisfiedByKey = new Map(complianceDocs.map(m => [m.key, m.satisfiedBy]));
              const docStates = countableDocs.map(d => documentStateFor({
                record: d,
                acknowledgement: ackByKey.get(d.document_key),
                satisfiedBy: satisfiedByKey.get(d.document_key),
              }));
              const docCounts = docStates.reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; }, {});
              const nextExpiry = onboardingDocs
                .filter(d => d.expiry_date && d.expiry_approved_at && !d.not_required)
                .map(d => d.expiry_date)
                .sort()[0] || null;

              return (
              <div className="grid grid-cols-1 min-[1100px]:grid-cols-[minmax(0,1fr)_300px] gap-8 items-start">
              <div className="min-w-0">
                <p className={sectionClass}>Risk Flag</p>
                {/* §6: risk is a MANUAL judgement and says so in one line; the
                    detail sits behind the disclosure like compliance's does. */}
                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4 -mt-2">
                  Set by hand — our exposure if this supplier falls over.
                  <InfoIcon heading="Risk flag vs compliance" ariaLabel="What's the difference between risk and compliance?">
                    <span className="block mb-1.5"><strong>Risk</strong> is a judgement you make: how much it would hurt if this supplier failed. High = significant business risk, Medium = informational, Low = fully cleared.</span>
                    <span className="block">
                      <strong>Compliance</strong> isn't a judgement — it's worked out from whether their documents are in order. The two are separate on purpose: a low-risk supplier can still be non-compliant.
                    </span>
                  </InfoIcon>
                </p>
                <div className={`p-4 border ${rfCurrent ? rfCurrent.border : 'border-line'} ${rfCurrent ? rfCurrent.bg : 'bg-white'} mb-6`}>
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <FlagIcon className={`h-5 w-5 ${rfCurrent ? rfCurrent.text : 'text-[#51534A]'}`} />
                      <p className={`font-['Metropolis'] text-sm font-semibold ${rfCurrent ? rfCurrent.text : 'text-[#51534A]'}`}>
                        {rfCurrent ? `${rfCurrent.label} Risk` : 'Not Set'}
                      </p>
                      {form.risk_flag_set_at && (
                        <p className="font-['Metropolis'] text-xs text-[#51534A]">
                          Last updated {fmtDate(form.risk_flag_set_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {['high', 'medium', 'low'].map(opt => {
                        const cfg = riskCfg[opt];
                        const isActive = rf === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => setRiskFlag(opt)}
                            disabled={savingRiskFlag}
                            className={`inline-flex items-center gap-1.5 font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-4 py-2 transition-colors disabled:opacity-50 ${isActive ? cfg.activeBtn : 'bg-white border border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}>
                            <FlagIcon />
                            {cfg.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <p className={sectionClass}>Compliance Status</p>
                {/* §6: one line, with the detail behind a disclosure. The old
                    four-line paragraph explained the whole derivation every
                    time anyone opened the tab. */}
                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4 -mt-2">
                  Worked out from this supplier's documents — nobody sets it by hand.
                  <InfoIcon heading="How compliance status is worked out" ariaLabel="How is compliance status worked out?">
                    <span className="block mb-1.5"><strong style={{ color: '#E0290A' }}>Red</strong> — a required document is expired, missing or was rejected.</span>
                    <span className="block mb-1.5"><strong style={{ color: '#7A4E10' }}>Amber</strong> — something expires soon, or is waiting on Fusion 2K.</span>
                    <span className="block mb-1.5"><strong style={{ color: '#3B6D11' }}>Green</strong> — every required document is approved and current.</span>
                    <span className="block">Overriding pins the status by hand for non-document reasons. It needs a reason and sticks until you revert it.</span>
                  </InfoIcon>
                </p>
                <div className={`p-4 border ${csCurrent ? csCurrent.border : 'border-line'} ${csCurrent ? csCurrent.bg : 'bg-white'} mb-6`}>
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="inline-block w-3 h-3 rounded-xl" style={{ background: csCurrent ? csCurrent.dot : '#51534A' }}></span>
                      <p className={`font-['Metropolis'] text-sm font-semibold ${csCurrent ? csCurrent.text : 'text-[#51534A]'}`}>
                        {csCurrent ? csCurrent.label : 'Not Set'}
                      </p>
                      <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-0.5 ${form.compliance_status_override ? 'text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40' : 'text-[#51534A] bg-[#51534A]/10 border border-[#DADCCD]/40'}`}>
                        {form.compliance_status_override ? 'Manual override' : 'Auto'}
                      </span>
                      {form.compliance_status_set_at && (
                        <p className="font-['Metropolis'] text-xs text-[#51534A]">
                          Last updated {fmtDate(form.compliance_status_set_at)}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {/* ONE action, not three equal buttons (§6). Risk is a
                          manual choice and looks like one; compliance is
                          derived, so the only thing to offer here is the
                          deliberate act of overriding it — and the status
                          picker belongs inside that decision, not beside the
                          value it would silently replace. */}
                      <button
                        type="button"
                        onClick={() => { setPendingOverrideStatus(cs || 'amber'); setOverrideReason(''); setShowOverrideModal(true); }}
                        disabled={savingComplianceStatus || savingOverride}
                        className="bg-white border border-line rounded-xl text-fusion-mid-grey font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-4 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                        Override…
                      </button>
                      {form.compliance_status_override && (
                        <button
                          type="button"
                          onClick={clearComplianceOverride}
                          disabled={savingComplianceStatus || savingOverride}
                          className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-4 py-2 hover:bg-[#2E8FA1]/10 transition-colors disabled:opacity-50">
                          Revert to Auto
                        </button>
                      )}
                    </div>
                  </div>
                  <label className={labelClass}>Notes</label>
                  {/* Thread of past notes — oldest first so the story reads
                      top-to-bottom and the latest entry sits closest to the
                      input box. Each note carries author + timestamp. */}
                  {complianceNotes.length > 0 && (
                    <div className="border border-line bg-white mb-3">
                      {complianceNotes.map(note => (
                        <div key={note.id} className="group px-3 py-2 border-b border-line last:border-0">
                          <div className="flex items-baseline justify-between gap-3 mb-1">
                            <p className="font-['Metropolis'] text-xs font-semibold text-fusion-mid-grey">{note.author?.full_name || NOT_SET}</p>
                            {/* §6: no Delete. These notes are the record of WHY
                                a compliance decision was made — an override, an
                                investigation, a judgement call. Deleting one
                                rewrites that record. A correction is another
                                note saying what changed. */}
                            <p className="font-['Metropolis'] text-[10px] text-[#51534A] whitespace-nowrap flex-shrink-0">{fmtDateTime(note.created_at)}</p>
                          </div>
                          <p className="font-['Metropolis'] text-sm text-ink whitespace-pre-wrap">{note.body}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <AutoSizeTextarea
                    value={newComplianceNote}
                    onChange={e => setNewComplianceNote(e.target.value)}
                    minRows={3}
                    placeholder="Add a note — why is this supplier on this status? (e.g. insurance lapsed, awaiting renewal, no concerns)"
                    className="w-full bg-white border border-line rounded-xl text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400]" />
                  <div className="mt-2 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={addComplianceNote}
                      disabled={savingComplianceNote || !newComplianceNote.trim()}
                      className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-semibold px-4 py-1.5 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                      {savingComplianceNote ? 'Saving...' : 'Add Note'}
                    </button>
                    <p className="font-['Metropolis'] text-[10px] tracking-[1px] uppercase text-[#51534A]">Notes are kept as a permanent audit trail.</p>
                  </div>
                </div>

                {/* Onboarding — the New Supplier Form. Kept as its own zone
                    (not under Documents) because its lifecycle is supplier-
                    driven: it auto-completes when the public form is submitted,
                    rather than being a portal compliance doc staff mark signed. */}
                <p className={`${sectionClass} mt-8`}>Onboarding Process</p>
                {/* The steps people actually DO to bring a supplier on board.
                    Nothing recorded them before, so "where did we get to with
                    them?" could only be answered by asking whoever did it last.
                    Ticking writes immediately — a checklist ticked mid-
                    conversation shouldn't need a second action to stick — and
                    stamps who did it, because an unattributed tick is worth
                    very little when you're reconstructing what happened. */}
                {(() => {
                  const byStep = completionByStep(checklistRows);
                  const progress = checklistProgress(checklistRows);
                  return (
                    <div className="mb-6">
                      <div className="flex items-baseline justify-between gap-3 mb-3 -mt-2">
                        <p className="font-['Metropolis'] text-xs text-[#51534A]">
                          Fusion's own steps for bringing this supplier on board. Not visible to the supplier.
                        </p>
                        <p className="font-['Metropolis'] text-xs font-bold text-ink whitespace-nowrap">
                          {progress.done} of {progress.total} done
                        </p>
                      </div>
                      <div className="border border-line bg-white rounded-xl overflow-hidden">
                        {ONBOARDING_STEPS.map(step => {
                          const row = byStep[step.key];
                          const done = !!row;
                          const who = row?.completed_by_user?.full_name;
                          return (
                            <label
                              key={step.key}
                              htmlFor={`onb-${step.key}`}
                              className="flex items-start gap-3 px-4 py-3 border-b border-line last:border-0 cursor-pointer hover:bg-[#F7F9F0]/60 transition-colors">
                              <input
                                id={`onb-${step.key}`}
                                type="checkbox"
                                checked={done}
                                disabled={checklistBusy === step.key}
                                onChange={e => toggleChecklistStep(step.key, e.target.checked)}
                                className="mt-0.5 w-4 h-4 accent-[#92D400] flex-shrink-0 disabled:opacity-50" />
                              <span className="min-w-0 flex-1">
                                <span className={`block font-['Metropolis'] text-sm ${done ? 'text-[#51534A] line-through decoration-[#DADCCD]' : 'text-ink font-semibold'}`}>
                                  {step.title}
                                </span>
                                <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">{step.detail}</span>
                                {done && (
                                  // Say who and when — the same stamping the
                                  // compliance notes carry.
                                  <span className="block font-['Metropolis'] text-[11px] text-fusion-forest mt-1">
                                    ✓ Done{who ? ` by ${who}` : ''}{row.completed_at ? ` · ${fmtDate(row.completed_at)}` : ''}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4">The New Supplier Form is completed by the supplier via a public link — it flags as completed automatically on submission.</p>
                <div className="border border-line bg-white rounded-xl overflow-hidden mb-2">
                  <div className="flex items-center justify-between gap-4 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="font-['Metropolis'] text-sm text-ink truncate">New Supplier Form</p>
                      {(nsfSent || nsfCompleted) && (
                        <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5 truncate">
                          {nsfSent && <>Sent {fmtNsf(form.setup_form_sent_at)}</>}
                          {nsfSent && nsfCompleted && <span className="mx-2">·</span>}
                          {nsfCompleted && <>Completed {fmtNsf(form.setup_form_completed_at || form.setup_form_sent_at)}</>}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-1 whitespace-nowrap ${nsfBadge}`}>{nsfLabel}</span>
                      {!nsfCompleted && (
                        <button
                          onClick={sendSetupForm}
                          disabled={sendingForm}
                          className="bg-[#2E8FA1] text-[#131313] rounded-xl font-['Metropolis'] text-xs font-semibold px-3 py-1.5 hover:bg-[#246d7d] transition-colors disabled:opacity-50 whitespace-nowrap">
                          {sendingForm ? 'Sending…' : nsfSent ? 'Re-send' : 'Send'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-8 mb-4 pb-2 border-b border-line">
                  <p className="font-['Metropolis'] text-[11px] font-semibold tracking-[0.18em] uppercase text-fusion-forest">Documents</p>
                  <button
                    type="button"
                    onClick={handleSendComplianceReminder}
                    disabled={sendingComplianceReminder}
                    title="Send a generic email asking the supplier to log in and complete their outstanding compliance documents."
                    className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-4 py-1.5 hover:bg-[#2E8FA1]/10 transition-colors disabled:opacity-50">
                    {sendingComplianceReminder ? 'Sending…' : 'Send Compliance Reminder Email'}
                  </button>
                </div>
                <p className="font-['Metropolis'] text-xs text-[#51534A] mb-4 -mt-2">Documents that get published to the supplier's portal for them to read and sign. Suppliers upload renewals via their portal and supply the expiry date themselves. Open a document to review it, approve the expiry date, publish it, or ask the supplier a question — every action lives on its detail page now. Rows go red when expired and orange in the final week.</p>
                <div className="border border-line bg-white rounded-xl overflow-hidden">
                  {complianceDocs.map((doc) => {
                    const record = onboardingDocs.find(r => r.document_key === doc.key);
                    const isPublished = Boolean(record?.sent_at);
                    const isSigned = Boolean(record?.signed_at);
                    const isNotRequired = Boolean(record?.not_required);
                    // Risk-driven: if the supplier has a risk flag and this
                    // doc isn't in the configured required set for that
                    // level, hide it from the main list — UNLESS staff has
                    // overridden via risk_override_applicable, in which
                    // case the doc joins the normal lifecycle. Hidden docs
                    // are still reachable via the "Add additional
                    // compliance document" picker below the table.
                    const hasRiskOverride = Boolean(record?.risk_override_applicable);
                    const isNotApplicableByRisk = Boolean(form.risk_flag) && !requiredDocKeys.has(doc.key) && !hasRiskOverride;
                    if (isNotApplicableByRisk) return null;
                    // State comes from the shared derivation — this page used to
                    // run its own ladder, which is how a signed document read
                    // "Awaiting Review" in the queue and "Not Published" here.
                    // lib/complianceRecord reconciles the onboarding row with any
                    // acknowledgement; lib/compliance decides the state.
                    const ack = ackByKey.get(doc.key);
                    const docState = documentStateFor({
                      record,
                      acknowledgement: ack,
                      satisfiedBy: doc.satisfiedBy,
                    });
                    const actioned = hasSupplierActioned({ record, acknowledgement: ack });
                    const isRejected = docState === DOCUMENT_STATES.OUTSTANDING
                      && (record?.review_decision === 'rejected' || ack?.review_decision === 'rejected');
                    const isExpired = docState === DOCUMENT_STATES.EXPIRED;
                    const isExpiringSoon = docState === DOCUMENT_STATES.EXPIRING_SOON;
                    const isNeedsClarification = docState === DOCUMENT_STATES.NEEDS_CLARIFICATION;
                    // "Pending" in this page's vocabulary means Fusion owes the
                    // supplier a decision — the same thing the state machine
                    // calls AWAITING_REVIEW.
                    const isPendingApproval = docState === DOCUMENT_STATES.AWAITING_REVIEW;
                    // The supplier's move, not ours.
                    const withSupplier = isRejected || isNeedsClarification;
                    // The pill keeps a finer vocabulary than the state machine in
                    // two places, because this page is the working record rather
                    // than a queue:
                    //   published / not published — has the supplier been ASKED
                    //     yet. The state machine has no opinion on that; both are
                    //     OUTSTANDING to it.
                    //   rejected — OUTSTANDING to the state machine (the supplier
                    //     must re-submit) but staff need to see WHY it's back.
                    const status = docState === DOCUMENT_STATES.NOT_REQUIRED ? 'not_required'
                      : isRejected ? 'rejected'
                      : isExpired ? 'expired'
                      : docState === DOCUMENT_STATES.NEEDS_CLARIFICATION ? 'needs_clarification'
                      : docState === DOCUMENT_STATES.AWAITING_REVIEW ? 'awaiting_review'
                      : isExpiringSoon ? 'expiring_soon'
                      : docState === DOCUMENT_STATES.APPROVED ? 'approved'
                      : actioned ? 'signed'
                      : isPublished ? 'published'
                      : 'not_published';
                    // Red is reserved for genuine compliance problems — expired
                    // or rejected. Not Published (we haven't sent it yet) is a
                    // neutral not-started state, not a failure, so it's GREY;
                    // Published / Pending Approval stay teal in-progress states.
                    const statusBadge = {
                      not_published: 'text-[#51534A] bg-[#EEF1E6] border border-[#DDE1D0]',
                      published: 'text-[#2E8FA1] bg-[#2E8FA1]/15 border border-[#2E8FA1]/40',
                      approved: 'text-fusion-forest bg-[#92D400]/10',
                      signed: 'text-fusion-forest bg-[#92D400]/10',
                      pending_approval: 'text-[#2E8FA1] bg-[#2E8FA1]/15 border border-[#2E8FA1]/40',
                      awaiting_review: 'text-[#2E8FA1] bg-[#2E8FA1]/15 border border-[#2E8FA1]/40',
                      needs_clarification: 'text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40',
                      rejected: 'text-[#E0290A] bg-[#E0290A]/15 border border-[#E0290A]/40',
                      expiring_soon: 'text-[#7A4E10] bg-[#E89A2E]/15 border border-[#E89A2E]/40',
                      expired: 'text-[#E0290A] bg-[#E0290A]/15 border border-[#E0290A]/40',
                      not_required: 'text-fusion-mid-grey bg-white border border-[#DADCCD]',
                    }[status];
                    const statusLabel = {
                      not_published: 'Not Published',
                      published: 'Published',
                      approved: 'Approved',
                      signed: 'Signed',
                      pending_approval: 'Pending',
                      awaiting_review: 'Awaiting Review',
                      needs_clarification: 'Needs Clarification',
                      rejected: 'Rejected',
                      expiring_soon: 'Expires Soon',
                      expired: 'Expired',
                      not_required: 'Not Required',
                    }[status];
                    // Row accent: red for expired or outstanding, orange for
                    // expiring soon. Pending Approval doesn't tint the row —
                    // only the pill — so the queue feels distinct from the
                    // doc-incomplete states.
                    const rowAccent = (isExpired || isRejected) ? 'bg-[#E0290A]/5 border-l-2 border-l-[#E0290A]'
                      : isExpiringSoon ? 'bg-[#E89A2E]/5 border-l-2 border-l-[#E89A2E]'
                      : '';
                    const labelTone = isNotRequired ? 'text-[#51534A] line-through'
                      : (isExpired || isRejected) ? 'text-[#E0290A]'
                      : isExpiringSoon ? 'text-[#7A4E10]'
                      : 'text-ink';
                    const fmt = (ts) => fmtDate(ts, '');
                    return (
                      <div key={doc.key} className={`grid items-center gap-3 px-4 py-3 border-b border-line last:border-0 grid-cols-[minmax(0,1fr)_130px_110px_20px] ${rowAccent}`}>
                        <div className="min-w-0">
                          {/* Name is the row's single click target → the full
                              document detail page (one chronological stream +
                              every version's file), which now hosts every
                              lifecycle action the ••• menu used to carry. The
                              row itself is read-only: name · expiry · status · ›. */}
                          {record?.id ? (
                            <button
                              type="button"
                              onClick={() => navigate(`/compliance/${record.id}/detail`)}
                              className={`font-['Metropolis'] text-sm truncate text-left bg-transparent border-0 p-0 cursor-pointer hover:underline underline-offset-2 ${labelTone}`}>
                              {doc.label}
                            </button>
                          ) : (
                            <p className={`font-['Metropolis'] text-sm truncate ${labelTone}`}>{doc.label}</p>
                          )}
                          {isNotRequired ? (
                            <p className="font-['Metropolis'] text-xs text-[#51534A] mt-0.5 truncate">
                              Not required{record.not_required_at ? ` — marked ${fmt(record.not_required_at)}` : ''}
                            </p>
                          ) : (
                            <div className="mt-0.5 space-y-0.5">
                              {isExpired && (
                                <p className="font-['Metropolis'] text-xs text-[#E0290A] truncate">
                                  Expired {fmt(record.expiry_date)}{record.expiry_email_sent_at ? ` — supplier emailed ${fmt(record.expiry_email_sent_at)}` : ''}
                                </p>
                              )}
                              {isExpiringSoon && (
                                <p className="font-['Metropolis'] text-xs text-[#7A4E10] truncate">
                                  Expires {fmt(record.expiry_date)}
                                </p>
                              )}
                              {isRejected && (
                                <p className="font-['Metropolis'] text-xs text-[#E0290A] truncate">
                                  Rejected{record.reviewed_at ? ` ${fmt(record.reviewed_at)}` : ''} — with the supplier to re-upload
                                </p>
                              )}
                              {isNeedsClarification && (
                                <p className="font-['Metropolis'] text-xs text-[#7A4E10] truncate">
                                  Clarification requested{record.reviewed_at ? ` ${fmt(record.reviewed_at)}` : ''} — waiting on the supplier
                                </p>
                              )}
                              {isPendingApproval && (
                                <p className="font-['Metropolis'] text-xs text-[#2E8FA1] truncate">
                                  Supplier proposed expiry {fmt(record.expiry_date)} — awaiting your approval
                                </p>
                              )}
                              {isPublished && (
                                <p className="font-['Metropolis'] text-xs text-[#51534A] truncate">
                                  Published {fmt(record.sent_at)}{record.sent_by_user?.full_name ? ` by ${record.sent_by_user.full_name}` : ''}
                                </p>
                              )}
                              {isSigned && (
                                <p className="font-['Metropolis'] text-xs text-[#51534A] truncate">
                                  Signed {fmt(record.signed_at)}{record.signed_marked_by_user?.full_name ? ` (marked by ${record.signed_marked_by_user.full_name})` : ''}
                                </p>
                              )}
                              {record?.expiry_approved_at && !withSupplier && (
                                <p className="font-['Metropolis'] text-xs text-[#51534A] truncate">
                                  Expiry approved
                                  {record.expiry_approved_by_user?.full_name ? ` by ${record.expiry_approved_by_user.full_name}` : ''}
                                  {' · '}{fmtDateTime(record.expiry_approved_at)}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Expiry — read-only, labelled so a bare date isn't
                            ambiguous. Editing (and approving) the date now lives
                            on the detail page; the row just reports it. */}
                        <div className="font-['Metropolis'] text-right leading-tight">
                          {isNotRequired || !record?.expiry_date ? (
                            <span className="text-xs text-[#9BA08C]">No expiry date</span>
                          ) : (
                            <>
                              <span className="block text-[9px] font-bold tracking-[1.5px] uppercase text-[#9BA08C]">
                                {isExpired ? 'Expired' : isPendingApproval ? 'Proposed expiry' : 'Expires'}
                              </span>
                              <span className={`text-xs ${isExpired ? 'text-[#E0290A]' : isExpiringSoon ? 'text-[#7A4E10]' : isPendingApproval ? 'text-[#2E8FA1]' : 'text-[#51534A]'}`}>
                                {fmt(record.expiry_date)}
                              </span>
                            </>
                          )}
                        </div>
                        {/* Status badge — fixed slot. */}
                        <div className="flex justify-end">
                          <span className={`inline-block w-full text-center font-['Metropolis'] text-[9px] font-bold tracking-[2px] uppercase rounded-md px-2 py-1 whitespace-nowrap ${statusBadge}`}>{statusLabel}</span>
                        </div>
                        {/* Chevron — a real second way into the detail page (the
                            name links there too). Inert only when there's no
                            record yet, in which case there's no detail page. */}
                        {record?.id ? (
                          <button
                            type="button"
                            onClick={() => navigate(`/compliance/${record.id}/detail`)}
                            aria-label={`Open ${doc.label} detail`}
                            className="flex justify-end text-[#9BA08C] hover:text-fusion-forest transition-colors bg-transparent border-0 p-0 cursor-pointer">
                            ›
                          </button>
                        ) : (
                          <div className="flex justify-end text-[#9BA08C]" aria-hidden="true">›</div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* "Add additional compliance document" — opens a panel of
                    every active compliance doc that isn't already in the
                    main list (i.e. not required for this supplier's risk
                    level and not already overridden in). Picking one
                    flips risk_override_applicable=true so the row joins
                    the normal lifecycle above. Hidden when the supplier
                    has no risk flag (the main list already shows
                    everything) or when there are no available docs. */}
                {Boolean(form.risk_flag) && (() => {
                  const availableExtras = complianceDocs.filter(d => {
                    if (requiredDocKeys.has(d.key)) return false;
                    const rec = onboardingDocs.find(r => r.document_key === d.key);
                    if (rec?.risk_override_applicable) return false;
                    return true;
                  });
                  if (availableExtras.length === 0) return null;
                  return (
                    <div className="mt-3 relative">
                      <button
                        type="button"
                        onClick={() => setShowAddOptionalDoc(prev => !prev)}
                        className="bg-white border border-[#2E8FA1] rounded-xl text-[#2E8FA1] font-['Metropolis'] text-xs font-semibold px-4 py-2 hover:bg-[#2E8FA1]/10 transition-colors">
                        {showAddOptionalDoc ? 'Cancel' : '+ Add additional compliance document'}
                      </button>
                      {showAddOptionalDoc && (
                        <div className="mt-2 border border-line bg-white rounded-xl overflow-hidden">
                          <p className="px-4 py-2 fusion-field-label border-b border-line">
                            Compliance documents not standard for {form.risk_flag.charAt(0).toUpperCase() + form.risk_flag.slice(1)} risk
                          </p>
                          {availableExtras.map(d => {
                            const isThisSaving = savingDocKey === d.key;
                            return (
                              <button
                                key={d.key}
                                type="button"
                                onClick={() => { setShowAddOptionalDoc(false); handleSetDocRiskOverride(d.key, true); }}
                                disabled={isThisSaving}
                                className="block w-full text-left px-4 py-2 font-['Metropolis'] text-sm text-fusion-mid-grey hover:bg-white hover:text-ink transition-colors disabled:opacity-50 border-b border-line last:border-0">
                                {d.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}

              </div>

              {/* ---- Documents rail (§3) ---- */}
              <aside className="min-w-0 flex flex-col gap-4 min-[1100px]:sticky min-[1100px]:top-2">
                <div className="bg-white border border-[#E6EADB] rounded-xl p-4">
                  <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.16em] text-[#51534A] mb-2">Compliance at a glance</div>
                  {docStates.length === 0 ? (
                    <p className="font-['Metropolis'] text-[12px] text-[#9BA08C] italic">No documents required or on file yet.</p>
                  ) : (
                    <div className="flex flex-col gap-1.5">
                      {[
                        [DOCUMENT_STATES.APPROVED, 'Approved', '#3B6D11'],
                        [DOCUMENT_STATES.EXPIRING_SOON, 'Expiring soon', '#E89A2E'],
                        [DOCUMENT_STATES.AWAITING_REVIEW, 'Awaiting review', '#2E8FA1'],
                        [DOCUMENT_STATES.NEEDS_CLARIFICATION, 'Needs clarification', '#E89A2E'],
                        [DOCUMENT_STATES.EXPIRED, 'Expired', '#51534A'],
                        [DOCUMENT_STATES.OUTSTANDING, 'Outstanding', '#E0290A'],
                      ].map(([state, label, colour]) => (
                        <div key={state} className="flex items-center justify-between gap-2">
                          <span className="font-['Metropolis'] text-[12px] text-[#51534A] inline-flex items-center gap-2">
                            <span aria-hidden="true" className="w-2 h-2 rounded-full" style={{ background: colour }} />
                            {label}
                          </span>
                          <span className="font-['Metropolis'] text-[12px] font-bold text-ink tabular-nums">{docCounts[state] || 0}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Say what ISN'T being counted, rather than leaving someone
                      to work out why the totals don't match the rows. */}
                  {(unpublishedCount > 0 || notLiveCount > 0) && (
                    <div className="mt-2.5 pt-2.5 border-t border-[#F0F2E8] space-y-1">
                      {unpublishedCount > 0 && (
                        <p className="font-['Metropolis'] text-[11px] text-[#7C7F6F] leading-snug">
                          {unpublishedCount} not published yet, so not counted — the supplier hasn't been asked for {unpublishedCount === 1 ? 'it' : 'them'}.
                        </p>
                      )}
                      {notLiveCount > 0 && (
                        <p className="font-['Metropolis'] text-[11px] text-[#7C7F6F] leading-snug">
                          {notLiveCount} against {notLiveCount === 1 ? 'a document' : 'documents'} that {notLiveCount === 1 ? 'is' : 'are'} no longer live, so not counted.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="bg-white border border-[#E6EADB] rounded-xl p-4">
                  <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.16em] text-[#51534A] mb-2">Reminders</div>
                  {/* What the supplier is actually being chased about, rather
                      than a claim that reminders are "on" — the schedule lives
                      on each document in Fusion Documents. */}
                  <p className="font-['Metropolis'] text-[12px] text-[#51534A] leading-snug">
                    {nextExpiry
                      ? <>Next expiry <strong className="text-ink">{fmtDate(nextExpiry)}</strong>. Renewal reminders go out automatically on the schedule set for each document.</>
                      : 'Nothing with an approved expiry date yet, so no renewal reminders are scheduled.'}
                  </p>
                </div>

                <div className="bg-white border border-[#E6EADB] rounded-xl p-4">
                  <div className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.16em] text-[#51534A] mb-2">Required at this tier</div>
                  <p className="font-['Metropolis'] text-[12px] text-[#51534A]">
                    {!rfCurrent
                      ? 'No risk tier set, so nothing is required yet.'
                      : !complianceDocsLoaded
                        ? <><strong className="text-ink">{rfCurrent.label}</strong> risk · loading requirements…</>
                        : <><strong className="text-ink">{rfCurrent.label}</strong> risk · <strong className="text-ink">{requiredDocKeys.size}</strong> {requiredDocKeys.size === 1 ? 'document' : 'documents'} required</>}
                  </p>
                  {/* A requirement whose document was never published (or has
                      since been archived) is a configuration problem in Risk
                      Flag Configuration, not a number to quietly drop. */}
                  {staleRequirementCount > 0 && (
                    <p className="font-['Metropolis'] text-[11px] text-[#7C7F6F] mt-1.5 leading-snug">
                      {staleRequirementCount} more {staleRequirementCount === 1 ? 'requirement points' : 'requirements point'} at {staleRequirementCount === 1 ? 'a document' : 'documents'} that {staleRequirementCount === 1 ? 'is' : 'are'} not live — check Risk Flag Configuration.
                    </p>
                  )}
                </div>
              </aside>
              </div>
              );
            })()}

          </div>
        </div>
      </div>

      {/* Compliance status override modal. Pinning a status (Red/Amber/
          Green) requires a reason — the reason is appended to the
          compliance notes thread so the audit trail captures *why*. */}
      {showOverrideModal && pendingOverrideStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white border border-line rounded-xl w-full max-w-lg p-6">
            <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink mb-2">Override Compliance Status</h3>
            <p className="font-['Metropolis'] text-sm text-[#51534A] mb-4">
              Compliance is worked out from this supplier's documents. Overriding pins it by hand
              for {form?.supplier_name} and stops it updating itself until you revert.
            </p>
            {/* The status choice lives HERE now (§6). It used to be three equal
                buttons on the card, which made a DERIVED status look like a
                manual one — three buttons of the same weight say "pick one",
                not "this is calculated for you". */}
            <label className={labelClass}>Pin the status to</label>
            <div className="flex gap-2 flex-wrap mb-5 mt-1">
              {['green', 'amber', 'red'].map(opt => {
                const cfg = csCfg[opt];
                const isActive = pendingOverrideStatus === opt;
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setPendingOverrideStatus(opt)}
                    className={`font-['Metropolis'] text-xs font-semibold tracking-[1px] uppercase px-4 py-2 rounded-lg transition-colors ${isActive ? cfg.activeBtn : 'bg-white border border-line text-fusion-mid-grey hover:border-[#DADCCD]'}`}>
                    {cfg.label}
                  </button>
                );
              })}
            </div>
            <label className={labelClass}>Reason</label>
            <AutoSizeTextarea
              autoFocus
              minRows={4}
              value={overrideReason}
              onChange={e => setOverrideReason(e.target.value)}
              placeholder="e.g. supplier under investigation following an incident; manually flagged Red until resolved"
              className="w-full bg-white border border-line rounded-xl text-ink px-3 py-2 font-['Metropolis'] text-sm focus:outline-none focus:border-[#92D400] mb-5" />
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowOverrideModal(false); setPendingOverrideStatus(null); setOverrideReason(''); }}
                disabled={savingOverride}
                className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={applyComplianceOverride}
                disabled={savingOverride || !overrideReason.trim()}
                className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                {savingOverride ? 'Saving…' : 'Apply Override'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Activate Supplier modal — opens from the header. Pre-fills with
          any existing risk flag (rare for inactive but possible if staff
          edited the supplier before activating). Activation is a single
          atomic write: status='active' + risk_flag together. */}
      {showActivateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="bg-white border border-line rounded-xl w-full max-w-lg p-6">
            <h3 className="font-['Metropolis'] text-lg font-bold tracking-[2px] uppercase text-ink mb-2">Activate Supplier</h3>
            <p className="font-['Metropolis'] text-sm text-[#51534A] mb-5">Pick a risk flag for {form?.supplier_name}. The risk level dictates which compliance documents this supplier must complete.</p>
            <div className="flex flex-col gap-3 mb-6">
              {[
                { key: 'high',   label: 'High Risk',   accent: '#E0290A', activeText: '#F9FCF1' },
                { key: 'medium', label: 'Medium Risk', accent: '#E89A2E', activeText: '#131313' },
                { key: 'low',    label: 'Low Risk',    accent: '#92D400', activeText: '#131313' },
              ].map(opt => {
                const isActive = activateRiskFlag === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => { setActivateRiskFlag(opt.key); setActivatePublishDocs(opt.key === 'low'); }}
                    className="inline-flex items-center gap-3 font-['Metropolis'] text-sm font-semibold tracking-[1px] uppercase px-5 py-3 rounded-lg transition-colors"
                    style={isActive
                      ? { background: opt.accent, color: opt.activeText }
                      : { background: '#EEF1E6', color: '#51534A', border: '1px solid #DDE1D0' }}>
                    <FlagIcon />
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {/* Auto-publish compliance docs to the supplier's portal at
                activation. The default tracks the selected risk flag:
                Low risk → ticked (their docs flow through automatically,
                no staff handholding needed). Medium / High risk → unticked
                (those tiers need staff to push each doc deliberately).
                Staff can still override either way on the modal — the
                policy is a default, not a hard rule. Already-published
                docs are skipped so a re-activation doesn't re-stamp
                signed rows. */}
            <label className="flex items-start gap-3 mb-3 cursor-pointer">
              <input
                type="checkbox"
                checked={activatePublishDocs}
                disabled={activating}
                onChange={e => setActivatePublishDocs(e.target.checked)}
                className="mt-0.5 accent-active-green" />
              <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                Publish required compliance documents to the supplier's portal now
                <span className="block font-['Metropolis'] text-[11px] text-[#51534A] mt-0.5">
                  Default: on for Low risk, off for Medium / High risk. Toggle to override.
                </span>
              </span>
            </label>
            {/* Optional launch email — defaults off so bulk activation
                doesn't fan out unexpected mail. When the supplier has no
                main contact email, the checkbox is disabled with a hint. */}
            <label className={`flex items-start gap-3 mb-5 ${form?.main_contact_email ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}>
              <input
                type="checkbox"
                checked={activateSendEmail && Boolean(form?.main_contact_email)}
                disabled={!form?.main_contact_email || activating}
                onChange={e => setActivateSendEmail(e.target.checked)}
                className="mt-0.5 accent-active-green" />
              <span className="font-['Metropolis'] text-sm text-fusion-mid-grey">
                Send launch / welcome email to{' '}
                {form?.main_contact_email ? (
                  <strong className="text-ink">
                    {form.main_contact_email}
                    {form.secondary_contact_email && form.secondary_contact_email !== form.main_contact_email ? ` and ${form.secondary_contact_email}` : ''}
                  </strong>
                ) : (
                  <span className="text-[#51534A] italic">(no contact email on file)</span>
                )}
              </span>
            </label>
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowActivateModal(false); setActivateRiskFlag(null); setActivateSendEmail(false); }}
                disabled={activating}
                className="bg-white rounded-xl border border-line text-fusion-mid-grey font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:border-[#DADCCD] transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleActivateSupplier}
                disabled={!activateRiskFlag || activating}
                className="bg-[#92D400] text-[#131313] rounded-xl font-['Metropolis'] text-sm font-semibold px-5 py-2 hover:bg-[#6a9a00] transition-colors disabled:opacity-50">
                {activating ? 'Activating…' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deactivate supplier — reason-required confirm. Persists status +
          audit and revokes portal access in handleDeactivate. */}
      {showDeactivateModal && isAdmin(currentUser) && (
        <Modal open={showDeactivateModal} onClose={() => setShowDeactivateModal(false)} maxWidth={520} ariaLabel="Deactivate supplier">
          <Modal.Header
            eyebrow="Lifecycle"
            title={`Deactivate ${form?.supplier_name || 'supplier'}`}
            sub="They'll be blocked from new briefs, their portal logins disabled, and all emails suppressed. You can reactivate at any time."
            onClose={() => setShowDeactivateModal(false)} />
          <Modal.Body>
            <label className={labelClass}>Reason *</label>
            <AutoSizeTextarea
              minRows={3}
              className={inputClass.replace('opacity-60 cursor-not-allowed', '')}
              value={deactivateReason}
              onChange={e => setDeactivateReason(e.target.value)}
              placeholder="Why is this supplier being deactivated?" />
            <p className="font-['Metropolis'] text-[11px] text-[#51534A] mt-1.5">Required — a short reason for the audit trail (at least 5 characters).</p>
          </Modal.Body>
          <Modal.Footer>
            <div className="flex items-center justify-end gap-3">
              <button type="button" onClick={() => setShowDeactivateModal(false)} disabled={deactivating}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase text-[#51534A] hover:text-fusion-mid-grey bg-transparent border border-line rounded-r-btn px-4 py-2 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleDeactivate} disabled={deactivating || deactivateReason.trim().length < 5}
                className="font-['Metropolis'] text-[11px] font-bold tracking-[0.1em] uppercase text-ink bg-[#E0290A] hover:bg-[#b82008] rounded-r-btn px-4 py-2 transition-colors disabled:opacity-50">
                {deactivating ? 'Deactivating…' : 'Deactivate supplier'}
              </button>
            </div>
          </Modal.Footer>
        </Modal>
      )}

    </div>
  );
}
